import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tooltip } from '@/components/ui/Tooltip';
import { Notice } from '@/components/ui/Notice';
import { EmailLink, TextLink } from '@/components/ui/TextLink';
import { CompanyFormModal } from '@/components/companies/CompanyFormModal';
import { CompanyAttachmentsPanel } from '@/components/companies/CompanyAttachmentsPanel';
import { CompanyCycleLinksField } from '@/components/companies/CompanyCycleLinksField';
import { CompanyEmailTrails } from '@/components/companies/CompanyEmailTrails';
/*
 * The Sales & billing history — deals, quotes, invoices and credit notes raised
 * against this account, with the credit balance in its Credit notes tab. The
 * HOOK is called inside `DocumentsHistorySection` below rather than at the page
 * level: it needs a company id, and the page has none until the record resolves,
 * which is below the hook line.
 */
import {
  CompanyDocumentsHistory,
  useCompanyDocumentsHistory,
} from '@/components/companies/CompanyDocumentsHistory';
import { CompanyEventInterestPanel } from '@/components/companies/CompanyEventInterestPanel';
import { CompanyPurchaseHistory } from '@/components/companies/CompanyPurchaseHistory';
import { CompanyServicesSection } from '@/components/companies/CompanyServicesSection';
import { CompanyProductInsightsSection } from '@/components/product-insights/CompanyProductInsightsSection';
import { CompanyMappingSection } from '@/components/company-mapper/CompanyMappingSection';
/*
 * Account lead history (owner 2026-10-25): the effective-dated lead periods
 * (layout id `lead-history`, never rename), and the "Effective from" question
 * every lead change on this page goes through. Mounted once.
 */
import { AccountLeadHistorySection } from '@/components/companies/AccountLeadHistorySection';
import { AccountLeadChangeDialog } from '@/components/companies/AccountLeadChangeDialog';
import { LEAD_HISTORY_TITLE, accountLeadErrorMessage } from '@/lib/account-lead-history';
import { BILLING_NAME_LABEL, COMPANY_NAME_LABEL } from '@/lib/company-names';
import {
  MarketingHistoryPanel,
  useCanReadMarketingHistory,
} from '@/components/marketing/MarketingHistoryPanel';
/*
 * The "+" on the Sales & billing history section — a NEW deal, quote or invoice
 * for THIS company, with its projects of interest seeded as lines. The same two
 * modules `/companies` mounts at its three "+" sites, wired the same way: the
 * menu asks per module, the flow fetches the company itself and is mounted ONCE
 * at the page.
 */
import {
  CompanyProjectLinesFlow,
  type CompanyProjectLinesRequest,
} from '@/components/records/CompanyProjectLinesFlow';
import {
  COMPANY_CREATE_TARGETS,
  type CompanyCreateTarget,
  CreateForCompanyMenu,
} from '@/components/records/RecordCreateMenu';
/*
 * Faculty and regions write rules, shared with `CompaniesPage`'s inline editors.
 *
 * ⚠️ Nothing in that module is a display helper: each function encodes a rule
 * the server enforces, and `regionOptions`' injection of held-but-retired ids is
 * the only thing standing between a whole-set `regionIds` REPLACE and silent
 * data loss. Change a rule there, not here.
 */
import {
  facultyOptions,
  masterLabel,
  refusedRegionIds,
  regionOptions,
  retiredRegionNames,
  useFaculties,
} from '@/components/companies/company-master-options';
import {
  InlineRegionsField,
  type InlineSetSaveResult,
} from '@/components/companies/InlineRegionsField';
/*
 * The grouped contacts list, and `primaryContactRefusal` with it.
 *
 * ⚠️ The refusal lives THERE, not here, because this page imports that module
 * and not the reverse — one copy, read by both affordances that can name a
 * primary contact (the picker's option hints below, and the list's row action).
 */
import {
  CompanyContactsList,
  primaryContactRefusal,
  type ContactGroupActions,
  type PrimaryActionState,
} from '@/components/companies/CompanyContactsList';
import { ContactFormModal } from '@/components/contacts/ContactFormModal';
import {
  ApprovalStatusBadge,
  ResendApprovalControl,
} from '@/components/companies/ResendApprovalControl';
import { ActivityButton, ActivityDrawer } from '@/components/tasks/ActivityDrawer';
import { CompanyTasksSection } from '@/components/tasks/CompanyTasksSection';
import { CompanyNotesSection } from '@/components/notes/CompanyNotesSection';
import { XeroLinkModal } from '@/components/xero/XeroLinkModal';
import { XeroPanel } from '@/components/xero/XeroPanel';
import {
  Avatar,
  Badge,
  type BadgeTone,
  Button,
  CollapsibleSection,
  ConfirmDialog,
  EmptyState,
  PageHeader,
  Panel,
  PanelFooter,
  PanelHeader,
  SegmentedControl,
  type SegmentedControlOption,
  Select,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
  ValuePills,
} from '@/components/ui';
// Imported by path rather than through the barrel, to avoid colliding with
// another edit of `src/components/ui/index.ts`.
import {
  InlineField,
  type InlineFieldOption,
  type InlineSaveResult,
} from '@/components/ui/InlineField';
/*
 * The billing/shipping pairing and the "Same as billing" wording, shared with
 * `CompanyFormModal` and `ContactFormModal`. Only the LIST and the LABEL come
 * from there — see that module's header for why the comparison stays local.
 */
import {
  ADDRESS_PAIRS,
  COMPANY_ADDRESS_PAIRS,
  SAME_AS_BILLING_LABEL,
  type CompanyAddressKey,
} from '@/lib/address-pairs';
import { ApiError, api } from '@/lib/api';
import { companyNotesConsequence } from '@/lib/company-delete';
/* The folded Sales & billing history's one-line summary, worded from its counts. */
import { companyDocumentsSummary } from '@/lib/company-documents';
/*
 * The projects-of-interest vocabulary and option list.
 *
 * ⚠️ Nothing here is re-derived locally. `projectOfInterestOptions` injects a
 * held-but-unlisted project back into the picker for the same reason
 * `regionOptions` does: `projectIdsOfInterest` is a whole-set REPLACE, so a
 * project the picker cannot name is one the next save silently drops. The two
 * label helpers are the ONE wording of this set — the collapsed read view and
 * the full list behind it — so the page and any future list column cannot word
 * the same fact differently.
 */
import {
  PROJECTS_OF_INTEREST_LABEL,
  companyProjectsPills,
  companyProjectsTitle,
  projectOfInterestOptions,
} from '@/lib/company-projects';
import { companySourceOptions, useCompanySources } from '@/lib/use-company-sources';
import { companyTypeOptions, useCompanyTypes } from '@/lib/use-company-types';
import { companyStageOptions, useCompanyStages } from '@/lib/use-company-stages';
import {
  filterStatusesForStage,
  useCompanyStageStatuses,
} from '@/lib/use-company-stage-statuses';
/*
 * Prospect sub-stage follow-ups (owner 2026-10-23): the dialog that asks for a
 * follow-up date or a reason before a stage status is saved, and the date
 * rules it shares with the inline follow-up editor. Flags, never names.
 */
import { useStageStatusChange } from '@/lib/use-stage-status-change';
import { nextDayIso, todayUtcIsoDate } from '@/lib/stage-status-follow-up';
import { isRealDate } from '@/components/catalog/form-utils';
/*
 * The currency MASTER, via the hook that already reads it correctly —
 * `perPage: 250` (this endpoint's own ceiling, clears all 164 rows) and
 * `isActive: true`.
 *
 * ⚠️ The master is NOT the picker's source. It is only how a code Xero enables
 * is turned into the `currencyId` we write — see `currencyPickerOptions`.
 */
import { useCurrencyOptions } from '@/lib/use-invoice-options';
import { cx } from '@/lib/cx';
import { formatDateOnly, formatDateTime, joinList, orDash } from '@/lib/format';
import { mailtoHref } from '@/lib/mail';
import { denialReason } from '@/lib/permissions';
/*
 * The cycle calendar split into CURRENT and PREVIOUS id sets, for the
 * Project-of-interest picker's narrowing.
 *
 * ⚠️ **The hook, not `cycleScopeIdIndex` over the `useCycleOptions` read in
 * `CompanyCycleLinksField`.** That one lives inside a child component, so its
 * rows are not reachable from here without lifting a fetch out of it. This is
 * one more `/cycles` GET on a single-record page, taken deliberately and gated
 * on `canUpdate`.
 *
 * ⚠️ It also resolves the four ways the question cannot be answered —
 * `loading`, `failed`, `hasMore` and an empty master — to
 * `UNKNOWN_CYCLE_SCOPES` rather than to an empty set, and `hasMore` is the one
 * a hand-rolled memo drops.
 */
/*
 * The pills' own vocabulary — ⛔ **imported, never restated.** `Current` on a
 * pill and `Current` in `projectOfInterestOptions`' pinned-project hint must be
 * the same word for the same set of cycles; a local pair of labels would let
 * the picker hint `Previous` on a row while a pill spelled differently claimed
 * to be showing it. There is no second notion of "current" in this file.
 */
import {
  DEFAULT_PROJECT_CYCLE_SCOPE,
  PROJECT_CYCLE_SCOPES,
  PROJECT_CYCLE_SCOPE_LABELS,
  type ProjectCycleScope,
} from '@/lib/project-cycle-scope';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { useCycleScopeIds } from '@/lib/use-line-project';
import { useProjectOptions } from '@/lib/use-project-options';
import { useReferenceOptions } from '@/lib/use-reference-options';
import type {
  Company,
  CompanyInput,
  CompanyPrimaryContact,
  Contact,
  ContactGroup,
  Country,
  Currency,
  MasterRef,
  Paginated,
  ReferenceRecord,
  UserSummary,
  XeroCurrency,
  XeroLineAmountType,
} from '@/lib/types';
import { viewerZone } from '@/lib/task-time';
import { useAuth } from '@/lib/use-auth';
/*
 * The page's SECTION LAYOUT as per-user SERVER preferences. Since 2026-10-20
 * the FOLDS stay in `useCompanyPageLayout` (owner, 2026-10-11) and order, width
 * and hidden live in the shared page layout manager (`usePageLayout('company')`),
 * whose defaults are built in the person's stored `companyPage.order`.
 */
import {
  COMPANY_PAGE_SECTIONS,
  type CompanyPageSectionId,
  companyPageLayoutDefaults,
  useCompanyPageLayout,
} from '@/lib/use-company-page-layout';
import { type LayoutBlockDefault, type PageLayout, usePageLayout } from '@/lib/use-page-layout';
import { LayoutGrid, LayoutToolbar } from '@/components/layout';
import { useUserOptions } from '@/lib/use-user-options';
import {
  USER_GROUP_SLUGS,
  narrowingNote,
  outsideGroupNote,
  type StaffNarrowing,
} from '@/lib/user-groups';
import {
  THEME_NOT_IN_LIST,
  type BrandingThemesState,
  brandingThemeOptions,
  useXeroBrandingThemes,
} from '@/lib/use-xero-branding-themes';
import { linkOf } from '@/lib/xero';
import {
  XeroCooldownError,
  noteXeroRateLimit,
  xeroCooldownSecondsRemaining,
  xeroCooldownSummary,
} from '@/lib/xero-cooldown';
import { useXero } from '@/lib/xero-context';
import {
  LINE_AMOUNT_OPTIONS,
  USE_ORGANISATION_SETTINGS,
  sellableTaxRates,
  taxRateHint,
  toLineAmountType,
  useXeroTaxRates,
} from '@/lib/xero-tax-rates';

/**
 * ONE page of this account's contacts, and it is the whole list.
 *
 * ⚠️ **100 is the entities family's ceiling, and the related list is no longer
 * PAGED.** It used to be five rows with a pager, which cannot carry a group
 * heading: "Finance 2" over a five-row page is a wrong count rendered
 * confidently while eleven finance contacts sit on pages nobody fetched. So the
 * whole list arrives at once and is grouped client-side.
 *
 * ⚠️ **Past 100 the counts are FLOORS**, and that has to be said rather than
 * silently truncated — see `contactsTruncated` and `CountBadge`. A missing
 * contact is indistinguishable from a contact that does not exist.
 *
 * One constant, because there is now ONE fetch: this list and the
 * primary-contact picker read the same request, so the two can never disagree
 * about who works here.
 */
const CONTACTS_PER_PAGE = 100;
/** The `/contact-groups` master's own ceiling — never its default of 50. */
const CONTACT_GROUPS_PER_PAGE = 200;
const SKELETON_ROWS = 3;

/**
 * The Project-of-interest picker's two pills, DERIVED from the shared union
 * rather than written out.
 *
 * ⛔ **The labels come from `PROJECT_CYCLE_SCOPE_LABELS`, which is also what
 * `projectOfInterestOptions` uses to say why a stored project is pinned.**
 * Spelling `Current` and `Previous` out here would be a second copy of a
 * vocabulary that already has one home, and the drift reads as the picker
 * contradicting its own pins — a row hinted `Previous` under a pill calling the
 * same set something else. Mapping the union also means a third scope arrives
 * on this control with no edit here.
 *
 * ⚠️ **`CompaniesPage` derives the same list the same way, and that is not a
 * duplicated decision.** What is shared is the union and the words; what is
 * repeated is a four-line `.map` over them, which cannot drift because neither
 * page holds a label of its own. Hoisting it would mean adding an export to
 * `project-cycle-scope.ts` that couples that module to a `ui` primitive.
 */
const PROJECT_CYCLE_SCOPE_OPTIONS: readonly SegmentedControlOption<ProjectCycleScope>[] =
  PROJECT_CYCLE_SCOPES.map((scope) => ({
    value: scope,
    label: PROJECT_CYCLE_SCOPE_LABELS[scope],
  }));

/**
 * The follow-up date refusals (`FOLLOW_UP_DATE_REQUIRED` / `_INVALID` /
 * `_NOT_ALLOWED`), matched on the CODE so they land on the control that sent
 * the date even without a `details` path.
 */
function isFollowUpDateRefusal(caught: ApiError): boolean {
  return caught.code.startsWith('FOLLOW_UP_DATE_');
}

/*
 * The company record itself is a four-way outcome, and collapsing any two of
 * them loses information the user needs: `missing` is a 404 on the record and
 * gets a not-found screen, `error` is anything else and gets a retry.
 */
type RecordState =
  | { status: 'loading' }
  | { status: 'ready'; company: Company }
  | { status: 'missing' }
  | { status: 'error'; message: string };

/**
 * The outcome of an in-place stage save.
 *
 * `field` and `banner` are separate because the API separates them: a 422 on
 * `stageId` arrives with `details` naming the path and belongs on the control,
 * while a 403 carries no `details` at all and must never be rendered as if a
 * field were wrong.
 *
 * `stripped` is the one that exists because of a real hazard rather than a real
 * error — see `saveStage`.
 */
type StageStatus =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved'; name: string }
  | { kind: 'field'; message: string }
  | { kind: 'banner'; message: string }
  | { kind: 'stripped'; message: string };

/**
 * What "Same as billing" is doing, or what it last did.
 *
 * ⚠️ **`saved` carries no message and that is deliberate**: the acknowledgement
 * is the five values changing on screen behind it, so the line only has to say
 * the write landed. The failure branches carry the SERVER's sentence — this
 * page never invents one for a refusal it did not author.
 */
type AddressCopyStatus =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'warning'; message: string }
  | { kind: 'error'; message: string };

/**
 * The rows of an address, in reading order — **four** on the billing side,
 * five on the shipping side.
 *
 * ⚠️ A `Record` over the shared key union rather than a list of its own: a
 * sixth pair added to `ADDRESS_PAIRS` fails to compile here until it is
 * labelled, where a parallel array would silently render an unlabelled row.
 * Both halves of a pair carry the same word — the column heading above them is
 * what says billing or shipping — so the confirmation can name a shipping
 * field with the label the reader is looking at.
 *
 * ⚠️ **`billingCountry` is gone from this table, and the KEY UNION is what
 * removes it.** A company's country is the `countryId` foreign key now (owner's
 * decision, 2026-08-31), edited through its own picker in Account details. `CompanyAddressKey`
 * omits the billing half, so a row for it cannot be built here at all — where a
 * merely-deleted entry would leave the key labellable again by accident.
 * `shippingCountry` stays: shipping has no foreign key and keeps its free text.
 */
const ADDRESS_ROW_LABELS: Record<CompanyAddressKey, string> = {
  billingStreet: 'Street',
  shippingStreet: 'Street',
  billingCity: 'City',
  shippingCity: 'City',
  billingState: 'State',
  shippingState: 'State',
  billingCode: 'Postal code',
  shippingCode: 'Postal code',
  shippingCountry: 'Country',
};

/**
 * The company's country OF RECORD as a label, or `null` when none is set.
 *
 * ⚠️ Read DEFENSIVELY, the same guard `CompanyFormModal`'s `CountryField`
 * carries and for the same reason: `CompanyCountryRef.name` was declared here
 * as `countryName` until 2026-08-31 and the server has never sent that, so an
 * embed spelling its label differently renders `undefined` — a confident blank
 * in a value slot. `Country #id` names the row without claiming to know what it
 * is called.
 */
function countryOfRecordLabel(country: Company['country']): string | null {
  if (country === null) return null;
  const name = typeof country.name === 'string' ? country.name.trim() : '';
  return name !== '' ? name : `Country #${country.id}`;
}

/**
 * The whole `/countries` master, for the Country picker.
 *
 * ⛔ **No `hasCompanies` — that key is for FILTERS only.** A filter offers what
 * can match; a picker offers what can be chosen, and narrowing this one would
 * make every country no company is in yet unreachable.
 *
 * ⚠️ No `isActive` on the fetch either: `countryPickerOptions` narrows to active
 * rows itself and injects the stored value, the same split `CompaniesPage`'s
 * `countryOptions` makes. `enabled` skips the request for a reader who cannot
 * write — they see the country as text.
 */
function useCountryRows(enabled: boolean) {
  const [rows, setRows] = useState<Country[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    api
      .listCountries({ perPage: 250 }, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setRows(response.data);
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setRows([]);
        setFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [enabled]);

  return { rows, loading: enabled ? loading : false, failed };
}

/**
 * The Country picker's choices: ACTIVE rows, plus the stored value injected
 * first when the master no longer lists it. A `Combobox` whose value matches no
 * option renders its placeholder, and the next save would clear a real country —
 * the field the server derives the billing region from. Mirrors
 * `CompaniesPage`'s `countryOptions`.
 */
function countryPickerOptions(
  countries: Country[],
  stored: Company['country'],
): InlineFieldOption[] {
  const options: InlineFieldOption[] = countries
    .filter((country) => country.isActive)
    .map((country) => ({ value: String(country.id), label: country.countryName }));
  if (stored !== null && !options.some((option) => option.value === String(stored.id))) {
    options.unshift({
      value: String(stored.id),
      label: `${countryOfRecordLabel(stored) ?? `Country #${stored.id}`} — no longer listed`,
    });
  }
  return options;
}

/** How long a "Saved" acknowledgement stays on screen. */
const SAVED_NOTICE_MS = 4000;

// --- the record's own fields, edited in place -----------------------------

/*
 * Server-enforced maximum lengths, copied from `CompanyFormModal` rather than
 * re-derived — a second, slightly different table would be two rules for one
 * column, and an inline editor that accepts what the modal refuses is a 422
 * the user only meets after typing it.
 *
 * `name` and `billingName` are in this table for their length rule, but they
 * are the two keys here that are NOT clearable server-side — see `companyPatch`.
 *
 * ⚠️ `layout` is deliberately absent: the column is still `NOT NULL` and still
 * edited by `CompanyFormModal`, just no longer IN PLACE on this page, so a
 * length rule, validator branch and patch branch here would be unreachable
 * code. Same call as `StaffKey`'s `ownerId`.
 */
const TEXT_MAX = {
  name: 255,
  /*
   * The Xero Contact name, always (owner 2026-10-25). Required: blank is
   * refused here and by the server (422 `BILLING_NAME_REQUIRED`), so it is
   * never sent as `null`.
   */
  billingName: 255,
  /*
   * Measured against the live API (this page is `shortName`'s only editor):
   * 61 characters is a 422 naming `shortName` under `details`.
   *
   * ⚠️ NOT unique. Two schools of one university legitimately share a short
   * name and there is no 409 — nothing here checks for a collision.
   */
  shortName: 60,
  email: 255,
  secondaryEmail: 255,
  mobile: 32,
  alternateMobile: 32,
  website: 255,
  linkedin: 255,
  billingStreet: 255,
  billingCity: 120,
  billingState: 120,
  billingCode: 32,
  /*
   * ⚠️ **`billingCountry` is deliberately ABSENT.** The company's country is the
   * `countryId` foreign key (owner's decision, 2026-08-31) and this page edits
   * it through its own picker in Account details; the free-text billing country is no longer
   * collected. Keeping the key would leave `companyPatch` able to build
   * `{billingCountry: null}` from a control that no longer renders, and an
   * explicit `null` CLEARS — so `TextKey` is the guard, not an `if`. Same call
   * as `layout` above.
   *
   * ⚠️ `shippingCountry` STAYS: shipping keeps its free-text country.
   */
  shippingStreet: 255,
  shippingCity: 120,
  shippingState: 120,
  shippingCode: 32,
  shippingCountry: 120,
  // Not copied from `CompanyFormModal` — this page is `xeroTaxNumber`'s only
  // editor. 50 is the server's own limit ("Too big: expected string to have
  // <=50 characters").
  xeroTaxNumber: 50,
  description: 20000,
} as const;

type TextKey = keyof typeof TEXT_MAX;

/**
 * Read as `accountLead` / `clientSuccessManager`, written as `…Id` — the read
 * key and the write key differ.
 *
 * `ownerId` is deliberately absent: it is still a real, editable column in the
 * Edit dialog, just not one this page edits in place. Leaving it in this union
 * would leave unreachable branches below that TypeScript cannot flag, since an
 * unused union member is not an error.
 */
type StaffKey = 'accountLeadId' | 'clientSuccessManagerId';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The client-side rules, mirrored from `CompanyFormModal.validate()`.
 *
 * Deliberately no format rule on phone / website / linkedin: the API validates
 * those by length alone, so a stricter rule here would refuse values the server
 * happily accepts.
 */
function validateCompanyText(key: TextKey, next: string): string | null {
  if (next.length > TEXT_MAX[key]) return `Must be ${TEXT_MAX[key]} characters or fewer.`;
  if (key === 'name' && next.trim() === '') return 'Company name is required.';
  if (key === 'billingName' && next.trim() === '') return 'Billing name is required.';
  if ((key === 'email' || key === 'secondaryEmail') && next !== '' && !EMAIL_PATTERN.test(next)) {
    return 'Enter a valid email address.';
  }
  return null;
}

/**
 * The body for one text column, and nothing else.
 *
 * PATCH semantics: an omitted key is left untouched, an explicit `null` clears
 * the column. Never spread the loaded record — that rewrites every column the
 * user never looked at, and `PATCH /companies` is not `.strict()`, so a stray
 * key is silently stripped rather than refused.
 */
function companyPatch(key: TextKey, next: string): CompanyInput {
  // `name` and `billingName` are required server-side; the validator above
  // keeps them non-blank, so they are sent as text and never as null.
  if (key === 'name') return { name: next };
  if (key === 'billingName') return { billingName: next };
  const patch: Omit<CompanyInput, 'name' | 'billingName'> = {};
  patch[key] = next === '' ? null : next;
  return patch;
}

/**
 * The Billing name's two refusals (owner 2026-10-25): 409
 * `COMPANY_BILLING_NAME_TAKEN` (another company's Xero name, normalised) and
 * 422 `BILLING_NAME_REQUIRED` (blank). Matched on the code, and only for the
 * field that sent it — Company name is not unique and never collides.
 */
function describeBillingNameRefusal(key: TextKey, caught: ApiError): InlineSaveResult | null {
  if (key !== 'billingName') return null;
  if (caught.code === 'COMPANY_BILLING_NAME_TAKEN') {
    return { status: 'error', message: 'Another company already uses this billing name in Xero.' };
  }
  if (caught.code === 'BILLING_NAME_REQUIRED') {
    return {
      status: 'error',
      message: caught.fieldErrors.billingName ?? 'Billing name is required.',
    };
  }
  return null;
}

/** One staff id, written out by hand so a typo is a compile error. */
function staffPatch(key: StaffKey, next: string): CompanyInput {
  const id = next === '' ? null : Number(next);
  if (key === 'accountLeadId') return { accountLeadId: id };
  return { clientSuccessManagerId: id };
}

/** The same value read back off the saved record, for the applied-check. */
function readStaff(saved: Company, key: StaffKey): string {
  const person = key === 'accountLeadId' ? saved.accountLead : saved.clientSuccessManager;
  return person ? String(person.id) : '';
}

// --- account lead history -------------------------------------------------

/**
 * The section's layout id (owner 2026-10-25). ⛔ Never rename: layouts are
 * stored per user by id.
 */
const LEAD_HISTORY_SECTION = 'lead-history';

/**
 * Whether the fold registry (`COMPANY_PAGE_SECTIONS`) declares the section. When
 * it does, the fold is the reader's stored one like every other section's; until
 * then the section folds for this visit only.
 */
const LEAD_HISTORY_IN_FOLD_REGISTRY = (COMPANY_PAGE_SECTIONS as readonly string[]).includes(
  LEAD_HISTORY_SECTION,
);
/** The fold id, used only when `LEAD_HISTORY_IN_FOLD_REGISTRY` is true. */
const LEAD_HISTORY_FOLD_ID = LEAD_HISTORY_SECTION as string as CompanyPageSectionId;

/**
 * The layout defaults with the history section directly after Overview (where
 * the account lead itself is edited). A no-op when the registry already
 * declares it, so the block is never listed twice.
 */
function withLeadHistoryDefault(defaults: LayoutBlockDefault[]): LayoutBlockDefault[] {
  if (defaults.some((block) => block.id === LEAD_HISTORY_SECTION)) return defaults;
  const overviewAt = defaults.findIndex((block) => block.id === 'overview');
  const next = [...defaults];
  next.splice(overviewAt + 1, 0, { id: LEAD_HISTORY_SECTION, label: LEAD_HISTORY_TITLE });
  return next;
}

/**
 * A lead change waiting on its "Effective from" date. `resolve` settles the
 * in-place picker that asked, so the field reports the outcome itself.
 */
interface PendingLeadChange {
  companyId: number;
  next: string;
  fromLead: UserSummary | null;
  toLead: { name: string } | null;
  resolve: (result: InlineSaveResult) => void;
}

// --- the primary contact --------------------------------------------------

/*
 * `primaryContactRefusal` used to live here and now lives in
 * `CompanyContactsList`, which is the one module both affordances can import —
 * this page mounts that list, so a function there is reachable from both while
 * a function here would need the list to import the page.
 */

/**
 * How the designation reads when idle.
 *
 * ⚠️ **Empty is ordinary and often permanent**, not a gap to fill: 129 of 148
 * accounts are designated, and 18 of the 19 without hold no contact at all, so
 * they can never gain one. It reads as a value, never as a warning.
 */
function PrimaryContactValue({ contact }: { contact: CompanyPrimaryContact | null }) {
  if (!contact) return <span className="text-fg-subtle">No primary contact</span>;
  const email = contact.email?.trim() ?? '';
  return (
    <span className="flex min-w-0 items-center gap-2">
      <Avatar name={contact.name} size="sm" />
      <span className="min-w-0">
        <span className="block truncate text-sm text-fg">{contact.name}</span>
        {email !== '' && <span className="block truncate text-xs text-fg-muted">{email}</span>}
      </span>
    </span>
  );
}

// --- faculty, regions, proposal links -------------------------------------

/**
 * `facultyOptions` in this page's own currency.
 *
 * The shared helper reports `retired` as a boolean because its two callers
 * spell it differently — a native `<select>` on `/companies` folds it into the
 * label, `InlineField`'s `Combobox` puts it in a `hint`. The FACT is shared;
 * the wording is not.
 */
function facultyPickerOptions(
  records: ReferenceRecord[],
  stored: MasterRef | null,
): InlineFieldOption[] {
  return facultyOptions(records, stored).map(({ value, label, retired }) => ({
    value,
    label,
    ...(retired ? { hint: 'Retired — no longer offered' } : {}),
  }));
}

// --- financial details: VAT number and default currency -------------------

/**
 * The currency codes THIS Xero organisation enables — 25 of them, against our
 * 164-row master.
 *
 * ⚠️ **This list, never `/currencies`, is what the picker offers.** The two
 * differ by 139 rows, every one refused on save — and the refusal is hard, not
 * a warning, because an unenabled currency fails Xero's WHOLE contact push. The
 * master is still read, only to turn a code into the `currencyId` we write; see
 * `currencyPickerOptions`.
 *
 * A failed read is kept as an EMPTY list and SAID so — "this organisation
 * enables no currencies" and "the list didn't arrive" look identical in a
 * dropdown and only one is a fact. Clearing does not depend on any of this —
 * see `currencyField`.
 *
 * `settled` rather than a `loading` flag set inside the effect: `enabled` flips
 * true when the Xero connection read lands, and a flag raised inside the effect
 * would show an empty picker for the frame before it.
 */
function useXeroEnabledCurrencies(enabled: boolean) {
  const [currencies, setCurrencies] = useState<XeroCurrency[]>([]);
  const [failed, setFailed] = useState(false);
  // Narrower than `failed`: the one failure with a WAIT attached, not a fault.
  // Both stay true at once so a cooldown can't look like success to a guard
  // that only checks `failed`.
  const [cooledDown, setCooledDown] = useState(false);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    // Spends from the same shared Xero budget as the themes and rates, so it
    // honours the same cooldown — rejecting rather than returning early so the
    // state below is written by the identical `.catch`/`.finally` pair.
    const waiting = xeroCooldownSecondsRemaining();
    const read: Promise<XeroCurrency[]> =
      waiting === null
        ? api.xeroCurrencies(controller.signal)
        : Promise.reject(new XeroCooldownError(waiting));
    read
      .then((data) => {
        if (controller.signal.aborted) return;
        setCurrencies(data);
        setFailed(false);
        setCooledDown(false);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setCurrencies([]);
        setFailed(true);
        // Records the cooldown too, so a rate limit met here stops the themes
        // and the rates asking as well.
        setCooledDown(noteXeroRateLimit(caught) !== null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setSettled(true);
      });
    return () => controller.abort();
  }, [enabled]);

  return {
    currencies: enabled ? currencies : [],
    loading: enabled && !settled,
    failed: enabled ? failed : false,
    cooledDown: enabled ? cooledDown : false,
  };
}

/**
 * ⚠️ **`currency.id` can be null while `code` is set** — the legal set is
 * Xero's, not ours, so a stored code may name no row in our master. A null id
 * falls back to a value derived from the CODE instead of `''`, which would
 * render "Not set" against an account that has a currency and be one stray
 * save away from clearing a field nobody touched.
 *
 * The `code:` prefix cannot collide with a master id (always digits). Nothing
 * ever writes this string: `InlineField` short-circuits a commit whose value
 * equals the stored one, and this IS the stored one — same property the
 * branding-theme picker relies on for a deleted theme.
 */
const CURRENCY_BY_CODE = 'code:';

function currencyValue(stored: Company['currency']): string {
  if (stored === null) return '';
  return stored.id === null ? `${CURRENCY_BY_CODE}${stored.code}` : String(stored.id);
}

/**
 * The picker's rows: every currency Xero enables THAT OUR MASTER CAN NAME AN ID
 * FOR, matched by code. We write `currencyId` (the master's id) and the server
 * validates the CODE behind it against what Xero enables, so a row needs both
 * halves to be selectable.
 *
 * **The code is the label; the name is only a hint.** Xero calls GBP "British
 * Pound", our master calls it "Pound Sterling" — leading with the name would
 * save one wording and re-render as another, reading as though the wrong row
 * had been chosen. The hint is taken from the MASTER since that is the wording
 * the saved record reads back with.
 *
 * `unmatched` is codes Xero enables that our master has no active row for —
 * cannot be offered (no id to write), and named rather than silently dropped.
 */
function currencyPickerOptions(
  enabled: readonly XeroCurrency[],
  master: readonly Currency[],
  stored: Company['currency'],
): { options: InlineFieldOption[]; codeByValue: Map<string, string>; unmatched: string[] } {
  const byCode = new Map<string, Currency>();
  for (const row of master) byCode.set(row.currencyCode.toUpperCase(), row);

  const options: InlineFieldOption[] = [];
  const codeByValue = new Map<string, string>();
  const unmatched: string[] = [];

  // Sorted by code — Xero documents no order of its own for this list, unlike
  // branding themes, where the tenant's `sortOrder` must be preserved.
  const rows = [...enabled].sort((a, b) => a.code.localeCompare(b.code));
  for (const row of rows) {
    const code = row.code.toUpperCase();
    const match = byCode.get(code);
    if (!match) {
      unmatched.push(code);
      continue;
    }
    const value = String(match.id);
    codeByValue.set(value, code);
    const name = match.currencyName.trim();
    options.push({ value, label: code, ...(name === '' || name === code ? {} : { hint: name }) });
  }

  // The stored currency stays selectable even when absent from the rows above
  // — it IS this account's currency, and dropping it would falsely render as
  // "Not set". Re-picking it writes nothing (see `currencyValue`).
  const value = currencyValue(stored);
  if (stored !== null && !options.some((option) => option.value === value)) {
    const code = stored.code.toUpperCase();
    const name = stored.name.trim();
    // Absence is only *described* when there is a list to be absent from — an
    // empty list is as often "still loading" as an answer, and accusing a live
    // currency of not being enabled on no data is a false claim. Same rule as
    // the faculty and branding-theme pickers.
    let hint = name === '' || name === code ? undefined : name;
    if (enabled.length > 0 && !enabled.some((row) => row.code.toUpperCase() === code)) {
      hint = 'Not enabled in this Xero organisation';
    } else if (master.length > 0 && !byCode.has(code)) {
      hint = 'Not in SEED EMERGE’s currency list';
    }
    options.unshift({ value, label: code, ...(hint === undefined ? {} : { hint }) });
  }

  return { options, codeByValue, unmatched };
}

/**
 * The two `currencyId` refusals that share a status AND a `path`.
 *
 * ⚠️ **Both are `422` and both carry `details[0].path === 'currencyId'`, so a
 * client keying field errors by `path` cannot tell them apart** — and they route
 * to different PEOPLE. Backend `companies.service.ts`, `resolveCurrencyDefaults`.
 * Matched on the code and never on the sentence: the message is a
 * per-organisation string naming a currency code, and this file has three
 * standing rules against matching on admin-visible text.
 */
const CURRENCY_UNKNOWN_CODE = 'CURRENCY_UNKNOWN';
const XERO_CURRENCY_NOT_ENABLED_CODE = 'XERO_CURRENCY_NOT_ENABLED';

/**
 * A currency write that failed for a reason `saveCompanyField` has no vocabulary
 * for. Returns the OUTCOME, not just a sentence, because for one of these the
 * right outcome is to stop offering a retry.
 *
 * ⚠️ **`currencyId` is validated by CALLING XERO, so an unreachable tenant is a
 * `502` and NOTHING is stored** — the exact opposite of `xeroBrandingThemeId`,
 * which saves unvalidated while Xero is down. Principled, not accidental: a
 * stale theme is harmless since it never reaches the contact push, a stale
 * currency would break the next sync. Neither field may be "fixed" to match
 * the other.
 *
 * ── THE TWO 422s SPLIT ON WHO CAN ACT, AND SO DOES THE RESULT STATUS ────────
 *
 * `CURRENCY_UNKNOWN` is `error`: the id named no active row of our master, the
 * list this page loaded is stale, and picking again IS the fix — so the editor
 * stays open on the draft, which is what `error` does.
 *
 * `XERO_CURRENCY_NOT_ENABLED` is `warning`, and that is the whole point of this
 * function returning a result. The value is FINE; this Xero organisation has not
 * enabled it, and enabling it is work in another system done by another person.
 * **Nothing anybody can do in this form will make that save succeed**, so
 * leaving the editor open on the draft with a live Save button is a false
 * promise — it invites a retry that is refused identically, for ever. `warning`
 * closes the editor and keeps the sentence on screen. See `InlineSaveResult`,
 * whose own note already gives this reasoning for its other two causes.
 *
 * The SERVER's sentence leads in both cases — it names the code (`XPF`), which
 * is the only part anybody can act on — and ours follows saying who acts. Never
 * ours instead of theirs.
 *
 * Upstream branches come FIRST and branch on the CODE, because
 * `XERO_TOKEN_REJECTED` and `XERO_UPSTREAM_UNAVAILABLE` share a status and need
 * opposite advice — one says re-authorise, the other says nothing is wrong,
 * wait. Status is only the fallback for a gateway failure naming neither.
 *
 * ⚠️ Returning `null` for an unrecognised code is deliberate and must stay: it
 * falls through to the server's own message. Unknown means "newer than this
 * branch", never "not a refusal" — the same degradation `xero.ts` and
 * `line-project.ts` are built around.
 */
function describeCurrencyFailure(caught: ApiError): InlineSaveResult | null {
  // Clearing is mentioned in every branch: it is the one write here that
  // never asks Xero, so it stays available whichever failure this is.
  const unchanged =
    'nothing was saved. Clearing the currency still works.';

  /*
   * The server's own sentence for this field. `fieldErrors` and `message` carry
   * the SAME string on both of these codes (the service passes one `message`
   * into both the envelope and its single `details` entry), so this is a genuine
   * fallback and not a `??` dropping one of two things that both need showing.
   */
  const said = caught.fieldErrors.currencyId ?? caught.message;

  if (caught.code === XERO_CURRENCY_NOT_ENABLED_CODE) {
    return {
      status: 'warning',
      message: `${said}. Not saved — enable the currency in Xero.`,
    };
  }

  if (caught.code === CURRENCY_UNKNOWN_CODE) {
    return {
      status: 'error',
      message: `${said}. Not saved — reload the page and choose again.`,
    };
  }

  if (caught.code === 'XERO_TOKEN_REJECTED' || caught.code === 'XERO_REAUTH_REQUIRED') {
    return {
      status: 'error',
      message: `Xero refused our credentials, so ${unchanged} Re-authorise the connection.`,
    };
  }
  if (caught.code === 'XERO_NOT_CONNECTED' || caught.code === 'XERO_NOT_CONFIGURED') {
    return {
      status: 'error',
      message: `Xero is not connected, so ${unchanged}`,
    };
  }
  if (
    caught.code === 'XERO_UPSTREAM_UNAVAILABLE' ||
    caught.code === 'XERO_RATE_LIMITED' ||
    caught.status === 502 ||
    caught.status === 504
  ) {
    return {
      status: 'error',
      message: `Xero couldn’t be reached, so ${unchanged} Try again shortly.`,
    };
  }
  return null;
}

// --- icons (Lucide paths, drawn by hand — no icon dependency) -------------

function iconProps(className: string) {
  return {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    className,
  };
}

function BuildingIcon() {
  return (
    <svg {...iconProps('h-5 w-5')}>
      <path d="M3 21h18" />
      <path d="M5 21V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v16" />
      <path d="M15 21v-9h2a2 2 0 0 1 2 2v7" />
      <path d="M9 7h2" />
      <path d="M9 11h2" />
      <path d="M9 15h2" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg {...iconProps('h-5 w-5')}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg {...iconProps('h-4 w-4')}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg {...iconProps('h-4 w-4')}>
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg {...iconProps('h-4 w-4')}>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  );
}

function ChevronIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg {...iconProps('h-3.5 w-3.5')}>
      <path d={direction === 'left' ? 'm15 18-6-6 6-6' : 'm9 18 6-6-6-6'} />
    </svg>
  );
}

// --- presentation helpers -------------------------------------------------

function BackToCompanies() {
  return (
    <TextLink to="/companies" className="inline-flex items-center gap-1">
      <ChevronIcon direction="left" />
      Companies
    </TextLink>
  );
}

/* -------------------------------------------------------------------------
 * The credit balance — WIRED 2026-09-24.
 * --------------------------------------------------------------------- */

/*
 * ⚠️ *SUPERSEDED: a `creditBalancesOf(company)` stub that returned `null`
 * because `GET /companies/:id/credit-balances` did not exist, and a withheld
 * "Credit" group.* The balance is served as
 * `GET /credit-notes/balances?companyId=N`.
 *
 * ⚠️ *SUPERSEDED AGAIN, 2026-10-11: "the block is now `CompanyCreditPanel`
 * … It ALWAYS renders — owner decision 2026-09-24".* That standalone block is
 * no longer mounted on this page. The balance per currency now renders INSIDE
 * the Sales & billing history's **Credit notes** tab (`CompanyDocumentsHistory`,
 * whose hook makes the balance read), beside the notes it is computed from —
 * one surface for one fact, and one read rather than two. The 2026-09-24 ruling
 * that the figure is never WITHHELD still holds there: "No credit" when there
 * is none, a failure sentence when the read fails, a refusal on a 403.
 *
 * The reasoning below is KEPT because it still binds that component:
 *
 * ## ⛔ DO NOT CLOSE THIS BY SUMMING `listCreditNotes({ companyId })`
 *
 * The obvious substitute is a page of that company's credit notes added up in
 * the browser, and it is wrong three times over:
 *
 *   - **A balance is not a sum of notes.** It is
 *     `SUM(spendable grand_total) − SUM(allocations)`, and the allocations are
 *     on a nested resource (`/credit-notes/:id/allocations`) that a list read
 *     does not carry. Summing the notes alone reports credit that has already
 *     been spent as still available — on the figure that decides how much of a
 *     real invoice gets discounted.
 *   - **A page is not the population.** `/credit-notes` pages, so a browser sum
 *     is a total over page 1 of N presented as an account balance, **and
 *     nothing on screen would say so.**
 *   - **`spendable` is a server flag.** Re-deriving `isApproved && !isVoid` is
 *     a second answer to a question the server already answers — and the two
 *     flags are not complements, because a note is approved and THEN voided.
 *
 * ## ⚠️ `null` AND `[]` ARE DIFFERENT FACTS, AND THE RETURN TYPE IS THE GUARD
 *
 * `[]` is *this company holds no spendable credit* — a real, ordinary state
 * the panel words as "No credit" / "No spendable credit". `null` (or a failed
 * read) is *we did not look*.
 * ⛔ **Never `?? []` at a call site**: that turns a read nobody made into a
 * claim that a customer is owed nothing, which is the one direction this figure
 * must not be wrong in.
 */

/**
 * One label/value pair. `<div>` inside `<dl>` is valid and keeps pairs together.
 *
 * `labelId` lets a form control in the `<dd>` name itself with
 * `aria-labelledby` — a `<dt>` is not a `<label>`, so without it the control
 * would announce as unlabelled.
 */
function DetailItem({
  label,
  labelId,
  className,
  children,
}: {
  label: string;
  labelId?: string;
  /** Column span, for the pairs that are a row rather than a cell. */
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cx('min-w-0', className)}>
      <dt
        {...(labelId ? { id: labelId } : {})}
        className="text-xs font-medium text-fg-muted"
      >
        {label}
      </dt>
      <dd className="m-0 mt-1 text-sm text-fg-secondary [overflow-wrap:anywhere]">{children}</dd>
    </div>
  );
}

function DetailGroup({
  title,
  columns = 'sm:grid-cols-2 lg:grid-cols-3',
  badge,
  note,
  children,
}: {
  title: string;
  columns?: string;
  /** A mark beside the heading, for a group whose fields behave differently. */
  badge?: ReactNode;
  /** Said once, under the heading, rather than repeated on every field in it. */
  note?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="border-b border-border px-4 py-4 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="m-0 text-sm font-semibold text-fg">{title}</h3>
        {badge}
      </div>
      {note && <p className="m-0 mt-1 text-xs text-fg-muted">{note}</p>}
      <dl className={cx('m-0 mt-3 grid gap-x-6 gap-y-4', columns)}>{children}</dl>
    </div>
  );
}

// The pipeline stage is a REFERENCE MASTER, not an enum, and an admin may
// rename or add rows — so the tone below is a loose hint derived from the
// text, defaulting to neutral for anything unrecognised. The label always
// carries the real meaning; colour is never the only signal.
function stageTone(name: string): BadgeTone {
  const text = name.toLowerCase();
  // Checked first, because "Past Client" also contains "client".
  if (/past|former|lapsed|lost|churn|inactive/.test(text)) return 'neutral';
  if (/client|customer|won/.test(text)) return 'success';
  if (/prospect|qualif/.test(text)) return 'info';
  return 'neutral';
}

/**
 * `stage: null` is a real, normal state — it means nobody has classified this
 * account yet, and both production companies are in it. It is deliberately NOT
 * phrased as something missing or overdue, and it is never rendered as "Lead".
 */
const UNCLASSIFIED_LABEL = 'Unclassified';

function StageBadge({ stage }: { stage: MasterRef | null }) {
  // No dot, where a real stage has one. Not a colour override: `neutral`
  // already sets a text colour, and two competing text utilities on one
  // element resolve by CSS source order, not by write order.
  if (!stage) return <Badge tone="neutral">{UNCLASSIFIED_LABEL}</Badge>;
  return (
    <Badge tone={stageTone(stage.name)} dot>
      {stage.name}
    </Badge>
  );
}

/** `linked` only when the value is NOT inside InlineField's trigger button (no nested anchors). */
function StaffValue({ person, linked }: { person: UserSummary | null; linked: boolean }) {
  if (!person) return <span className="text-fg-subtle">Unassigned</span>;
  return (
    <span className="flex min-w-0 items-center gap-2">
      <Avatar name={person.name} size="sm" />
      <span className="min-w-0">
        <span className="block truncate text-sm text-fg">{person.name}</span>
        {linked && person.email !== '' ? (
          <EmailLink address={person.email} tone="muted" className="block truncate text-xs" />
        ) : (
          <span className="block truncate text-xs text-fg-muted">{person.email}</span>
        )}
      </span>
    </span>
  );
}

/** Users type "seed.edu", not "https://seed.edu" — and the API accepts either. */
function externalHref(value: string): string {
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

/**
 * A `<button>` may not contain an `<a>`, so following the value and editing it
 * are two controls side by side rather than one. Never hover-only: on a phone
 * there is no hover, and calling the number is the reason half these fields
 * exist.
 */
function ValueAction({ value, kind }: { value: string | null; kind: 'url' | 'email' | 'tel' }) {
  if (!value || value.trim() === '') return null;
  const trimmed = value.trim();
  const href =
    kind === 'email'
      ? mailtoHref(trimmed)
      : kind === 'tel'
        ? `tel:${trimmed.replace(/\s+/g, '')}`
        : externalHref(trimmed);
  const label =
    kind === 'email' ? `Email ${trimmed}` : kind === 'tel' ? `Call ${trimmed}` : `Open ${trimmed}`;
  /*
   * Only `url` opens a new tab. *SUPERSEDED 2026-10-16: `email` joined it while
   * the mail link was a Gmail compose URL (2026-09-18).* The owner chose a real
   * `mailto:` again, which, like `tel:`, is handed to another application and
   * leaves this page alone.
   */
  const opensElsewhere = kind === 'url';

  return (
    <Tooltip label={label}>
      <a
        href={href}
        aria-label={label}
        {...(opensElsewhere ? { target: '_blank', rel: 'noreferrer noopener' } : {})}
        className={cx(
          'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-fg-subtle max-sm:h-9 max-sm:w-9',
          'transition-colors duration-150 ease-out motion-reduce:transition-none',
          'hover:bg-surface-hover hover:text-fg',
        )}
      >
        {kind === 'email' ? <MailIcon /> : kind === 'tel' ? <PhoneIcon /> : <ExternalIcon />}
      </a>
    </Tooltip>
  );
}

function MailIcon() {
  return (
    <svg {...iconProps('h-3.5 w-3.5')}>
      <rect width="20" height="16" x="2" y="4" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg {...iconProps('h-3.5 w-3.5')}>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92Z" />
    </svg>
  );
}

function ExternalIcon() {
  return (
    <svg {...iconProps('h-3.5 w-3.5')}>
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </svg>
  );
}

/*
 * The contact ROW — identity, group headings and the primary badge — now lives
 * in `CompanyContactsList`, so the card list and the table cannot describe a
 * contact differently. Only the loading placeholder below stays here, because
 * the `!company` branch renders it before there is anything to group.
 */

function DetailsSkeleton() {
  return (
    <Panel>
      {Array.from({ length: 3 }, (_, group) => (
        <div key={group} className="border-b border-border px-4 py-4 last:border-b-0">
          <Skeleton className="h-4 w-32" />
          <div className="mt-3 grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }, (_, item) => (
              <div key={item} className="grid gap-1.5">
                <Skeleton className="h-2.5 w-20" />
                <Skeleton className="h-3.5 w-36" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </Panel>
  );
}

/**
 * The loading placeholder, in the real shape rather than a spinner.
 *
 * `withActions` follows `canUpdate` for the same reason the live table's does:
 * a skeleton column for a control this reader will never be shown is a
 * placeholder for nothing, and the layout would shift the moment the rows
 * arrived.
 */
function ContactsSkeleton({
  withActions = false,
  withRowActions = false,
}: {
  withActions?: boolean;
  /** Edit/Delete — `canUpdate('contacts') || canDelete('contacts')`, as the list decides it. */
  withRowActions?: boolean;
}) {
  // 664 for the four base columns, +120 for Primary, +96 for Actions — the
  // same per-column floors `CompanyContactsList` sums.
  const minWidth = 664 + (withActions ? 120 : 0) + (withRowActions ? 96 : 0);
  return (
    <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
      {/* The column set and both floors mirror `CompanyContactsList` exactly —
          a skeleton one column narrower than the table it stands in for is a
          layout jump at the moment the data lands. */}
      <Table style={{ minWidth }}>
        <THead>
          <TR>
            <TH>Contact</TH>
            <TH className="w-48">Title</TH>
            <TH className="w-56">Group</TH>
            <TH className="w-44">Updated</TH>
            {withActions && <TH className="w-52">Primary</TH>}
            {withRowActions && (
              <TH className="w-24" numeric>
                Actions
              </TH>
            )}
          </TR>
        </THead>
        <TBody>
          {Array.from({ length: SKELETON_ROWS }, (_, i) => (
            <TR key={i}>
              <TD>
                <div className="flex items-center gap-2.5">
                  <Skeleton className="h-6 w-6 rounded-full" />
                  <div className="grid gap-1.5">
                    <Skeleton className="h-3 w-32" />
                    <Skeleton className="h-2.5 w-44" />
                  </div>
                </div>
              </TD>
              <TD>
                <Skeleton className="h-3 w-24" />
              </TD>
              <TD>
                <Skeleton className="h-9 w-full" />
              </TD>
              <TD>
                <Skeleton className="h-3 w-28" />
              </TD>
              {withActions && (
                <TD>
                  <Skeleton className="h-3 w-24" />
                </TD>
              )}
              {withRowActions && (
                <TD numeric>
                  <Skeleton className="ml-auto h-6 w-14" />
                </TD>
              )}
            </TR>
          ))}
        </TBody>
      </Table>
    </TableWrap>
  );
}

// --- Xero configurations ---------------------------------------------------

/**
 * `null` on a sales default is a REAL answer, not a gap — the first option in
 * Xero's own dropdowns. Rendering it as a dash would invite someone to "fix" a
 * company that is already correct. **Both production companies are in this
 * state** — the only one reachable today — so it must read well permanently,
 * not as a placeholder.
 */
const ORG_DEFAULT_LABEL = USE_ORGANISATION_SETTINGS;

/**
 * Same idea for the branding theme, worded for a THEME rather than
 * `ORG_DEFAULT_LABEL`: the sales defaults fall back to the organisation's
 * *settings*, while a document with no theme prints on its default *template*.
 * `brandingThemeOptions` adds no "none" row of its own so each screen can say
 * which it means.
 */
const ORG_DEFAULT_THEME_LABEL = 'Use Xero’s organisation default';

/**
 * Xero's own wording for the three line-amount types, so the value reads the
 * same here as on the Xero screen it describes.
 *
 * ⚠️ These say what XERO should assume and change nothing in SEED EMERGE: line
 * arithmetic here is tax-EXCLUSIVE with no inclusive mode, so a company can
 * read "Tax inclusive" while every total this app prints is exclusive. The
 * panel says so explicitly rather than relying on the label.
 */
const LINE_AMOUNT_LABELS: Record<XeroLineAmountType, string> = {
  INCLUSIVE: 'Tax inclusive',
  EXCLUSIVE: 'Tax exclusive',
  NONE: 'No tax',
};

function lineAmountLabel(value: XeroLineAmountType | null): string {
  if (value === null) return ORG_DEFAULT_LABEL;
  // `noUncheckedIndexedAccess` types this as `string | undefined`, which is
  // useful: a member Xero adds later is still a real value, and showing it raw
  // beats falling through to a blank.
  return LINE_AMOUNT_LABELS[value] ?? value;
}

/**
 * The code is the stored value; the name is what a person reads, so the name
 * leads. `xeroSalesTaxName` is a server-side cache derived from the code and
 * can legitimately be absent while the code is set — the code is then shown
 * rather than nothing, since a blank tax rate on a company that has one would
 * be a lie about the customer's VAT.
 */
function TaxRateValue({ company }: { company: Company }) {
  if (company.xeroSalesTaxName) return <>{company.xeroSalesTaxName}</>;
  if (company.xeroSalesTaxType) {
    return <span className="font-mono text-xs">{company.xeroSalesTaxType}</span>;
  }
  return <>{ORG_DEFAULT_LABEL}</>;
}

/*
 * `api.xeroBrandingThemes()` THROWS on failure and never resolves to an empty
 * list — an empty list would read as "this organisation has no templates",
 * indistinguishable from a real answer — so every failure must reach the
 * screen, kept apart by the advice each needs.
 *
 * ⚠️ The four-way model and its fetch live in `useXeroBrandingThemes`, read
 * ONCE at the page level and handed to this panel — the theme picker resolves
 * a theme's NAME from that list rather than from the stored
 * `xeroBrandingThemeName`, so a second read here could disagree with the
 * page's about which one failed, and would spend a second call out of the
 * office-wide Xero budget.
 *
 * `reconnect` (re-authorise, a dead token) and `retry` (Xero did not answer,
 * wait) are both `502` and want opposite advice — never branch on status.
 */

/*
 * ⚠️ The fold that used to live here — `emerge.xero.salesDefaultsCollapsed` in
 * `localStorage`, with its own Show/Hide button — is GONE (2026-10-11). The Xero
 * configurations are body content of the page's "Xero & billing setup" section
 * now, and that section folds through the per-user SERVER layout preference
 * (`useCompanyPageLayout`), like every other section on this page. A second,
 * browser-local fold inside a server-folded section would be two answers to
 * "is this open", kept in two stores that cannot see each other.
 */

/**
 * The one sentence a FOLDED "Xero & billing setup" section still has to say, or
 * null when nothing needs saying. Rendered as that section's `summary`.
 *
 * The sales defaults themselves are never urgent — each saves and reports on
 * its own field, and `null` is a real answer, not a gap (see
 * `ORG_DEFAULT_LABEL`). The themes fetch is the half that can fail with nobody
 * having touched anything, and it is what the branding-theme picker inside
 * that section resolves its label from — so hiding "Xero rejected the stored
 * connection" behind the fold would hide a whole-connection failure, and hide
 * the one control that failure has disabled. `missing` (a 404 on the endpoint)
 * stays behind the fold: it is a normal state on the way to the backend
 * shipping its half.
 */
function themesAlert(state: BrandingThemesState): { tone: 'danger' | 'warning'; line: string } | null {
  if (state.error === null || state.error.kind === 'missing') return null;
  if (state.error.kind === 'reconnect') {
    return { tone: 'danger', line: 'Xero rejected the stored connection.' };
  }
  // ⚠️ A cooldown is NOT the `retry` line: "Xero didn't answer" describes a
  // tenant that failed to respond, while a rate limit is a tenant that
  // answered on purpose to say the budget is spent. Retrying is exactly what
  // cannot work here. Warning, not danger — the connection is healthy.
  if (state.error.kind === 'cooldown') {
    return {
      tone: 'warning',
      line:
        state.error.retryAfterSeconds === undefined
          ? 'Xero’s rate limit has been reached, so its branding themes are unavailable.'
          : xeroCooldownSummary('branding themes', state.error.retryAfterSeconds),
    };
  }
  if (state.error.kind === 'retry') {
    return { tone: 'warning', line: 'Xero didn’t answer, so its branding themes are missing.' };
  }
  return { tone: 'danger', line: 'The branding themes couldn’t be loaded.' };
}

/**
 * Writes one company column and reports what happened, in `InlineField`'s
 * vocabulary. Handed down from the page: `saveCompanyField` is the ONE place
 * that knows the record's id, replaces the loaded record with the response,
 * and turns a 403/404/422 into a sentence. A second copy here would be a
 * second answer to "did that save?", and a second copy of the record for the
 * Edit dialog to disagree with.
 */
type SaveCompanyField = (
  requestKey: string,
  patch: CompanyInput,
  readBack?: { sent: string; read: (saved: Company) => string; noun: string },
) => Promise<InlineSaveResult>;

/**
 * **Xero configurations** — everything on this account that is about how Xero
 * treats it. Three `DetailItem`s for a `DetailGroup` inside the page's "Xero &
 * billing setup" section, UNDER the link panel: the link is the prerequisite fact
 * (what this account IS in Xero) and these are what is configured around it.
 *
 * ⚠️ *SUPERSEDED 2026-10-11: "Its own panel, not a group inside 'Account
 * details' … with its own fold."* The reasoning — facts about another system,
 * with their own fetch and failure states — is what put them in the Xero section
 * rather than in Overview; the panel shell and the `localStorage` fold went
 * because the SECTION folds now, through the per-user layout preference. What a
 * folded section still has to say is `themesAlert`, rendered by the page as that
 * section's summary.
 *
 * Three fields, and they are NOT all the same kind of thing:
 *
 * - **Line amounts** and **Tax rate** are Xero's own *Sales defaults* — what
 *   Xero is told to assume about a document for this customer.
 * - **Branding theme** is a column of OURS. It seeds the letterhead on a new
 *   quote or invoice and **is never pushed to the Xero contact** — Xero
 *   exposes a contact's theme read-only, so no wording here may say it is
 *   sent. Owner's instruction, 2026-08-30: it used to sit in Account details
 *   and belongs beside the other two.
 *
 * ⚠️ The group therefore holds MORE than the sales defaults, which is why the
 * heading is no longer "Xero sales defaults". Three other surfaces still carry
 * that narrower heading over the two-field set it correctly describes —
 * `CompanyFormModal`, `XeroSalesDefaultsDialog` and `CompanyApprovalPage` —
 * and none of them offers a branding theme. Do not rename those to match.
 *
 * All three are edited HERE in place — one key per PATCH — and the two sales
 * defaults also in bulk by `CompanyFormModal`; both read `company` off the one
 * loaded record and both replace it with the server's response, so neither is
 * left showing a value the other changed.
 *
 * ⚠️ Xero exposes no way to ask which theme a *contact* carries, so nothing
 * here reads one: the picker's options are the ORGANISATION's themes and the
 * chosen value is ours.
 */
function XeroSalesDefaultsFields({
  company,
  canUpdate,
  themes,
  onSave,
}: {
  company: Company;
  canUpdate: boolean;
  /**
   * Read ONCE at the page level and handed down, never re-read here — the read
   * is a rate-limited Xero call shared with the whole office.
   */
  themes: BrandingThemesState;
  onSave: SaveCompanyField;
}) {
  const { connection, loading: connectionLoading, notInstalled } = useXero();

  // The tenant's tax rates, for the rate picker only. Gated on `canUpdate`,
  // unlike the theme list the page reads for everyone: `xeroSalesTaxName` is a
  // server-side cache, so a read-only page renders the rate by name without
  // asking Xero, while a theme's name can only come from the live list — and
  // the office-wide Xero rate limit shouldn't spend on a read nobody here can
  // act on. ⚠️ Not gated on the SECTION's fold either: `CollapsibleSection`
  // keeps a folded section's children MOUNTED, so this read is made once per
  // record whether or not the section is open — folding and unfolding never
  // re-reads it.
  const rates = useXeroTaxRates(
    canUpdate && !connectionLoading && connection.connected && !notInstalled,
  );

  // ⚠️ Unreachable does NOT mean frozen — CLEARING still works:
  // `resolveTaxDefaults` returns early on `null` and never calls Xero, so only
  // a non-null code is validated against the live list. Locking the control
  // whenever the tenant is unreachable would trap an account on a rate it
  // could not be released from.
  const ratesUnavailable = connectionLoading || !connection.connected || rates.error !== null;
  const taxCanOnlyClear = ratesUnavailable && company.xeroSalesTaxType !== null;

  // Plain functions returning elements, never components declared in here — a
  // component declared inside this one is a new type on every render, so each
  // field would unmount (losing its draft and "Saved" line) whenever anything
  // else on the panel changed. Same rule as the page's `textField`/`staffField`.
  const lineAmountField = () => (
    <InlineField
      kind="picker"
      label="Line amounts Xero should default to"
      value={company.xeroSalesLineAmountType ?? ''}
      display={<>{lineAmountLabel(company.xeroSalesLineAmountType)}</>}
      disabled={!canUpdate}
      options={LINE_AMOUNT_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
      /* `null` is the FIRST row in Xero's own dropdown, and a real answer here too. */
      emptyOptionLabel={ORG_DEFAULT_LABEL}
      placeholder={ORG_DEFAULT_LABEL}
      searchPlaceholder="Search line amount types…"
      onSave={async (next) => {
        const result = await onSave(
          'xeroSalesLineAmountType',
          /*
           * `toLineAmountType` narrows the control's string back to the union
           * and answers `null` for anything outside it — including `''`, which
           * is the organisation-settings row and the documented way to clear
           * the column.
           */
          { xeroSalesLineAmountType: toLineAmountType(next) },
          {
            sent: next,
            read: (saved) => saved.xeroSalesLineAmountType ?? '',
            noun: 'line-amount default',
          },
        );
        return next === '' && result.status === 'saved'
          ? { status: 'saved', message: 'Saved.' }
          : result;
      }}
    />
  );

  const taxRateField = () => {
    const stored = company.xeroSalesTaxType;
    /*
     * Codes are matched case-insensitively — the server normalises casing on
     * write, so the stored code and the live one can differ in case for one
     * rate. The picker's VALUE is then the code as the live list spells it,
     * because `Combobox` finds its selected option by exact string.
     */
    const live = rates.rates.find(
      (rate) => stored !== null && rate.taxType.toUpperCase() === stored.toUpperCase(),
    );

    /*
     * REVENUE RATES ONLY, exactly as in `XeroSalesDefaultsFields` — the two
     * controls set the same column and must not offer different lists. An
     * expense code is refused at push time (`422 XERO_TAX_TYPE_NOT_REVENUE`),
     * and `RRINPUT` vs `RROUTPUT` has already been picked wrongly once.
     *
     * `sellableTaxRates` re-injects the stored code when it is itself one of
     * the withheld expense rates, so an account already filed against one
     * still renders it rather than rendering blank.
     */
    const options: InlineFieldOption[] = sellableTaxRates(rates.rates, stored ?? '').map(
      (rate) => ({
        value: rate.taxType,
        label: rate.name,
        hint: taxRateHint(rate),
      }),
    );

    /*
     * The stored code stays selectable when the live list no longer carries it
     * — an accountant can retire or rename a rate long after this account was
     * filed. Dropped, the control renders blank, which reads as "use
     * organisation settings" and is one stray save from clearing a real
     * default.
     *
     * `xeroSalesTaxName` IS safe to read here, unlike the branding theme's
     * cached name: it is derived server-side from the code on every write. And
     * an empty `rates` proves nothing about the code, so it is only DESCRIBED
     * as missing when there is a list for it to be missing from.
     */
    if (stored !== null && !live) {
      options.unshift({
        value: stored,
        label: company.xeroSalesTaxName ?? stored,
        hint: rates.rates.length > 0 ? `${stored} · not in the current Xero list` : stored,
      });
    }

    const base = connectionLoading
      ? 'Checking the Xero connection…'
      : !connection.connected
        ? notInstalled
          ? 'Xero is not set up on the API yet, so its rates cannot be listed.'
          : 'Xero is not connected, so its rates cannot be listed.'
        : rates.error
          ? // A cooldown names itself rather than borrowing "could not be
            // read" — short here; the full sentence with the wait is in the
            // alert under this field.
            rates.error.kind === 'cooldown'
            ? taxCanOnlyClear
              ? 'Xero rate limit reached — list unavailable. The chosen rate can still be cleared.'
              : 'Xero rate limit reached — no rate can be chosen right now.'
            : taxCanOnlyClear
              ? 'Rate list unavailable. The chosen rate can still be cleared.'
              : 'Rate list unavailable — no rate can be chosen.'
          : rates.loading
            ? 'Reading this organisation’s rates from Xero…'
            : rates.rates.length === 0
              ? 'This Xero organisation lists no tax rates.'
              : null;

    return (
      <div className="grid gap-1.5">
        <InlineField
          kind="picker"
          label="Sales tax rate Xero should default to"
          value={live?.taxType ?? stored ?? ''}
          display={<TaxRateValue company={company} />}
          disabled={!canUpdate || (ratesUnavailable && !taxCanOnlyClear)}
          options={options}
          optionsLoading={rates.loading}
          emptyOptionLabel={ORG_DEFAULT_LABEL}
          placeholder={rates.loading ? 'Reading rates from Xero…' : ORG_DEFAULT_LABEL}
          searchPlaceholder="Search by name or code…"
          footnote={base ?? undefined}
          onSave={async (next) => {
            const result = await onSave(
              'xeroSalesTaxType',
              // The CODE, never the name — validated against the LIVE Xero
              // list on every write, so an unknown code and a gone tenant are
              // both a 422 naming `xeroSalesTaxType`.
              { xeroSalesTaxType: next === '' ? null : next },
              {
                // Compared case-insensitively: the server normalises the code,
                // so an exact compare would report a save that worked as
                // "nothing was saved".
                sent: next.toUpperCase(),
                read: (saved) => (saved.xeroSalesTaxType ?? '').toUpperCase(),
                noun: 'tax rate',
              },
            );
            return next === '' && result.status === 'saved'
              ? { status: 'saved', message: 'Saved.' }
              : result;
          }}
        />

        {/* `missing` is a 404 on the endpoint, which retrying cannot move. */}
        {canUpdate && connection.connected && rates.error && rates.error.kind !== 'missing' && (
          <div className="flex flex-wrap items-start gap-2">
            <span
              role="alert"
              className={cx(
                'min-w-0 flex-1 text-xs [overflow-wrap:anywhere]',
                // A cooldown is a calm state of a HEALTHY connection — not
                // painted as a fault.
                rates.error.kind === 'cooldown' ? 'text-warning' : 'text-danger',
              )}
            >
              {rates.error.message}
            </span>
            <Button size="sm" variant="secondary" onClick={rates.reload}>
              Try again
            </Button>
          </div>
        )}
      </div>
    );
  };

  /**
   * The account's default Xero **branding theme** — the letterhead its
   * documents are printed against.
   *
   * ⚠️ It sits beside the two sales defaults and is NOT one of them: those two
   * are what Xero is told to assume about a document, while this is a column of
   * OURS that seeds a new quote or invoice. **It is never pushed to Xero as a
   * contact field** — Xero exposes a contact's theme read-only — so nothing
   * here may be worded as being sent.
   *
   * ⚠️ Three facts shape this field:
   *
   * 1. **The name is resolved at render time from the live list, never from
   *    `xeroBrandingThemeName`.** A stored label goes stale the moment someone
   *    renames a theme in Xero; a lookup cannot. Never SENT either — a name
   *    alongside the id is ignored.
   * 2. **An unknown id is a `422`** naming `xeroBrandingThemeId`, which
   *    `onSave` routes onto this control with no special handling. The picker
   *    offers only ids from the live list plus the stored one.
   * 3. **A disconnected tenant still SAVES** — validation is skipped rather
   *    than the write refused, so nothing here may block a save on the
   *    connection being up.
   *
   * `brandingThemeOptions` is passed `null` for the cached name deliberately —
   * see (1). A theme an accountant has since deleted is labelled with its GUID
   * rather than dropped, since a dropped id renders the control blank, reading
   * as "organisation default" — one stray save from silently changing the
   * letterhead.
   *
   * ⚠️ **Re-selecting that deleted theme must not write.** It cannot:
   * `InlineField` short-circuits a commit whose value equals the stored one,
   * and the injected option's value IS the stored string. The only writes this
   * field can make are a different live id, or `null`.
   */
  const brandingThemeField = () => {
    const stored = company.xeroBrandingThemeId;
    // A GUID's case is not significant, so the list is searched
    // case-insensitively — and the picker's VALUE is the id as the LIVE list
    // spells it, since `Combobox` matches by exact string.
    const live =
      stored === null
        ? undefined
        : themes.themes.find(
            (theme) => theme.brandingThemeId.toLowerCase() === stored.toLowerCase(),
          );

    // ⚠️ NOT a guard on saving — the server takes either a set or a clear
    // right now. It guards OFFERING: with no live list there is no id that may
    // legitimately be sent, so the only rows left are the current value and
    // the organisation-default row. Inert only when there is nothing to
    // choose from AND nothing to clear.
    const listUnreachable =
      connectionLoading || !connection.connected || notInstalled || themes.error !== null;
    const canOnlyClear = listUnreachable && stored !== null;

    // The stored id is never described as retired unless there is a list for
    // it to be missing from — an empty `themes` is as often a failed read as
    // an answer.
    const storedThemeNote = themes.loading
      ? 'Reading this organisation’s branding themes from Xero…'
      : themes.themes.length > 0
        ? `${THEME_NOT_IN_LIST}. Showing its id.`
        : // The line under a STORED theme that could not be resolved — naming
          // the rate limit tells the reader the value is intact and only its
          // LABEL is missing.
          themes.error?.kind === 'cooldown'
          ? 'Xero rate limit reached — the theme’s name can’t be looked up.'
          : 'Theme name can’t be shown — Xero’s themes aren’t available here.';

    const footnote = connectionLoading
      ? 'Checking the Xero connection…'
      : !connection.connected
        ? notInstalled
          ? 'Xero is not set up on the API yet, so its branding themes cannot be listed.'
          : 'Xero is not connected, so its branding themes cannot be listed.'
        : themes.error
          ? themes.error.kind === 'cooldown'
            ? canOnlyClear
              ? 'Xero rate limit reached — list unavailable. The chosen theme can still be cleared.'
              : 'Xero rate limit reached — no theme can be chosen right now.'
            : canOnlyClear
              ? 'Theme list unavailable. The chosen theme can still be cleared.'
              : 'Theme list unavailable — no theme can be chosen.'
          : themes.loading
            ? 'Reading this organisation’s branding themes from Xero…'
            : themes.themes.length === 0
              ? 'This Xero organisation has no branding themes.'
              : `${themes.themes.length} themes, in Xero’s order.`;

    return (
      <div className="grid gap-1.5">
        <InlineField
          kind="picker"
          label="Xero branding theme"
          value={live?.brandingThemeId ?? stored ?? ''}
          display={
            stored === null ? (
              // A real answer, not an absence — so it is worded, never a dash.
              <span className="text-fg-subtle">{ORG_DEFAULT_THEME_LABEL}</span>
            ) : live ? (
              <>{live.name}</>
            ) : (
              // A disconnected tenant must not make a stored theme vanish.
              <span className="grid gap-0.5">
                <span className="font-mono text-xs">{stored}</span>
                <span className="text-xs text-fg-muted">{storedThemeNote}</span>
              </span>
            )
          }
          disabled={!canUpdate || (listUnreachable && !canOnlyClear)}
          options={brandingThemeOptions(themes.themes, stored, null)}
          optionsLoading={themes.loading}
          emptyOptionLabel={ORG_DEFAULT_THEME_LABEL}
          placeholder={themes.loading ? 'Reading themes from Xero…' : ORG_DEFAULT_THEME_LABEL}
          searchPlaceholder="Search branding themes…"
          footnote={footnote}
          onSave={(next) =>
            onSave(
              'xeroBrandingThemeId',
              { xeroBrandingThemeId: next === '' ? null : next },
              {
                // Compared case-insensitively — a GUID in different case is
                // still the same GUID.
                sent: next.toLowerCase(),
                read: (saved) => (saved.xeroBrandingThemeId ?? '').toLowerCase(),
                noun: 'branding theme',
              },
            )
          }
        />

        {/* `missing` is a 404 on the endpoint, which no amount of retrying moves. */}
        {canUpdate && connection.connected && themes.error && themes.error.kind !== 'missing' && (
          <div className="flex flex-wrap items-start gap-2">
            <span
              role="alert"
              className={cx(
                'min-w-0 flex-1 text-xs [overflow-wrap:anywhere]',
                // Calm, not red — see the same choice on the rate field above.
                themes.error.kind === 'cooldown' ? 'text-warning' : 'text-danger',
              )}
            >
              {themes.error.message}
            </span>
            <Button size="sm" variant="secondary" onClick={themes.reload}>
              Try again
            </Button>
          </div>
        )}
      </div>
    );
  };

  // Three `<dt>`/`<dd>` pairs for the caller's `DetailGroup` — no shell of its
  // own. The section they sit in folds; the alert a fold must not hide is
  // `themesAlert`, which the page renders as that section's summary.
  return (
    <>
      <DetailItem label="Line amounts in Xero">{lineAmountField()}</DetailItem>
      <DetailItem label="Tax rate in Xero">{taxRateField()}</DetailItem>
      {/* Last, and deliberately NOT a third sales default: those two say
          what Xero should assume about a document, while this is a
          column of ours that seeds a new quote or invoice and is never
          pushed to the Xero contact.

          It replaced a read-only list of the organisation's themes that
          used to sit here. The picker IS that list — every theme is a
          row in it, its footnote carries the count, and it reports the
          same fetch's failures with the same "Try again". Keeping both
          once they shared a group put two copies of one error message and
          two retry buttons for one request side by side. */}
      <DetailItem label="Xero branding theme">{brandingThemeField()}</DetailItem>
    </>
  );
}

/**
 * The folded Xero section's summary — `themesAlert`'s line, in its tone — or
 * `undefined` when the section has nothing urgent to say while folded.
 *
 * Both tones carry their WORD as well as their colour (the line itself names
 * what failed), so colour is never the only signal.
 */
function xeroSectionSummary(themes: BrandingThemesState): ReactNode {
  const unresolved = themesAlert(themes);
  if (unresolved === null) return undefined;
  return (
    <span
      role="alert"
      className={cx(
        'font-semibold',
        unresolved.tone === 'danger' ? 'text-danger' : 'text-warning',
      )}
    >
      {unresolved.line}
    </span>
  );
}

// --- the Sales & billing history section ----------------------------------

/**
 * **Sales & billing history** — the deals, quotes, invoices and credit notes
 * raised against this account, in one foldable section.
 *
 * A component of its own, declared at MODULE level, for one reason:
 * `useCompanyDocumentsHistory` needs the company's id, and the page has no id
 * to give it until the record has resolved — which happens below the page's
 * hook line. A component that mounts only once the record is ready can call
 * the hook unconditionally. (Not declared inside the page, which would make it
 * a new type on every render and remount the history on every keystroke.)
 *
 * It also owns the section's folded SUMMARY, because the counts the summary is
 * worded from come back on the same hook — a parent cannot word a summary over
 * state it does not hold.
 *
 * ⚠️ The Credit notes tab carries the per-currency credit BALANCE, which is why
 * the old standalone Credit block (`CompanyCreditPanel`) is no longer mounted
 * on this page: one surface for one fact. ⛔ A balance is per currency and is
 * never summed — that rule lives with the history component, not here.
 */
function DocumentsHistorySection({
  id,
  companyId,
  collapsed,
  onToggle,
  onMove,
  canMoveUp,
  canMoveDown,
  actions,
  reloadNonce,
}: {
  /** The shell's own id — handed through so this section is wired like its six siblings. */
  id: string;
  companyId: number;
  collapsed: boolean;
  onToggle: () => void;
  /** Absent since 2026-10-20: the page layout manager reorders; the shell draws no arrows. */
  onMove?: (direction: 'up' | 'down') => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  /**
   * The "+" create menu, when the reader may create anything — handed to the
   * history's own `actions` slot (the tab-strip row), where that component
   * reserved room for exactly this.
   */
  actions: ReactNode;
  /**
   * Bumped by the page when a document was raised from THIS page's "+" menu, so
   * the list it feeds is re-read rather than patched. A document raised
   * elsewhere is picked up on the next visit, exactly as before.
   */
  reloadNonce: number;
}) {
  const history = useCompanyDocumentsHistory(companyId);

  // Re-read on the page's say-so. `reload` is the hook's own re-fetch; this
  // effect only forwards the page's "something was raised" signal to it, and
  // skips the mount (nonce 0), when the hook has already made its first read.
  const { reload } = history;
  useEffect(() => {
    if (reloadNonce === 0) return;
    reload();
  }, [reloadNonce, reload]);

  return (
    <CollapsibleSection
      id={id}
      title="Sales & Billing"
      // `null` when every count is unread or refused — the shell then draws no
      // summary at all, rather than a `0` the reader was not allowed to verify.
      summary={companyDocumentsSummary(history.counts)}
      collapsed={collapsed}
      onToggle={onToggle}
      onMove={onMove}
      canMoveUp={canMoveUp}
      canMoveDown={canMoveDown}
    >
      <div className="px-4 py-4">
        <CompanyDocumentsHistory state={history} actions={actions} />
      </div>
    </CollapsibleSection>
  );
}

// --- page -----------------------------------------------------------------

export function CompanyDetailPage() {
  const { id: idParam = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { ability, user } = useAuth();

  /*
   * The read gate, computed here because the return that uses it has to sit
   * below every hook. See that return, further down, for the reasoning.
   */
  const canReadModule = ability.canRead('companies');

  const {
    connection: xeroConnection,
    loading: xeroLoading,
    notInstalled: xeroNotInstalled,
  } = useXero();

  // The connected tenant's branding themes, read ONCE for the whole page and
  // handed to `XeroSalesDefaultsFields` (the picker) and `xeroSectionSummary`
  // (the folded section's alert) — the only two things that read it.
  //
  // ⚠️ It stays HERE rather than moving into those fields with the picker they
  // feed. The read is a rate-limited Xero call against a budget the whole
  // office shares, and this `enabled` expression is what decides how often it
  // is spent — moving it under the section's fold would re-read on every
  // expand, and moving it into the fields at all invites a second call site
  // later.
  //
  // ⚠️ Not gated on `canUpdate`, unlike every other picker's list here: the
  // picker resolves a theme's NAME from this list rather than from the stored
  // `xeroBrandingThemeName` (see `brandingThemeField`), so a read-only visitor
  // needs it as much as an editor does — gating it would leave a bare GUID for
  // precisely the people who cannot fix it.
  const brandingThemes = useXeroBrandingThemes(
    !xeroLoading && xeroConnection.connected && !xeroNotInstalled,
  );

  /*
   * The section ORDER and FOLDS — a per-user SERVER preference (owner's
   * instruction, 2026-10-11), so one person's arrangement follows them to every
   * company and every device.
   *
   * ⚠️ Called here, above every early return, like every other hook — and NOT
   * keyed on the company: the layout is the reader's, not the record's.
   *
   * 2026-10-20 (layout manager): `folds` keeps FOLDING only; `pageLayout` owns
   * order, width and hidden. Its defaults are the person's stored companyPage
   * order (declared order when none), so nobody's page changes until they use
   * Edit layout. `companyPageLayoutDefaults` returns a fresh array per render;
   * the engine keys on content.
   */
  const folds = useCompanyPageLayout();
  const pageLayout = usePageLayout(
    'company',
    withLeadHistoryDefault(companyPageLayoutDefaults(folds.order)),
  );
  /*
   * ONE Reset for the whole arrangement: the manager's layout AND the folds
   * (with the carried-over order) go back to the built-in layout together, and
   * the button counts as "default" only when both are.
   */
  const layoutControl: PageLayout = useMemo(
    () => ({
      ...pageLayout,
      isDefault: pageLayout.isDefault && folds.isDefault,
      reset: () => {
        pageLayout.reset();
        folds.reset();
      },
    }),
    [pageLayout, folds],
  );

  /*
   * Whether this reader may see the Marketing emails section AT ALL. A section
   * the reader may not read is SKIPPED — not rendered folded, not rendered
   * empty — so the question is asked once, here, and `renderSection` consults
   * the answer. ⚠️ A hidden section is a VIEW decision; the API behind the
   * panel refuses on its own terms.
   */
  const canReadMarketing = useCanReadMarketingHistory();
  const canReadTasks = ability.canRead('tasks');
  const canReadNotes = ability.canRead('notes');
  // Services (2026-10-18) reads the Client Deliverables tracker: skipped, and
  // never fetched, without `deliverables` read.
  const canReadDeliverables = ability.canRead('deliverables');

  /*
   * The "+" on the Sales & billing history section: a NEW deal, quote or invoice
   * for this company, seeded from its projects of interest — the same menu and
   * the same flow `/companies` mounts, gated identically.
   *
   * Creating a deal, quote or invoice is gated on THAT module, not on
   * `companies` — nothing the menu offers writes a company. A target the reader
   * cannot create is DROPPED from the menu rather than shown disabled, and with
   * no target left the "+" is withheld entirely (an affordance whose only
   * outcome is an empty menu must not render).
   */
  const createTargets = useMemo(
    () => COMPANY_CREATE_TARGETS.filter((target) => ability.canCreate(target.module)),
    [ability],
  );
  /*
   * What the "+" asked for; `null` is closed, so there is no second `open` flag
   * to disagree with it. The request carries `{id, name}` and NOTHING ELSE,
   * because the flow fetches the company itself — the same shape as the three
   * `/companies` mounts, and the reason there is one code path rather than one
   * per mount point.
   */
  const [projectLinesRequest, setProjectLinesRequest] =
    useState<CompanyProjectLinesRequest | null>(null);
  /**
   * Bumped when the flow SAVES a document, so the history section re-reads.
   * The history is a child component's own fetch; a nonce is how the page tells
   * it something changed without holding a copy of its list.
   */
  const [historyNonce, setHistoryNonce] = useState(0);
  /**
   * ONE reload token for every task AND note surface on this page
   * (2026-10-16): bumped by the Tasks section's and the Notes section's own
   * writes AND by the Tasks & notes drawer's `onActivityChanged`. Both
   * sections re-read on it, and the header's `ActivityButton` is keyed on it
   * so its counts re-read too.
   */
  const [tasksNonce, setTasksNonce] = useState(0);
  const bumpTasks = useCallback(() => setTasksNonce((n) => n + 1), []);
  /*
   * ⚠️ THE MENU'S MODULE AND THE FLOW'S KIND ARE DIFFERENT VOCABULARIES and the
   * names are one letter apart: the menu speaks module slugs (`quotes`), the
   * flow speaks a record kind (`quote`). They are MAPPED, never cast. Returns
   * `true` for every target, so nothing here navigates to the item's `href` —
   * which stays on the item as the honest destination if this interception is
   * ever removed.
   */
  const openProjectLines = useCallback(
    (company: { id: number; name: string }) =>
      (module: CompanyCreateTarget['module']): boolean => {
        const kind = { quotes: 'quote', invoices: 'invoice', deals: 'deal' } as const;
        setProjectLinesRequest({ kind: kind[module], company });
        return true;
      },
    [],
  );

  // A non-numeric segment can never match a record, so it is a not-found, not
  // a request worth making.
  const companyId = /^\d+$/.test(idParam) ? Number(idParam) : Number.NaN;
  const validId = Number.isInteger(companyId) && companyId > 0;

  const [recordNonce, setRecordNonce] = useState(0);
  const [contactsNonce, setContactsNonce] = useState(0);
  const [linkOpen, setLinkOpen] = useState(false);

  // Each fetch stores the key of the request it answered, so "is this stale?"
  // is derived during render rather than a `setLoading(true)` at the top of
  // the effect — which also removes any window where a new id shows the
  // previous record as settled.
  const recordKey = `${companyId}:${recordNonce}`;
  const contactsKey = `${companyId}:${contactsNonce}`;

  const [recordResult, setRecordResult] = useState<{ key: string; value: RecordState } | null>(null);
  const [contactsResult, setContactsResult] = useState<{
    key: string;
    result: Paginated<Contact> | null;
    error: string | null;
    /** True only for a 404 from the related endpoint — never for an empty list. */
    gone: boolean;
  } | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [addContactOpen, setAddContactOpen] = useState(false);
  /*
   * The contact row actions. `null` is closed for both, so there is no second
   * `open` flag to disagree with the record each one is about. The edit modal
   * is the same `ContactFormModal` as Add, mounted a second time only because
   * its create and edit modes seed from different props.
   */
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [pendingContactDelete, setPendingContactDelete] = useState<Contact | null>(null);
  const [deletingContact, setDeletingContact] = useState(false);
  const [contactDeleteError, setContactDeleteError] = useState<string | null>(null);
  // The sheet owns its own fetching; the page holds only whether it is open.
  const [activityOpen, setActivityOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  /**
   * How many notes on this account the VIEWER CAN SEE, read when the delete
   * confirmation opens. A floor on what the delete destroys, never the total —
   * see `openDeleteCounts` and `companyNotesConsequence`.
   *
   * ⚠️ `null` is "not read yet, or the read failed" and is NOT zero.
   */
  const [noteTotal, setNoteTotal] = useState<number | null>(null);
  /**
   * How many notes on this account SEED EMERGE wrote itself — `systemCount` from
   * the same read.
   *
   * 🔴 **NOT the same kind of number as `noteTotal` beside it.** A system note is
   * readable by every reader who may read notes at all (`visibilityClause`'s
   * second arm is `OR n.is_system = 1`), so this figure is EXACT and identical
   * for everybody, where `noteTotal` is viewer-scoped and is only ever a floor.
   * ⛔ **They are never summed** — see `companyNotesConsequence`.
   *
   * ⚠️ `0` here, not `null`: an absent key and a real zero both mean "say nothing
   * about automatic notes", so there is no third state to carry.
   */
  const [systemNoteTotal, setSystemNoteTotal] = useState(0);
  /** Only the newest open's reply may set the figures. */
  const noteTotalSeq = useRef(0);

  const state: RecordState = !validId
    ? { status: 'missing' }
    : recordResult?.key === recordKey
      ? recordResult.value
      : { status: 'loading' };

  useEffect(() => {
    if (!validId) return;
    const controller = new AbortController();
    api
      .getCompany(companyId, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setRecordResult({ key: recordKey, value: { status: 'ready', company: response.data } });
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        if (caught instanceof ApiError && caught.status === 404) {
          setRecordResult({ key: recordKey, value: { status: 'missing' } });
          return;
        }
        setRecordResult({
          key: recordKey,
          value: {
            status: 'error',
            message: caught instanceof Error ? caught.message : 'Failed to load this account',
          },
        });
      });
    return () => controller.abort();
  }, [companyId, validId, recordKey]);

  const companyReady = state.status === 'ready';

  /*
   * This account's contacts — ONE request, serving BOTH the grouped list in the
   * Contacts section and the primary-contact picker in Overview.
   *
   * ⚠️ **It used to be two fetches of the same endpoint** — the list at
   * `perPage: 5` with a pager, the picker at `perPage: 100` — and they could
   * disagree about who works here. They are one now for two reasons that
   * arrived together: a group heading cannot be drawn over a five-row page
   * without stating a wrong count, and a picker offering five of an account's
   * contacts is indistinguishable from an account that only has five. Both
   * wanted the same 100 rows.
   *
   * ⚠️ **It is no longer gated on `canUpdate`.** The picker's copy was, because
   * a read-only page has no picker to fill; the LIST is not optional for
   * anybody. Net effect: a viewer still makes exactly one contacts request, and
   * an editor now makes one instead of two.
   *
   * Gated on the record having loaded: `GET /companies/:id/contacts` 404s for
   * a company that does not exist and 200s with an empty array for a real one
   * with no contacts. Running this only after the record resolved means a 404
   * here can't mean "bad id in the URL" — it means the account disappeared.
   */
  useEffect(() => {
    if (!companyReady) return;
    const controller = new AbortController();
    api
      .listCompanyContacts(
        companyId,
        { perPage: CONTACTS_PER_PAGE, sort: 'name', order: 'asc' },
        controller.signal,
      )
      .then((response) => {
        if (controller.signal.aborted) return;
        setContactsResult({ key: contactsKey, result: response, error: null, gone: false });
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        const gone = caught instanceof ApiError && caught.status === 404;
        setContactsResult({
          key: contactsKey,
          result: null,
          error: gone
            ? null
            : caught instanceof Error
              ? caught.message
              : 'Failed to load contacts',
          gone,
        });
      });
    return () => controller.abort();
  }, [companyReady, companyId, contactsKey]);

  const refreshContacts = useCallback(() => setContactsNonce((n) => n + 1), []);
  // A Xero action changes the record, not a related list — the link lives on
  // the company itself, and re-reading it is also how a queued push (`pending`
  // rather than `synced`) is noticed.
  const refreshRecord = useCallback(() => setRecordNonce((n) => n + 1), []);

  const contactsFresh = contactsResult?.key === contactsKey;
  const contactsLoading = companyReady && !contactsFresh;
  // The previous answer stays on screen (dimmed) while a refresh loads — but
  // only within one company, or navigating between records would flash the
  // wrong contacts.
  const contacts =
    contactsResult?.key.split(':')[0] === String(companyId) ? contactsResult.result : null;
  const contactsError = contactsFresh ? contactsResult.error : null;
  const contactsGone = contactsFresh ? contactsResult.gone : false;

  /*
   * ⚠️ **The account holds contacts this page did not fetch.**
   *
   * `meta.total` above what came back means every group count below is a FLOOR
   * and a whole group may be missing entirely. It is stated rather than
   * silently truncated: a contact absent from a list is indistinguishable from
   * a contact who does not exist, and a group heading is a count somebody
   * reads as a total.
   *
   * There is no pager to reach the rest, deliberately — 100 is this endpoint's
   * ceiling, so paging would put a group heading back over a partial page,
   * which is the defect the single fetch exists to remove. The honest answer
   * is to say the list is capped; the real fix is `?contactGroupId=` on
   * `/contacts`, which is a different screen.
   */
  const contactsTruncated = contacts !== null && contacts.meta.total > contacts.data.length;

  // Deleting an account is admin-only (`canDelete` encodes it); the
  // Add-contact button is a contacts write, so it asks about that module.
  const canUpdate = ability.canUpdate('companies');
  const canDelete = ability.canDelete('companies');
  const canCreateContact = ability.canCreate('contacts');
  const company = state.status === 'ready' ? state.company : null;

  // The related list's `meta.total` is fresher than `contactCount` — it
  // re-reads after every add — so the badge and delete copy stay correct
  // without a second fetch of the record.
  const contactCount = contacts?.meta.total ?? company?.contactCount ?? 0;

  /*
   * The `/contact-groups` master — read ONCE per mount, not per company: it is
   * a global master, and this page only ever shows one account.
   *
   * ⚠️ **It decides the ORDER of the group headings and nothing else.** Which
   * sections exist comes from the contacts' own `contactGroup`, so a failed
   * read costs a heading its place in the running order and costs nobody their
   * contacts — which is why there is no `failed` state here and no error block
   * for it. `null` is "failed, or not answered yet" and `groupContacts` treats
   * both the same, deliberately.
   *
   * ⚠️ **`isActive` is NOT sent.** A picker would send it; this is an ordering
   * table, and reads never filter on `isActive` — a contact can legitimately
   * hold a since-retired group, and narrowing the master would drop that
   * group's rank and drift its heading to the bottom for no reason the reader
   * could see.
   *
   * Not gated on any ability: reads are open to every authenticated role, and
   * the grouping is what a read-only visitor came for.
   */
  const [contactGroups, setContactGroups] = useState<ContactGroup[] | null>(null);
  const [contactGroupsSettled, setContactGroupsSettled] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listContactGroups({ perPage: CONTACT_GROUPS_PER_PAGE }, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setContactGroups(response.data);
        setContactGroupsSettled(true);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setContactGroupsSettled(true);
      });
    return () => controller.abort();
  }, []);

  // --- the pipeline stage, edited in place -------------------------------

  // Only fetched when the user can actually edit: a read-only page renders the
  // stage as text and has no picker to fill, and the rate limit is shared
  // across the office.
  const {
    records: stageRecords,
    loading: stagesLoading,
    failed: stagesFailed,
    reload: retryStages,
  } = useCompanyStages(canUpdate);

  /*
   * The staff directory, for the three people-pickers. Same gating as the stage
   * master above.
   *
   * ⚠️ `failed` and `hasMore` are no longer destructured — both existed solely
   * to word sentences removed under the owner's stricter helper-text ruling
   * (2026-09-05): a load-failure notice and a truncation notice. The hook still
   * tracks them; this page no longer reports them.
   */
  const { loading: usersLoading, byGroup } = useUserOptions(canUpdate, true);
  // ⚠️ Both pickers on this page are GATED by a user group. Naming somebody
  // outside `account_lead` as `accountLeadId` is a 422 on `/companies`,
  // create and update alike — the narrowing states a server rule, not a house
  // convention. No Owner picker here, so the un-narrowed `users` list is
  // never read — `ownerId` is the one staff field the server does NOT check.
  const staffByKey: Record<StaffKey, StaffNarrowing> = {
    accountLeadId: byGroup[USER_GROUP_SLUGS.accountLead],
    clientSuccessManagerId: byGroup[USER_GROUP_SLUGS.clientSuccessManager],
  };

  /*
   * The faculty master and the regions master, gated on `canUpdate` for the
   * same reason as the two above: a read-only page renders both as text.
   *
   * Regions come through the shared `useReferenceOptions` hook rather than a
   * second fetch of the same endpoint — it already pins `isActive: true` and
   * `perPage: 200`, which are the two things a picker of a reference master has
   * to get right.
   */
  const {
    records: facultyRecords,
    loading: facultiesLoading,
    failed: facultiesFailed,
  } = useFaculties(canUpdate);
  /*
   * `canUpdate` for the reason the faculty read uses it: a reader who cannot
   * write renders the source as text and has nothing to fill a picker with, so
   * the request is one nothing on the page can use.
   *
   * ⚠️ *SUPERSEDED: "and the rate limit is 120/60s shared across the whole
   * office."* It is **12,000/60s** (corrected 2026-09-17) — the same correction
   * `useFaculties` carries in `company-master-options.ts`. Skipping the request
   * is still right; the arithmetic that reason cited was off by a factor of a
   * hundred.
   *
   * It also keeps `sourcesFailed` honest — the hook never fetches when
   * disabled, so the flag can only ever be true for somebody the control is
   * actually offered to.
   */
  const {
    records: sourceRecords,
    loading: sourcesLoading,
    failed: sourcesFailed,
  } = useCompanySources(canUpdate);
  /* `canUpdate` for the same two reasons as the master above it. */
  const {
    records: companyTypeRecords,
    loading: companyTypesLoading,
    failed: companyTypesFailed,
  } = useCompanyTypes(canUpdate);
  /*
   * Fetched for EVERY reader, unlike the masters above (owner 2026-10-25): the
   * header labels the status by the company's stage ("Prospect status") and
   * hides it for a stage with no active statuses, which only the master can
   * tell. One shared, cached GET.
   */
  const stageStatusMaster = useCompanyStageStatuses(true);
  const stageStatusRecords = stageStatusMaster.statuses;
  const stageStatusesLoading = stageStatusMaster.loading;
  const stageStatusesFailed = stageStatusMaster.error !== null;
  /*
   * Asks for a follow-up date or a reason before a stage status is saved, when
   * the chosen status's flags call for one. Its `dialog` is mounted ONCE, with
   * the page's other modals.
   */
  const stageStatusChange = useStageStatusChange();
  /*
   * Account lead history (owner 2026-10-25). A lead change from the in-place
   * picker waits here for its "Effective from" date; the dialog is mounted ONCE
   * with the page's other modals. `leadHistoryNonce` re-reads the history
   * section after a lead change lands.
   */
  const [pendingLeadChange, setPendingLeadChange] = useState<PendingLeadChange | null>(null);
  const [leadChangeBusy, setLeadChangeBusy] = useState(false);
  const [leadChangeError, setLeadChangeError] = useState<string | null>(null);
  const [leadHistoryNonce, setLeadHistoryNonce] = useState(0);
  const [leadHistoryCollapsed, setLeadHistoryCollapsed] = useState(false);
  /* `canUpdate` for the same two reasons as the masters above it. */
  const {
    rows: countryRows,
    loading: countriesLoading,
    failed: countriesFailed,
  } = useCountryRows(canUpdate);
  const {
    records: regionRecords,
    loading: regionsLoading,
    failed: regionsFailed,
  } = useReferenceOptions('regions', canUpdate);

  /*
   * The projects a company is being pursued for. Gated on `canUpdate` like every
   * other picker master here — a read-only page renders the set as text and has
   * nothing to offer.
   *
   * ⚠️ `useProjectOptions` pins `isActive: true`, so its rows are the ACTIVE
   * projects and a project already on this company may legitimately be absent
   * from them. That absence is not a refusal waiting to happen: unlike a region,
   * assigning a project is checked for EXISTENCE and never for `isActive`, so a
   * retired one saves. `projectOfInterestOptions` injects it back so it survives
   * the next whole-set write; there is deliberately NO retired-member warning
   * here, because the regions one states a rule that does not apply to projects.
   *
   * ✅ **`hasMore` IS destructured and IS rendered — 2026-09-13.**
   *
   * ⚠️ *SUPERSEDED: "`hasMore` is not destructured, matching the cycles and
   * staff pickers on this page (see their comments): the truncation sentences
   * went under the owner's stricter helper-text ruling of 2026-09-05. The
   * truncation itself is unchanged, and `/projects` caps at 100 — so this is the
   * one place on this field where a person could fail to find a project and not
   * be told why."*
   *
   * **That last clause was the defect, stated in the comment and left standing.**
   * `/projects` caps `perPage` at the server's own ceiling of 100 against 113
   * ACTIVE projects, so the picker is short by roughly a dozen on essentially
   * every open — and it is the same tail of the alphabet every time, which reads
   * as a naming convention rather than as a cap. `/companies` and
   * `CompanyProjectsModal` both report it; this field was the one surface over
   * this column that did not.
   *
   * ⚠️ **It is a FACT ABOUT THE DATA, which is the footnote kind the
   * no-helper-text rule keeps** — the same standing as a `Combobox`'s own
   * truncation notice. It is not a carve-out from that rule and licenses no
   * other prose on this field. The wording is `CompaniesPage`'s and
   * `CompanyProjectsModal`'s verbatim, so three surfaces over one column cannot
   * describe the same data differently.
   *
   * ⛔ **The fix is NEVER a bigger `perPage`.** 100 is the server's ceiling and
   * there is no larger value to ask for; the routes out are this footnote, or
   * paging the hook the way the permission catalogue is paged.
   *
   * ⚠️ The cycles and staff pickers on this page still do not report theirs (see
   * their comments) — that is untouched here, and this field is no longer an
   * example of it.
   */
  const {
    projects: projectRecords,
    loading: projectsLoading,
    failed: projectsFailed,
    hasMore: moreProjects,
  } = useProjectOptions(canUpdate);

  /*
   * Which cycles count as CURRENT, for the picker above. Gated on `canUpdate`
   * like every other picker master on this page — a read-only page renders the
   * set as text and has nothing to offer — and disabled the hook returns
   * `UNKNOWN_CYCLE_SCOPES`, which offers everything rather than nothing.
   *
   * ⚠️ **`Project.cycle` is a `MasterRef` and carries NO dates**, so the rows
   * `useProjectOptions` already holds cannot classify themselves; the master
   * read is not avoidable.
   *
   * ⚠️ **This narrows what the picker OFFERS and never what it DISPLAYS or
   * SAVES.** `projectOfInterestOptions` still resolves this company's stored
   * projects against the FULL list and pins any that fall outside the scope,
   * labelled `Previous` — which is what keeps a closed-cycle project of
   * interest distinguishable from a retired one, and what stops the next
   * whole-set write dropping it.
   */
  const projectCycleScopes = useCycleScopeIds(canUpdate);

  /**
   * Which half of the cycle calendar that picker is OFFERING.
   *
   * ⚠️ **Page state here, where `CompaniesPage` needs a component per row** —
   * that page renders one of these pickers per company, so a single scope there
   * would be shared by twenty of them. This page has exactly one picker
   * (`projectsOfInterestField`, one call site), so the page IS the picker's
   * scope and a component would buy nothing.
   *
   * ⛔ **Switching the pill must not clear the selection, and structurally
   * cannot**: this value reaches `projectOfInterestOptions` and nothing else,
   * while `values` comes from the record and the draft lives inside
   * `InlineRegionsField`. A stored project stays ticked under both pills,
   * because that function pins every stored project into EVERY scope.
   *
   * ⚠️ **Per-picker UI state, and nothing more — not a URL param, not a saved
   * filter, and not a filter on anything.** It narrows one dropdown. In the
   * query string it would make a shared link render a different picker for two
   * people, and would read as though the record had been filtered.
   */
  // Remembered on the account (owner, 2026-10-16) — still not in the URL.
  const [projectCycleScope, setProjectCycleScope] = useRememberedState<ProjectCycleScope>(
    'company-projects-cycle-scope',
    DEFAULT_PROJECT_CYCLE_SCOPE,
    (raw) => PROJECT_CYCLE_SCOPES.find((value) => value === raw),
  );

  /*
   * The primary-contact picker's option list — the SAME fetch as the grouped
   * list below the record, read four different ways.
   *
   * ⚠️ It used to be a second request of its own. Merging them removed the one
   * thing neither could rule out: two reads of the same endpoint, resolving at
   * different moments against a list somebody else is editing, describing this
   * account's contacts differently on one screen.
   *
   * ⚠️ `primaryContacts: null` is "not answered, or FAILED", which is NOT the
   * same fact as an account with no contacts (`[]`). The two stay apart because
   * the field disables itself on the failure: with no list, the only outcome a
   * picker can reach is clearing a name the reader can see.
   *
   * `contactsGone` (a 404 — the account was deleted under the page) counts as
   * a failure here for the same reason: there is nothing to pick from, and the
   * panel below already says what happened in its own words.
   */
  const primaryContacts = contacts?.data ?? null;
  const primaryContactsFailed = contactsError !== null || contactsGone;
  const primaryContactsMore = contactsTruncated;
  const primaryContactsLoading = companyReady && !contactsFresh;

  /*
   * The two halves of the currency picker, both gated on `canUpdate` for the
   * same reason every other list here is: a read-only page renders the currency
   * from the record — `code` is on it — and has no picker to fill.
   *
   * They are separate reads because they are separate facts. `xeroCurrencies`
   * is the only LEGAL set (what this Xero organisation enables, 25 rows), and
   * the master is only how one of those codes becomes the `currencyId` we
   * write. Neither substitutes for the other: the master alone would offer 139
   * currencies that are refused on save, and the enabled list alone carries no
   * id to send.
   *
   * The enabled list additionally needs the tenant reachable, so it waits for
   * the connection read exactly as the branding themes do — a request that can
   * only fail would put a failure message on a field whose real state is "Xero
   * is not connected", which is a different sentence.
   */
  const {
    currencies: currencyMaster,
    loading: currencyMasterLoading,
    failed: currencyMasterFailed,
  } = useCurrencyOptions(canUpdate);
  const xeroCurrencies = useXeroEnabledCurrencies(
    canUpdate && !xeroLoading && xeroConnection.connected && !xeroNotInstalled,
  );

  /*
   * Which in-place write is the newest. Two fields can be in flight at once —
   * blur one, click straight into another — and the responses can cross. Only
   * the newest answer is allowed to replace the record, so a slower earlier
   * PATCH cannot put its (older) copy of the company back on screen.
   */
  const recordWriteSeq = useRef(0);

  /*
   * The choice the user just made, held only while it is in flight or after it
   * failed. Null means "show what the record says". Keeping a failed choice on
   * screen — next to a message that names the stage the account still has — is
   * more use than silently snapping the control back and leaving them to
   * wonder whether it went through.
   */
  const [stageDraft, setStageDraft] = useState<string | null>(null);
  const [stageStatus, setStageStatus] = useState<StageStatus>({ kind: 'idle' });
  /*
   * The one make-primary press that is in flight, or the last one that failed.
   *
   * ⚠️ **There is no success branch and that is the design.** A successful
   * write replaces the RECORD, so the badge moves in the list and the picker in
   * Account details follows in the same paint — both read
   * `company.primaryContact` and neither keeps a copy. A "Saved" message here
   * would be a second, weaker statement of something the reader can already
   * see, and it would go stale the moment somebody else changed it.
   */
  const [primaryAction, setPrimaryAction] = useState<PrimaryActionState>(null);
  /*
   * The group write in flight on each row, keyed by contact id.
   *
   * ⚠️ **A MAP rather than the single slot `primaryAction` uses, and the
   * difference is a rule about the data, not a preference.** One company column
   * holds the primary, so two designations in flight is one of them losing;
   * groups sit on separate contact rows, so two saves cannot collide and
   * neither may disable or speak for the other. A shared slot would put one
   * row's failure message beside a different row's picker.
   *
   * ⚠️ **No success entry — a saved row is REMOVED from this map.** The
   * acknowledgement is the row moving into its new band with the counts
   * corrected, which the list draws from the contact record itself.
   */
  const [contactGroupActions, setContactGroupActions] = useState<ContactGroupActions>({});
  /*
   * The "Same as billing" copy, and the confirmation in front of it.
   *
   * ⚠️ **The confirmation's OPEN state is here and its wording is derived at
   * render**, from the record, rather than captured when the button was
   * pressed. A dialog naming figures somebody else has since changed is the
   * stale-acknowledgement hole this codebase already records; deriving it means
   * the sentence and the patch are read from the same values in the same paint.
   */
  const [addressCopy, setAddressCopy] = useState<AddressCopyStatus>({ kind: 'idle' });
  const [addressCopyConfirmOpen, setAddressCopyConfirmOpen] = useState(false);
  /*
   * Two quick changes can resolve out of order, and the loser must not win the
   * record. Only the newest request is allowed to write state.
   */
  const stageSeq = useRef(0);

  /*
   * A re-read of the record — a Xero action, a retry, a different company —
   * makes any pending draft and any older message meaningless.
   *
   * Adjusted during render rather than in an effect: React's documented
   * reset-state-when-something-changes pattern re-renders before paint, so the
   * previous account's stage message is never shown against the new one. An
   * effect would paint it first, and `react(set-state-in-effect)` says so.
   */
  const [stageOwnerKey, setStageOwnerKey] = useState(recordKey);
  if (stageOwnerKey !== recordKey) {
    setStageOwnerKey(recordKey);
    setStageDraft(null);
    setStageStatus({ kind: 'idle' });
    /*
     * The make-primary refusal goes too. It names a contact and an outcome
     * against ONE version of the record; carried onto the next read it would
     * sit under a row whose designation may already have moved — and a stale
     * refusal reads exactly like a live one.
     */
    setPrimaryAction(null);
    /*
     * And the address copy, both halves. Its message describes ONE version of
     * the addresses; against a re-read record it may already be untrue, and a
     * stale "Saved." over five values that have since moved is a claim nobody
     * can tell from a live one. The dialog closes for the same reason — the
     * fields it names are the ones it was opened over.
     */
    setAddressCopy({ kind: 'idle' });
    setAddressCopyConfirmOpen(false);
  }

  /*
   * The group refusals are keyed to the CONTACTS read, not the record one — a
   * "Try again" on the contacts panel re-fetches every row, and a refusal
   * describing the list that was on screen before it would sit under a row
   * whose group may already have moved. Same reset-during-render pattern as the
   * stage block above, and for the same reason: an effect would paint the stale
   * message first.
   */
  const [groupOwnerKey, setGroupOwnerKey] = useState(contactsKey);
  if (groupOwnerKey !== contactsKey) {
    setGroupOwnerKey(contactsKey);
    setContactGroupActions({});
  }

  useEffect(() => {
    if (stageStatus.kind !== 'saved') return;
    const timer = setTimeout(() => setStageStatus({ kind: 'idle' }), SAVED_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [stageStatus]);

  /*
   * The acknowledgement fades; a failure does not. Same asymmetry the inline
   * fields use — a message somebody has to act on must not disappear while they
   * are reading it, and once "Saved." goes the control falls back to saying why
   * it is now disabled, which is the more useful line by then.
   */
  useEffect(() => {
    if (addressCopy.kind !== 'saved') return;
    const timer = setTimeout(() => setAddressCopy({ kind: 'idle' }), SAVED_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [addressCopy]);

  async function saveStage(nextValue: string) {
    if (!company) return;
    const currentId = company.stage?.id ?? null;
    const nextId = nextValue === '' ? null : Number(nextValue);

    // PATCH sends only what changed, and an empty patch is a 422 — so choosing
    // the value the record already holds is a state reset, not a request.
    if (nextId === currentId) {
      setStageDraft(null);
      setStageStatus({ kind: 'idle' });
      return;
    }

    const seq = stageSeq.current + 1;
    stageSeq.current = seq;
    setStageDraft(nextValue);
    setStageStatus({ kind: 'saving' });

    try {
      // Built from one explicit key. `PATCH /companies` is NOT `.strict()` —
      // it strips unknown keys and answers 200 — so a payload assembled by
      // spreading anything could quietly change nothing at all.
      const response = await api.updateCompany(company.id, { stageId: nextId });
      if (stageSeq.current !== seq) return;

      const saved = response.data;
      // The server's record is the truth either way, so it lands regardless of
      // whether it says what we asked for.
      setRecordResult({ key: recordKey, value: { status: 'ready', company: saved } });
      setStageDraft(null);

      // ⚠️ A 200 does not prove the change applied — unknown keys are silently
      // stripped, so confirm against what came back rather than the status code.
      const appliedId = saved.stage?.id ?? null;
      if (appliedId !== nextId) {
        setStageStatus({
          kind: 'stripped',
          message: `The server accepted the request but this account is still ${
            saved.stage ? saved.stage.name : UNCLASSIFIED_LABEL
          }. Nothing was changed.`,
        });
        return;
      }
      setStageStatus({ kind: 'saved', name: saved.stage ? saved.stage.name : UNCLASSIFIED_LABEL });
    } catch (caught) {
      if (stageSeq.current !== seq) return;

      const stillIs = company.stage ? company.stage.name : UNCLASSIFIED_LABEL;
      if (caught instanceof ApiError && caught.status === 403) {
        // No `details` on a 403, so this is a banner and never a field error.
        setStageStatus({
          kind: 'banner',
          message:
            denialReason(ability, 'update', 'companies') ??
            'You no longer have permission to change this.',
        });
        return;
      }
      if (caught instanceof ApiError && caught.status === 404) {
        setStageStatus({
          kind: 'banner',
          message: 'This account no longer exists, so its stage could not be changed.',
        });
        return;
      }
      // A bad `stageId` is a 422 that DOES name the path, unlike most of this
      // module's errors — so it can mark the control it belongs to.
      const fieldMessage = caught instanceof ApiError ? caught.fieldErrors.stageId : undefined;
      if (fieldMessage) {
        setStageStatus({
          kind: 'field',
          message: `${fieldMessage} This account is still ${stillIs}.`,
        });
        return;
      }
      // `formMessage` before `message`: on a 422 the latter is only
      // "Validation failed", while a `.strict()` rejection or the empty-PATCH
      // refusal carries the useful sentence on `formMessage` alone. Same
      // order as `saveCompanyField` below.
      const refusal =
        (caught instanceof ApiError ? caught.formMessage : null) ??
        (caught instanceof Error ? caught.message : null) ??
        'The change could not be saved.';
      setStageStatus({
        kind: 'banner',
        message: `${refusal} This account is still ${stillIs}.`,
      });
    }
  }

  /**
   * The one figure the delete confirmation names, read when it opens.
   *
   * ⚠️ NOTES ONLY, AND THAT IS THE WHOLE POINT OF THIS FUNCTION EXISTING. Until
   * backend migration 111 (2026-08-30) a company holding notes could not be
   * deleted at all, so this page deliberately pre-fetched nothing — a refused
   * delete destroys nothing, so there was nothing to warn about. The owner
   * reversed that rule and the notes now CASCADE, which makes this dialog the
   * only place anybody is told before they are gone.
   *
   * TASKS ARE NOT READ HERE. `fk_tasks_company` is still `ON DELETE RESTRICT`,
   * so tasks still REFUSE the delete rather than being destroyed by it; the
   * server's own `409` names them, with a count computed at the moment of the
   * attempt. Naming them in a "this will be deleted" sentence would promise to
   * destroy the one thing that in fact blocks the operation.
   *
   * 🔴 THIS FIGURE IS A FLOOR, NOT THE ACCOUNT'S TOTAL. `/notes/summary` runs
   * through the same visibility clause as every notes read, so without the audit
   * grant at scope `all` it counts the VIEWER'S OWN notes only — while the
   * cascade destroys everybody's. `companyNotesConsequence` is what words that
   * honestly; do not "improve" this into a total, and do not read a zero here as
   * "there are no notes".
   */
  function openDeleteCounts() {
    if (!company) return;
    // Cleared first: a figure left over from a previous open understates what is
    // about to be destroyed, which is the direction that costs somebody history.
    setNoteTotal(null);
    setSystemNoteTotal(0);
    const seq = noteTotalSeq.current + 1;
    noteTotalSeq.current = seq;
    void api
      .noteSummary({ target: 'company', ids: String(company.id) })
      .then((notes) => {
        if (noteTotalSeq.current !== seq) return;
        const row = notes.data[0];
        setNoteTotal(row?.count ?? 0);
        // ⚠️ `typeof`, NOT `?? 0`. The key is typed required and can still be
        // absent from a server predating it; both that and a real `0` mean "say
        // nothing about automatic notes", and only the absent one is a guess.
        setSystemNoteTotal(typeof row?.systemCount === 'number' ? row.systemCount : 0);
      })
      .catch(() => {
        if (noteTotalSeq.current !== seq) return;
        setNoteTotal(null);
        setSystemNoteTotal(0);
      });
  }

  async function handleDelete() {
    if (!company) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteCompany(company.id);
      navigate('/companies', { replace: true });
    } catch (caught) {
      // Already deleted elsewhere: the user's intent is satisfied either way.
      if (caught instanceof ApiError && caught.status === 404) {
        navigate('/companies', { replace: true });
        return;
      }
      // 409: the account still carries TASKS or deliverables. Surfaced verbatim
      // because the server's sentence names which of the two is in the way and
      // how many, computed at the moment of the attempt.
      //
      // ⚠️ NOTES ARE NO LONGER A REASON FOR THIS BRANCH (backend migration 111,
      // 2026-08-30) — they cascade, and the confirmation above says so before
      // the delete runs. The `code` is still `COMPANY_HAS_TASKS_OR_NOTES` even
      // though it can now only mean tasks: renaming a code a deployed bundle
      // branches on is a breaking change with no grace period. Nothing here
      // reads the code, so the wide name costs this page nothing.
      if (caught instanceof ApiError && caught.status === 409) {
        setDeleteError(caught.message);
        setDeleting(false);
        return;
      }
      // A 403 carries no `details`, so it stays a banner. `denialReason` is null
      // when the ability still believed the delete was allowed — a role changed
      // under the live token.
      setDeleteError(
        caught instanceof ApiError && caught.status === 403
          ? (denialReason(ability, 'delete', 'companies') ??
            'Your account no longer has permission to delete accounts.')
          : caught instanceof Error
            ? caught.message
            : 'Delete failed',
      );
      setDeleting(false);
    }
  }

  /*
   * The read gate, below every hook so the early return cannot change the hook
   * order — and ABOVE the not-found and error branches, deliberately. A reader
   * who may not see companies must not be told whether this one exists:
   * "Account not found" over a record that is merely hidden from them is a wrong
   * answer dressed as a real one.
   *
   * ⚠️ **A detail page needs the same gate as its list.** `/companies/:id` open
   * while `/companies` refuses is the same hole one level down, and a detail
   * page is exactly what a stale bookmark or a link in an email points at.
   *
   * ⚠️ DEGRADES OPEN: only an explicit `none` scope denies. And **hiding a page
   * is not refusing a request** — `GET /companies/:id` answers the same to
   * everybody, so nothing here may be worded as though the record were
   * protected. This is a view control.
   *
   * No back link: `/companies` answers this reader with the same refusal.
   */
  if (!canReadModule) {
    return (
      <>
        <PageHeader eyebrow="Records" title="Companies" />
        <Panel className="min-w-0">
          <EmptyState
            title="This page isn’t available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </>
    );
  }

  if (state.status === 'missing') {
    return (
      <>
        <PageHeader
          eyebrow={<BackToCompanies />}
          title="Account not found"
          description="Nothing here matches that link."
        />
        <Panel>
          <EmptyState
            icon={<BuildingIcon />}
            title="This account doesn’t exist"
            description="It may have been deleted, or the address mistyped."
            action={
              <Button variant="primary" onClick={() => navigate('/companies')}>
                Back to companies
              </Button>
            }
          />
        </Panel>
      </>
    );
  }

  if (state.status === 'error') {
    return (
      <>
        <PageHeader
          eyebrow={<BackToCompanies />}
          title="Account"
          description="This record couldn’t be loaded."
        />
        <Panel>
          <Notice tone="danger" role="alert" title="Couldn’t load this account." className="m-4">
            <p className="m-0 min-w-0 [overflow-wrap:anywhere]">{state.message}</p>
          </Notice>
          <EmptyState
            icon={<BuildingIcon />}
            title="Nothing to show"
            description="The request didn’t complete. Try again."
            action={
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button variant="primary" onClick={() => setRecordNonce((n) => n + 1)}>
                  Try again
                </Button>
                <Button onClick={() => navigate('/companies')}>Back to companies</Button>
              </div>
            }
          />
        </Panel>
      </>
    );
  }

  if (!company) {
    return (
      <>
        <PageHeader eyebrow={<BackToCompanies />} title={<Skeleton className="h-7 w-64" />} />
        <div className="space-y-4">
          <DetailsSkeleton />
          <Panel>
            {/* `description="Loading…"` went with the stricter helper-text
                ruling (2026-09-05); the skeleton below is what says the panel is
                loading, and it says it in the real shape. */}
            <PanelHeader title="Contacts" />
            <ContactsSkeleton
              withActions={canUpdate}
              withRowActions={ability.canUpdate('contacts') || ability.canDelete('contacts')}
            />
          </Panel>
        </div>
      </>
    );
  }

  const contactRows = contacts?.data ?? [];
  /*
   * ⚠️ The GROUPS master is part of the first load, not a second wave.
   *
   * Both requests go out in parallel, so waiting for the master costs nothing
   * in latency — but rendering before it lands would draw the headings in
   * fallback (id) order and then re-order them under the reader's eye a moment
   * later. A one-paint reshuffle of a list somebody is reading is worse than a
   * slightly longer skeleton, and it looks like a bug rather than like loading.
   */
  const contactsInitialLoad =
    (contactsLoading || !contactGroupsSettled) && !contacts && !contactsGone && !contactsError;
  /**
   * Why the row action is missing for this reader, or `null` when it is there.
   *
   * ⚠️ Said ONCE, here, rather than as a `disabledReason` on every row: the
   * value the reader came for — which contact is primary — is never hidden, and
   * a hundred disabled buttons each repeating the same sentence is noise, not
   * an explanation. The Overview's primary-contact picker carries the same
   * reason on the one control that can also set it.
   */
  const primaryDenial = canUpdate ? null : denialReason(ability, 'update', 'companies');
  /*
   * ⚠️ A SEPARATE question from `canUpdate`, and it has to be asked separately:
   * the group is a column on the CONTACT, so it is `contacts` that decides —
   * and `basic_user` may hold one of the two without the other.
   *
   * Unlike `primaryDenial`, this one is carried down to every row rather than
   * stated once on the panel, because the Group cell is never removed: the
   * value stays readable for everybody, and the reason rides on the cell's
   * `title` rather than taking a line under each of a hundred rows.
   */
  const canUpdateContacts = ability.canUpdate('contacts');
  const contactsDenial = canUpdateContacts ? null : denialReason(ability, 'update', 'contacts');
  const canDeleteContacts = ability.canDelete('contacts');
  /*
   * `countLabel` is GONE — owner's stricter helper-text ruling, 2026-09-05. It
   * read "{n} contacts" under the Contacts panel heading, as prose.
   *
   * ⚠️ `contactCount` itself STAYS and is read in two places: the delete
   * confirmation, where it names how many contacts survive the company — a
   * destructive consequence, which is an explicit keeper — and, since
   * 2026-10-11, the Contacts SECTION's one-line summary beside its title, which
   * is the line of facts a folded section carries so a reader need not open it
   * to learn it is empty (see `CollapsibleSection`'s `summary`). A count beside
   * a heading in a shell built to carry one is not the line under a heading
   * that went.
   */

  // `company` is the target TYPE (singular); the drawer derives the list
  // FILTER key from it, so only the singular goes in.
  const activityTarget = { type: 'company', id: company.id } as const;
  // An empty name falls back to the id, since the sheet's subheading is the
  // only thing saying which account it is about.
  const activityLabel = company.name.trim() || `Company #${company.id}`;

  /*
   * The header's ONE compact facts line (owner's decision, 2026-10-11): stage ·
   * approval · account lead · CSM · country, as plain middle-dot-separated text.
   * Read-only — every one of these is EDITED in the Overview section, so there
   * is still exactly one control per fact and one place that can disagree with
   * the record.
   *
   * ⚠️ *SUPERSEDED: the old `locality` line — `[billingCity, country of record]`
   * — and its `company.layout` fallback.* The owner named the five facts; the
   * billing city and the layout are still on the record and still edited in
   * Overview, they just no longer lead the page.
   *
   * ⚠️ The approval WORDS mirror `ApprovalStatusBadge`'s three labels, as plain
   * text — the owner asked for no badges on this line beyond the stage. If that
   * badge's wording moves, this moves with it.
   *
   * An absent person or country is simply LEFT OUT, never rendered as a dash:
   * a dash in a prose line reads as a typo, and the Overview below says
   * "Unassigned" / "Not recorded" in words where the absence is the fact.
   */
  const approvalFact: Record<Company['approvalStatus'], string> = {
    approved: 'Approved',
    rejected: 'Rejected',
    pending: 'Awaiting approval',
  };
  const headerFacts: ReactNode[] = [
    <StageBadge key="stage" stage={company.stage} />,
    <span key="approval">{approvalFact[company.approvalStatus]}</span>,
    ...(company.accountLead
      ? [
          <span key="lead">
            Lead{' '}
            {company.accountLead.name.trim() ||
              (company.accountLead.email !== '' && (
                <EmailLink address={company.accountLead.email} tone="muted" />
              ))}
          </span>,
        ]
      : []),
    ...(company.clientSuccessManager
      ? [
          <span key="csm">
            CSM{' '}
            {company.clientSuccessManager.name.trim() ||
              (company.clientSuccessManager.email !== '' && (
                <EmailLink address={company.clientSuccessManager.email} tone="muted" />
              ))}
          </span>,
        ]
      : []),
    ...(countryOfRecordLabel(company.country)
      ? [<span key="country">{countryOfRecordLabel(company.country)}</span>]
      : []),
  ];

  /*
   * "Same as billing", derived from the RECORD — this page holds no draft, so
   * there is nothing else it could be derived from.
   *
   * ⚠️ **`?? ''` is load-bearing, not defensive.** The wire uses `null` for a
   * blank column and this page's own editors write `null` when somebody clears
   * a box, so a pair can legitimately be `null` on one side and `''` on the
   * other while meaning the same thing. Compared raw, those two would read as a
   * difference, the copy would build a patch that changes nothing, and the
   * server would answer 422 to a button that looked like it had work to do.
   */
  /*
   * ⚠️ **`COMPANY_ADDRESS_PAIRS`, so the country is NOT copied** (2026-08-31).
   * A company has no free-text billing country to copy FROM, and
   * `shippingCountry` is deliberately left exactly as somebody typed it rather
   * than being blanked by a button labelled "same as billing" — this control
   * already asks for confirmation before it destroys a shipping value, and
   * silently clearing one it cannot source is the opposite of that promise.
   */
  const addressPairValues = COMPANY_ADDRESS_PAIRS.map(([from, to]) => ({
    to,
    billing: company[from] ?? '',
    shipping: company[to] ?? '',
  }));
  const billingIsBlank = addressPairValues.every(({ billing }) => billing.trim() === '');
  /** Exactly what the patch will carry — an empty list means there is nothing to send. */
  const addressCopyChanges = addressPairValues.filter(
    ({ billing, shipping }) => billing !== shipping,
  );
  /*
   * The subset a copy would DESTROY. A shipping field that is blank loses
   * nothing, so it is not worth a confirmation — and a dialog that appears over
   * nothing is how people learn to dismiss the one that matters.
   */
  const addressCopyOverwrites = addressCopyChanges.filter(
    ({ shipping }) => shipping.trim() !== '',
  );

  /*
   * Why the button is held down, or `null` when it is live. All three are
   * states the reader cannot otherwise see, which is the exception the
   * no-helper-text rule names: a bare `disabled` makes "already identical",
   * "nothing to copy" and "you may not edit this account" look the same.
   *
   * Order matters. The permission answer is about the READER and is true
   * whatever the addresses hold, so it comes first; a blank billing address
   * beats "already matches" because when both are empty both sentences are
   * true and only one of them tells somebody what to do next.
   */
  const addressCopyRefusal = !canUpdate
    ? (denialReason(ability, 'update', 'companies') ??
      'You don’t have permission to change this account.')
    : billingIsBlank
      ? 'There’s nothing in the billing address to copy.'
      : addressCopyChanges.length === 0
        ? 'The shipping address already matches billing.'
        : null;

  const stageChoices = companyStageOptions(stageRecords, company.stage);
  /*
   * The stage status is labelled by the company's CURRENT stage (owner
   * 2026-10-25), matching the per-stage list columns: "Prospect status". Shown
   * when the API reports the field, the company has a stage, and either a
   * status is held (even a retired one) or the stage has an ACTIVE status to
   * pick — by stage ID, never by name. While the master is loading it waits
   * rather than flash in and out; when it failed, the field shows with its
   * refusal so a held value is never hidden behind an outage.
   */
  const stageStatusLabel = company.stage ? `${company.stage.name} status` : 'Stage status';
  const showStageStatus =
    company.stageStatus !== undefined &&
    company.stage != null &&
    (company.stageStatus !== null ||
      stageStatusesFailed ||
      (!stageStatusesLoading &&
        stageStatusMaster.statusesForStage(company.stage.id).length > 0));
  // The record is the source of truth; the draft only overrides it while a save
  // is in flight or after one failed.
  const stageValue = stageDraft ?? (company.stage ? String(company.stage.id) : '');
  const stageMessage =
    stageStatus.kind === 'saving'
      ? 'Saving…'
      : stageStatus.kind === 'saved'
        ? `Saved — now ${stageStatus.name}.`
        : stageStatus.kind === 'idle'
          ? stagesLoading
            ? 'Loading the stage list…'
            : stagesFailed
              ? 'Stage list unavailable — can’t be changed here.'
              : 'Saves straight away.'
          : stageStatus.message;

  // --- the rest of the record, edited in place ---------------------------

  /**
   * Writes one column and reports what happened, in the vocabulary
   * `InlineField` renders. Nothing here throws — the field needs an outcome,
   * not an exception.
   *
   * `readBack` is passed only for ID-valued columns: a 200 does NOT prove the
   * change applied, but free text can also come back legitimately normalised, so
   * checking it there would cry wolf. An id comes back exactly as sent or not at
   * all.
   *
   * ⚠️ **The justification here USED to be "`PATCH /companies` is not
   * `.strict()`", and that is now false** — both company write schemas are
   * `.strict()` (`companies.schema.ts`, the `.strict()` landing on the
   * `ZodObject` above the `.refine()`). The CONTROL is still right and the
   * reason had to change: strictness answers *"the key was not recognised"*,
   * the read-back answers *"the key was accepted and not applied"* — a derived
   * column, a guard that ignored it, a write that landed elsewhere. Different
   * questions, so this does not become redundant.
   *
   * `describe` is the escape hatch for a column whose write can fail in a way
   * this function has no vocabulary for — only `currencyId`, validated by
   * calling Xero, where an unreachable tenant is a `502` with nothing stored.
   * Returning `null` leaves the generic handling untouched.
   *
   * ⚠️ It returns a whole `InlineSaveResult`, not a message, because one of its
   * branches must choose a different STATUS: a refusal nobody can satisfy from
   * this form has to close the editor rather than invite a retry. A `string`
   * return would have forced every branch to `error`. See
   * `describeCurrencyFailure`.
   */
  const saveCompanyField = async (
    /*
     * The request keys this patch carries, so a 422 can be routed to the
     * control that caused it. A string for the ordinary one-key write; a LIST
     * for "Same as billing", which is the only writer here sending more than
     * one column in a request.
     *
     * ⚠️ It is the keys SENT, not the keys the form owns. A 422 naming a key
     * this patch did not carry is not this control's to claim, and falls
     * through to the form-level handling below — the same partition
     * `routeFieldErrors` draws for the modals.
     */
    requestKey: string | readonly string[],
    patch: CompanyInput,
    readBack?: { sent: string; read: (saved: Company) => string; noun: string },
    describe?: (caught: ApiError) => InlineSaveResult | null,
  ): Promise<InlineSaveResult> => {
    const ticket = recordWriteSeq.current + 1;
    recordWriteSeq.current = ticket;

    try {
      const response = await api.updateCompany(company.id, patch);
      const saved = response.data;

      if (recordWriteSeq.current === ticket) {
        setRecordResult({ key: recordKey, value: { status: 'ready', company: saved } });
      }

      if (readBack && readBack.read(saved) !== readBack.sent) {
        return {
          status: 'warning',
          message: `${readBack.noun} not saved — the server didn’t apply the change.`,
        };
      }
      return { status: 'saved' };
    } catch (caught) {
      if (caught instanceof ApiError) {
        // A 403 carries no `details`, so it can never be a field error — and
        // can happen even though `canUpdate` said otherwise, since a role can
        // change under a live token.
        if (caught.status === 403) {
          return {
            status: 'error',
            message: `${
              denialReason(ability, 'update', 'companies') ??
              'You no longer have permission to change this account.'
            } Nothing was saved.`,
          };
        }
        if (caught.status === 404) {
          return {
            status: 'error',
            message: 'This account no longer exists, so the change could not be saved.',
          };
        }
        // Asked BEFORE the field errors — the ordering is the safety, not a
        // preference: an upstream failure must never arrive dressed as a
        // validation problem, and the two `currencyId` 422s below would
        // otherwise be flattened into one by their shared `path`. It cannot
        // swallow anything else: it recognises only named codes and the
        // upstream statuses, and returns `null` for everything it does not know.
        const described = describe?.(caught) ?? null;
        if (described) return described;
        // A 422 names the request key exactly, so it lands on the field that
        // caused it rather than on a banner about a field nobody can see.
        //
        // ⚠️ **EVERY refused key is reported, not the first.** A one-key write
        // can only ever produce one, so this is byte-identical there; a
        // five-key write that trips two rules would otherwise say one thing and
        // silently drop the other, which is this codebase's `??`-drop rule
        // arriving through a `find`.
        const keys = typeof requestKey === 'string' ? [requestKey] : requestKey;
        const fieldMessages = keys
          .map((key) => caught.fieldErrors[key])
          .filter((message): message is string => Boolean(message));
        // Joined with a space, not `joinList`: each one is a whole sentence the
        // server wrote, and stitching sentences together with "and" makes a
        // refusal read as though we paraphrased it.
        if (fieldMessages.length > 0) {
          return { status: 'error', message: fieldMessages.join(' ') };
        }
        // A `.strict()` rejection and the empty-PATCH refusal both carry an
        // EMPTY path, so they arrive here instead.
        const formMessage = caught.formMessage;
        if (formMessage) return { status: 'error', message: formMessage };
        return { status: 'error', message: caught.message };
      }
      return {
        status: 'error',
        message: 'Couldn’t reach the server. Check your connection.',
      };
    }
  };

  /**
   * An account lead change from the in-place picker (owner 2026-10-25): opens
   * the "Effective from" dialog and answers the picker once the dialog settles.
   * A newer request supersedes a pending one, whose picker reads "not changed".
   */
  const requestLeadChange = (
    next: string,
    fromLead: UserSummary | null,
    options: readonly InlineFieldOption[],
  ): Promise<InlineSaveResult> => {
    const toLead =
      next === ''
        ? null
        : { name: options.find((option) => option.value === next)?.label ?? `User #${next}` };
    pendingLeadChange?.resolve({ status: 'warning', message: 'Account lead not changed.' });
    setLeadChangeError(null);
    return new Promise<InlineSaveResult>((resolve) => {
      setPendingLeadChange({ companyId: company.id, next, fromLead, toLead, resolve });
    });
  };

  const closeLeadChange = () => {
    if (leadChangeBusy || pendingLeadChange === null) return;
    pendingLeadChange.resolve({ status: 'warning', message: 'Account lead not changed.' });
    setPendingLeadChange(null);
    setLeadChangeError(null);
  };

  /**
   * The PATCH, with `accountLeadEffectiveFrom` beside `accountLeadId`. A refusal
   * of the DATE stays in the dialog so it can be corrected there; every other
   * outcome closes the dialog and lands on the picker, through the same writer
   * (and read-back) as every other field on this page.
   */
  const confirmLeadChange = async (dateIso: string) => {
    const pending = pendingLeadChange;
    if (pending === null || leadChangeBusy || pending.companyId !== company.id) return;
    setLeadChangeBusy(true);
    setLeadChangeError(null);
    const dateRefusal: { message: string | null } = { message: null };
    const result = await saveCompanyField(
      ['accountLeadId', 'accountLeadEffectiveFrom'],
      { ...staffPatch('accountLeadId', pending.next), accountLeadEffectiveFrom: dateIso },
      {
        sent: pending.next,
        read: (saved) => readStaff(saved, 'accountLeadId'),
        noun: 'Account lead',
      },
      (caught) => {
        const fieldMessage = caught.fieldErrors.accountLeadEffectiveFrom;
        if (!caught.code.startsWith('ACCOUNT_LEAD_EFFECTIVE_FROM_') && !fieldMessage) return null;
        const message =
          accountLeadErrorMessage(caught.code) ?? fieldMessage ?? caught.formMessage ?? caught.message;
        dateRefusal.message = message;
        return { status: 'error', message };
      },
    );
    setLeadChangeBusy(false);
    if (dateRefusal.message !== null) {
      setLeadChangeError(dateRefusal.message);
      return;
    }
    setPendingLeadChange(null);
    pending.resolve(result);
    if (result.status !== 'error') setLeadHistoryNonce((n) => n + 1);
  };

  /**
   * Name a contact from the grouped list as this account's primary.
   *
   * ⚠️ **A SECOND AFFORDANCE, not a second writer.** It goes through
   * `saveCompanyField` — the identical `PATCH /companies/:id` the picker in
   * Account details makes, with the identical read-back — so the two cannot
   * word a refusal differently, cannot disagree about what was saved, and
   * cannot race each other: `recordWriteSeq` inside that function already
   * settles crossing writes, whichever control started them.
   *
   * **Why both views agree afterwards, structurally rather than by
   * convention**: the only thing this writes on success is the RECORD, and both
   * the badge and the picker render from `company.primaryContact`. Nothing here
   * holds a copy of who the primary is, so there is nothing that can drift.
   *
   * ⚠️ **No `isPrimary` on the contact.** "Exactly one" holds because a
   * single-valued column on the company cannot express two — not because
   * anything enforces it — and a second writer would break that quietly.
   */
  const makePrimary = async (contact: Contact) => {
    // One at a time. Two crossing designations would leave whichever answered
    // last on screen, and the loser's press would look as though it did
    // nothing.
    if (primaryAction?.kind === 'saving') return;
    // Re-sending the value the record already holds is an empty PATCH, which is
    // a 422 — the list already hides the button on that row, and this is the
    // guard behind it.
    if (company.primaryContact?.id === contact.id) return;

    setPrimaryAction({ contactId: contact.id, kind: 'saving' });

    const result = await saveCompanyField(
      'primaryContactId',
      { primaryContactId: contact.id },
      {
        sent: String(contact.id),
        read: (saved) => (saved.primaryContact ? String(saved.primaryContact.id) : ''),
        noun: 'primary contact',
      },
    );

    if (result.status === 'saved') {
      setPrimaryAction(null);
      return;
    }
    /*
     * A `warning` (the server accepted the call and did not apply it) is kept
     * on the row exactly like an `error`. Both mean the designation did not
     * move, and only one of them says so in a status code.
     */
    setPrimaryAction({ contactId: contact.id, kind: 'failed', message: result.message });
  };

  /**
   * Move one contact into a department, from the list that bands by it.
   *
   * ⚠️ **This writes the CONTACT, not the company** — `PATCH /contacts/:id`
   * with `contactGroupId` and nothing else. Read key ≠ write key here: the
   * value comes back as `contact.contactGroup`, an object, and posting that
   * back would be a 422 on a schema that does not know the name.
   *
   * ⚠️ **Only the changed key is sent, and an unchanged pick sends nothing.**
   * `PATCH /contacts` is `.strict()` and refuses an empty body, so a re-pick of
   * what is already set would be a 422 describing a write nobody asked for —
   * and spreading the loaded record would rewrite every column the reader never
   * opened.
   *
   * ⚠️ **`''` is NEVER sent.** The empty option means "no group", which this
   * API clears with an explicit `null`; `''` is a 422 on this field. That
   * inverts `shortName` on a company, one panel up, which clears on `''`.
   *
   * **How the row re-bands, and why no count can go stale**: the only thing
   * this writes on success is the contact record inside the list state, and
   * `CompanyContactsList` derives its sections and their counts from that list
   * on every render. Nothing caches a section, so the row moves and every
   * heading re-counts in the same paint.
   */
  const saveContactGroup = async (contact: Contact, next: string) => {
    const stored = contact.contactGroup ? String(contact.contactGroup.id) : '';
    if (next === stored) {
      // Not a write — and the refusal that may be sitting on this row is about
      // a value the reader has just abandoned, so it goes with it.
      setContactGroupActions((current) => {
        if (!current[contact.id]) return current;
        const { [contact.id]: _dropped, ...rest } = current;
        return rest;
      });
      return;
    }

    setContactGroupActions((current) => ({
      ...current,
      [contact.id]: { kind: 'saving', value: next },
    }));

    const settle = (action: ContactGroupActions[number]) =>
      setContactGroupActions((current) => {
        if (action === undefined) {
          const { [contact.id]: _dropped, ...rest } = current;
          return rest;
        }
        return { ...current, [contact.id]: action };
      });

    try {
      const response = await api.updateContact(contact.id, {
        contactGroupId: next === '' ? null : Number(next),
      });
      const saved = response.data;

      /*
       * The server's own copy of the row replaces ours — never the value that
       * was clicked. `PATCH /contacts` is `.strict()`, so a 200 already proves
       * the key was recognised; taking the record back is what proves what it
       * became, and it is what the banding reads.
       *
       * Guarded on the list still being the one this started against: a
       * refresh, or a different account, and this answer describes a list
       * nobody is looking at.
       */
      setContactsResult((current) => {
        if (current === null || current.key !== contactsKey || current.result === null) {
          return current;
        }
        const data = current.result.data.map((row) => (row.id === saved.id ? saved : row));
        return { ...current, result: { ...current.result, data } };
      });
      settle(undefined);
    } catch (caught) {
      if (caught instanceof ApiError) {
        if (caught.status === 403) {
          /*
           * The role changed under a live access token — they last about five
           * days — so the control being rendered is an optimisation and this is
           * the guarantee. A 403 carries no `details`, so it is a message
           * beside the control and never a field error.
           */
          settle({
            kind: 'failed',
            value: next,
            message: `${
              denialReason(ability, 'update', 'contacts') ??
              'You no longer have permission to change this contact.'
            } Nothing was saved.`,
          });
          return;
        }
        if (caught.status === 404) {
          settle({
            kind: 'failed',
            value: next,
            message: 'This contact no longer exists, so the group could not be saved.',
          });
          return;
        }
        /*
         * The 422 for an unknown or inactive group names `contactGroupId`, so
         * the field message is the specific one. `formMessage` before
         * `message`: on a 422 the latter is only "Validation failed".
         */
        settle({
          kind: 'failed',
          value: next,
          message: caught.fieldErrors.contactGroupId ?? caught.formMessage ?? caught.message,
        });
        return;
      }
      settle({
        kind: 'failed',
        value: next,
        message: 'Couldn’t reach the server. Check your connection.',
      });
    }
  };

  const requestContactDelete = (contact: Contact) => {
    setContactDeleteError(null);
    setPendingContactDelete(contact);
  };

  const closeContactDelete = () => {
    if (deletingContact) return;
    setPendingContactDelete(null);
    setContactDeleteError(null);
  };

  /**
   * `DELETE /contacts/:id`, from the row's trash button via the confirmation.
   *
   * ⚠️ **Deleting the PRIMARY contact also empties the company's designation**
   * — `fk_d_companies_primary_contact` is `ON DELETE SET NULL` — so the record
   * is re-read as well as the list, or the Account-details picker would go on
   * naming somebody who no longer exists.
   *
   * The 409 is the server's own sentence (it counts the deliverables naming
   * this person as point of contact), so it is shown as given.
   */
  const confirmContactDelete = async () => {
    const target = pendingContactDelete;
    if (!target || deletingContact) return;
    const wasPrimary = company?.primaryContact?.id === target.id;
    setDeletingContact(true);
    setContactDeleteError(null);
    try {
      await api.deleteContact(target.id);
      setPendingContactDelete(null);
      refreshContacts();
      if (wasPrimary) refreshRecord();
    } catch (caught) {
      if (caught instanceof ApiError) {
        if (caught.status === 403) {
          setContactDeleteError(
            denialReason(ability, 'delete', 'contacts') ??
              'You no longer have permission to delete contacts.',
          );
        } else if (caught.status === 404) {
          setContactDeleteError(`${target.name} no longer exists — the list has been refreshed.`);
          refreshContacts();
        } else {
          setContactDeleteError(caught.formMessage ?? caught.message);
        }
      } else {
        setContactDeleteError('Couldn’t reach the server. Check your connection.');
      }
    } finally {
      setDeletingContact(false);
    }
  };

  /**
   * The regions set, written as a whole. Its own function rather than a call
   * to `saveCompanyField`: the 422 names EVERY offending id at once, and those
   * ids have to reach the control so each bad chip is marked — see
   * `InlineSetSaveResult`.
   */
  const saveRegions = async (next: string[]): Promise<InlineSetSaveResult> => {
    const ticket = recordWriteSeq.current + 1;
    recordWriteSeq.current = ticket;

    try {
      // `regionIds` REPLACES the stored set: omitted leaves it alone, `[]`
      // clears it. So even "add one region" sends the complete array.
      const response = await api.updateCompany(company.id, { regionIds: next.map(Number) });
      const saved = response.data;

      if (recordWriteSeq.current === ticket) {
        setRecordResult({ key: recordKey, value: { status: 'ready', company: saved } });
      }

      // A 200 does not prove the change applied (`PATCH /companies` is not
      // `.strict()`). Ids come back exactly as sent or not at all, so the
      // read-back is conclusive here in a way it would not be for free text.
      const savedIds = new Set(saved.regions.map((region) => String(region.id)));
      const applied = savedIds.size === next.length && next.every((id) => savedIds.has(id));
      if (!applied) {
        return {
          status: 'warning',
          message:
            'Regions not saved — the server didn’t apply the change.',
        };
      }
      return { status: 'saved' };
    } catch (caught) {
      if (caught instanceof ApiError) {
        if (caught.status === 403) {
          return {
            status: 'error',
            message: `${
              denialReason(ability, 'update', 'companies') ??
              'You no longer have permission to change this account.'
            } Nothing was saved.`,
          };
        }
        if (caught.status === 404) {
          return {
            status: 'error',
            message: 'This account no longer exists, so the change could not be saved.',
          };
        }
        const message = caught.fieldErrors.regionIds ?? caught.formMessage ?? caught.message;
        return { status: 'error', message, invalidValues: refusedRegionIds(caught, next) };
      }
      return {
        status: 'error',
        message: 'Couldn’t reach the server. Check your connection.',
      };
    }
  };

  /**
   * The projects of interest, written as a whole — `saveRegions` above is the
   * shape this follows, key for key.
   *
   * Projects of interest are a list on the company: adding or removing one
   * creates or closes no sales pursuit (owner's decision 2026-10-12).
   */
  const saveProjectsOfInterest = async (next: string[]): Promise<InlineSetSaveResult> => {
    const ticket = recordWriteSeq.current + 1;
    recordWriteSeq.current = ticket;

    try {
      /*
       * `projectIdsOfInterest` REPLACES the stored set: omitted leaves it alone,
       * `[]` CLEARS it, and `null` is a 422. So even "add one project" sends the
       * complete array — and an unchanged set sends nothing at all, which the
       * control enforces before it ever calls this (an empty PATCH is a 422).
       *
       * One key, alone. Nothing else on the record travels with it.
       */
      const response = await api.updateCompany(company.id, {
        projectIdsOfInterest: next.map(Number),
      });
      const saved = response.data;

      if (recordWriteSeq.current === ticket) {
        setRecordResult({ key: recordKey, value: { status: 'ready', company: saved } });
      }

      // Same read-back as the regions set, and conclusive for the same reason:
      // `PATCH /companies` is not `.strict()`, and ids come back exactly as sent
      // or not at all.
      const savedIds = new Set(saved.projectsOfInterest.map((project) => String(project.id)));
      const applied = savedIds.size === next.length && next.every((id) => savedIds.has(id));
      if (!applied) {
        return {
          status: 'warning',
          message: `${PROJECTS_OF_INTEREST_LABEL} not saved — the server didn’t apply the change.`,
        };
      }
      return { status: 'saved' };
    } catch (caught) {
      if (caught instanceof ApiError) {
        if (caught.status === 403) {
          return {
            status: 'error',
            message: `${
              denialReason(ability, 'update', 'companies') ??
              'You no longer have permission to change this account.'
            } Nothing was saved.`,
          };
        }
        if (caught.status === 404) {
          return {
            status: 'error',
            message: 'This account no longer exists, so the change could not be saved.',
          };
        }
        /*
         * The server's own sentence, and no `invalidValues`.
         *
         * ⚠️ Deliberate. `refusedRegionIds` reads the ids out of a refusal whose
         * `path` starts `regionIds` and whose wording is known — neither is true
         * here, and a regex guessed against a message nobody has seen would mark
         * the wrong chips as confidently as the right ones. The message still
         * names what was refused; marking chips waits for a helper that belongs
         * beside the other set rules, not for a second copy invented on this
         * page.
         */
        return {
          status: 'error',
          message: caught.fieldErrors.projectIdsOfInterest ?? caught.formMessage ?? caught.message,
        };
      }
      return {
        status: 'error',
        message: 'Couldn’t reach the server. Check your connection.',
      };
    }
  };

  // Built by plain functions returning elements, never components declared in
  // here — a component declared inside this one is a new type on every
  // render, so every field would unmount (losing its draft and "Saved" line)
  // whenever anything else on the page changed.
  const textField = (
    key: TextKey,
    label: string,
    extra: {
      display?: ReactNode;
      action?: ReactNode;
      /** For a column whose empty state reads better as words than as a dash. */
      emptyLabel?: string;
      type?: 'text' | 'email' | 'tel';
      inputMode?: 'text' | 'url';
      autoComplete?: string;
      placeholder?: string;
      multiline?: boolean;
      rows?: number;
    } = {},
  ) => {
    const { multiline, ...rest } = extra;
    return (
      <InlineField
        label={label}
        value={company[key] ?? ''}
        kind={multiline ? 'multiline' : 'text'}
        disabled={!canUpdate}
        maxLength={TEXT_MAX[key]}
        validate={(next) => validateCompanyText(key, next)}
        onSave={(next) =>
          saveCompanyField(key, companyPatch(key, next), undefined, (caught) =>
            describeBillingNameRefusal(key, caught),
          )
        }
        {...rest}
      />
    );
  };

  // A picker over the staff directory. The record's own holder is injected
  // when the fetched list doesn't carry them — a picker that silently dropped
  // the current assignee would read as "Unassigned" and one stray save would
  // clear a field nobody had touched.
  const staffField = (key: StaffKey, label: string, person: UserSummary | null) => {
    const staff = staffByKey[key];
    const options: InlineFieldOption[] = staff.users.map((user) => ({
      value: String(user.id),
      label: user.name,
      hint: user.email,
    }));
    // ⚠️ The stored holder is injected whether or not they are in the group —
    // dropping them would read as "Unassigned" and the next save would
    // reassign it. `outsideGroupNote` labels them, returning null while the
    // list is unknown so a valid lead is never accused on no data.
    if (person && !staff.users.some((user) => user.id === person.id)) {
      const outside = outsideGroupNote(staff);
      options.unshift({
        value: String(person.id),
        label: person.name,
        hint: [person.email, outside].filter(Boolean).join(' · '),
      });
    }

    /*
     * Independent facts about the same list, so each is said rather than one
     * standing in for the others.
     *
     * ⚠️ **DOWN TO ONE — owner's stricter helper-text ruling, 2026-09-05.** Two
     * of the three went: "Staff list unavailable — this picker is empty for that
     * reason." (a load failure; note the picker is NOT disabled here, only
     * empty, so it was never a why-this-is-disabled sentence) and "Showing the
     * first 100 users." (a truncation notice).
     *
     * ⚠️ What SURVIVES is `narrowingNote`, and it is a refusal: this picker is
     * narrowed to a user group `/companies` enforces, so naming somebody outside
     * it is a live 422. It is also what stops a one-name dropdown reading as a
     * broken fetch. The array shape is kept rather than collapsed to a string —
     * a second refusal would join it, and a `??` between two of them is the
     * silent drop this codebase keeps meeting.
     */
    const notes = [narrowingNote(staff)].filter((note): note is string => note !== null);

    return (
      <div className="grid min-w-0 gap-1">
        <InlineField
          kind="picker"
          label={label}
          value={person ? String(person.id) : ''}
          display={<StaffValue person={person} linked={!canUpdate} />}
          disabled={!canUpdate}
          {...(canUpdate && person && person.email !== ''
            ? { action: <ValueAction value={person.email} kind="email" /> }
            : {})}
          options={options}
          optionsLoading={usersLoading}
          emptyOptionLabel="Unassigned"
          placeholder={usersLoading ? 'Loading users…' : 'Unassigned'}
          searchPlaceholder="Search staff…"
          {...(notes.length > 0 ? { footnote: notes.join(' ') } : {})}
          onSave={(next) =>
            // The account lead asks "Effective from" first (owner 2026-10-25);
            // the Client success manager saves straight away, as before.
            key === 'accountLeadId'
              ? requestLeadChange(next, person, options)
              : saveCompanyField(key, staffPatch(key, next), {
                  sent: next,
                  read: (saved) => readStaff(saved, key),
                  noun: label.toLowerCase(),
                })
          }
        />
        {/*
          * "Staff list unavailable — this picker is empty." stood here and is
          * GONE — owner's stricter helper-text ruling, 2026-09-05. A load
          * failure over a picker that is empty but NOT disabled, so it was never
          * a why-this-is-disabled sentence.
          *
          * ⚠️ Its own comment recorded the cost and it still applies: a failed
          * staff read now renders as a perfectly ordinary "Unassigned".
          */}
      </div>
    );
  };

  /**
   * The faculty picker.
   *
   * `null` is a real state — "no faculty chosen" — so "Not set" is a choice in
   * the list rather than only the empty rendering of one.
   */
  const facultyField = () => {
    const stored = company.faculty;
    const retired =
      stored !== null &&
      facultyRecords.length > 0 &&
      !facultyRecords.some((record) => record.id === stored.id && record.isActive);

    return (
      <InlineField
        kind="picker"
        label="Faculty"
        value={stored ? String(stored.id) : ''}
        display={
          stored ? (
            <span className="flex min-w-0 flex-wrap items-center gap-1.5">
              <span className="min-w-0">{masterLabel(stored.id, stored.name)}</span>
              {/* Marked because changing away from a retired faculty is a
                  one-way door — it cannot be chosen again. */}
              {retired && <Badge tone="warning">Retired</Badge>}
            </span>
          ) : (
            <span className="text-fg-subtle">Not set</span>
          )
        }
        disabled={!canUpdate}
        options={facultyPickerOptions(facultyRecords, stored)}
        optionsLoading={facultiesLoading}
        emptyOptionLabel="Not set"
        placeholder={facultiesLoading ? 'Loading faculties…' : 'Not set'}
        searchPlaceholder="Search faculties…"
        {...(facultiesFailed
          ? { footnote: 'Faculty list unavailable. Reload to try again.' }
          : {})}
        onSave={(next) =>
          saveCompanyField(
            'facultyId',
            { facultyId: next === '' ? null : Number(next) },
            {
              sent: next,
              read: (saved) => (saved.faculty ? String(saved.faculty.id) : ''),
              noun: 'faculty',
            },
          )
        }
      />
    );
  };

  /**
   * The company's country OF RECORD — `countryId`, the foreign key the server
   * derives the billing region from. Editable here on the owner's instruction
   * (2026-10-11), as it already is on `/companies` and in the Edit dialog.
   *
   * ⚠️ It sends `countryId` and nothing else. The region, currency and Xero
   * defaults are the server's to re-derive; nothing here touches them.
   *
   * ⚠️ `null` is the ordinary state and reads "Not recorded" — never a country,
   * and never "Rest of the World", which is a claim about a currency. A failed
   * master read DISABLES the control with its reason, the `sourceField` shape:
   * an empty picker over a visible value offers only clearing it.
   */
  const countryField = () => {
    const stored = company.country;

    const reason = !canUpdate
      ? denialReason(ability, 'update', 'companies')
      : countriesFailed
        ? 'Country list unavailable — can’t be changed here.'
        : null;

    return (
      <InlineField
        kind="picker"
        label="Country"
        value={stored ? String(stored.id) : ''}
        display={
          countryOfRecordLabel(stored) ?? <span className="text-fg-subtle">Not recorded</span>
        }
        disabled={!canUpdate || countriesFailed}
        {...(reason === null ? {} : { disabledReason: reason })}
        options={countryPickerOptions(countryRows, stored)}
        optionsLoading={countriesLoading}
        emptyOptionLabel="Not recorded"
        placeholder={countriesLoading ? 'Loading countries…' : 'Not recorded'}
        searchPlaceholder="Search countries…"
        onSave={(next) =>
          saveCompanyField(
            'countryId',
            // `null` clears the nullable FK; `''` would be a 422.
            { countryId: next === '' ? null : Number(next) },
            {
              sent: next,
              read: (saved) => (saved.country ? String(saved.country.id) : ''),
              noun: 'country',
            },
          )
        }
      />
    );
  };

  /**
   * Where this account came from.
   *
   * ⚠️ **`null` is the ORDINARY state, not a gap** — the column is new, nothing
   * was backfilled, and a school nobody wrote a source down for is an ordinary
   * school. So "No source recorded" is a labelled CHOICE in the list and the
   * rendering of an unset value, in those same words: picking the empty row
   * returns the reader to exactly what they started at, rather than to a
   * differently-worded blank that reads as a second state.
   *
   * ⚠️ **Never "None".** "None" is an answer somebody gave — an account that
   * came from nowhere — where this blank says only that nobody recorded it. The
   * difference is invisible on one record and wrong on all 413.
   *
   * ⚠️ **A failed master read DISABLES the control, with the reason.** An empty
   * picker over a value the reader can see offers exactly one usable action —
   * clearing it — so it is the `primaryContactField` shape rather than
   * `facultyField`'s footnote, which renders inside a popover this control
   * would never open. A why-this-is-disabled sentence is one of the four the
   * no-helper-text rule keeps.
   */
  const sourceField = () => {
    const stored = company.source;

    // A bare `disabled` makes "you may not" and "nothing to pick from" look
    // identical, and they want different sentences.
    const reason = !canUpdate
      ? denialReason(ability, 'update', 'companies')
      : sourcesFailed
        ? 'Source list unavailable — can’t be changed here.'
        : null;

    return (
      <InlineField
        kind="picker"
        label="Company source"
        value={stored ? String(stored.id) : ''}
        display={
          stored ? (
            <span className="min-w-0">{masterLabel(stored.id, stored.name)}</span>
          ) : (
            <span className="text-fg-subtle">No source recorded</span>
          )
        }
        disabled={!canUpdate || sourcesFailed}
        {...(reason === null ? {} : { disabledReason: reason })}
        options={companySourceOptions(sourceRecords, stored)}
        optionsLoading={sourcesLoading}
        emptyOptionLabel="No source recorded"
        placeholder={sourcesLoading ? 'Loading sources…' : 'No source recorded'}
        searchPlaceholder="Search sources…"
        onSave={(next) =>
          saveCompanyField(
            'sourceId',
            /*
             * ONE explicit key, never a spread: `null` clears the column and
             * `''` is a value, so the empty choice must travel as `null`. A
             * spread of the form's state would send every other column with it.
             */
            { sourceId: next === '' ? null : Number(next) },
            /*
             * ⚠️ The read-back is not optional here. A `200` is not evidence
             * the change was applied — this repo has been caught by that four
             * times — and this is a column the server may legitimately ignore
             * (a retired row, a guard nobody here knows about) while answering
             * cheerfully.
             */
            {
              sent: next,
              read: (saved) => (saved.source ? String(saved.source.id) : ''),
              noun: 'source',
            },
          )
        }
      />
    );
  };

  /**
   * The drill-down under the pipeline stage (owner 2026-10-23). Offers only the
   * ACTIVE statuses of the company's CURRENT stage; a retired stored value
   * still displays. A stage change clears it server-side, and `saveStage`
   * already lands the response, so nothing here has to follow the stage.
   *
   * `stageStatus` absent (`undefined`) means an older API: the caller renders
   * nothing rather than claim the account has no status.
   *
   * Labelled by the CURRENT stage, `{Stage name} status` (owner 2026-10-25,
   * the same wording as the per-stage list columns). The caller hides it when
   * there is no stage, or the stage has no active statuses and none is held
   * (`showStageStatus`). Both follow `company.stage`, which `saveStage` lands.
   */
  const stageStatusField = () => {
    const stored = company.stageStatus ?? null;
    const stageId = company.stage?.id ?? null;
    const label = stageStatusLabel;

    const options: InlineFieldOption[] =
      stageId === null
        ? []
        : filterStatusesForStage(stageStatusRecords, stageId, {
            includeInactiveId: stored?.id ?? null,
          }).map((status) => ({
            value: String(status.id),
            label: status.isActive ? status.name : `${status.name} (retired)`,
          }));
    if (stored && !options.some((option) => option.value === String(stored.id))) {
      options.unshift({ value: String(stored.id), label: stored.name });
    }

    const reason = !canUpdate
      ? denialReason(ability, 'update', 'companies')
      : stageStatusesFailed
        ? `${label} list unavailable — can’t be changed here.`
        : stageId === null
          ? 'Pick a stage first.'
          : null;

    return (
      <InlineField
        kind="picker"
        label={label}
        value={stored ? String(stored.id) : ''}
        display={
          stored ? (
            <span className="min-w-0">{masterLabel(stored.id, stored.name)}</span>
          ) : (
            <span className="text-fg-subtle">—</span>
          )
        }
        disabled={!canUpdate || stageStatusesFailed || stageId === null}
        {...(reason === null ? {} : { disabledReason: reason })}
        options={options}
        optionsLoading={stageStatusesLoading}
        emptyOptionLabel="—"
        placeholder={stageStatusesLoading ? 'Loading…' : '—'}
        searchPlaceholder="Search statuses…"
        onSave={async (next) => {
          const nextId = next === '' ? null : Number(next);
          /*
           * The chosen status's FLAGS decide whether a follow-up date or a
           * reason is asked for first (`useStageStatusChange`). `null` is a
           * cancel: nothing is sent. A status the master no longer lists goes
           * through without the dialog, and the server's 422 is the boundary.
           */
          const chosen =
            nextId === null
              ? null
              : (stageStatusRecords.find((status) => status.id === nextId) ?? null);
          // The status BEFORE the change, from the master by id; a status the
          // master no longer lists is left out (unknown), never guessed.
          const previousStatusId = company.stageStatus?.id ?? null;
          const currentStatus =
            previousStatusId === null
              ? null
              : (stageStatusRecords.find((status) => status.id === previousStatusId) ?? undefined);
          const extra = await stageStatusChange.request({
            companyName: company.name,
            status: chosen,
            currentFollowUpOn: company.followUpOn ?? null,
            ...(currentStatus === undefined ? {} : { currentStatus }),
          });
          if (extra === null) {
            return { status: 'warning', message: `${label} not changed.` };
          }
          return saveCompanyField(
            // The keys SENT, so a 422 on the date or the reason lands here too.
            ['stageStatusId', ...Object.keys(extra)],
            // The empty choice travels as `null`.
            { stageStatusId: nextId, ...extra },
            {
              sent: next,
              read: (saved) => (saved.stageStatus ? String(saved.stageStatus.id) : ''),
              noun: label,
            },
            // Matched on the CODE so the refusal lands on this control even
            // when it arrives without a `details` path.
            (caught) =>
              caught.code === 'STAGE_STATUS_WRONG_STAGE' ||
              caught.code === 'STAGE_STATUS_REASON_REQUIRED' ||
              isFollowUpDateRefusal(caught)
                ? {
                    status: 'error',
                    message:
                      caught.fieldErrors.stageStatusId ??
                      caught.fieldErrors.followUpOn ??
                      caught.fieldErrors.stageStatusReason ??
                      caught.message,
                  }
                : null,
          );
        }}
      />
    );
  };

  /**
   * The follow-up date beside the stage status (owner 2026-10-23). Shown when
   * one is stored, or when the current status resurfaces (decided by its
   * `resurfaceMode` FLAG) so a missing date can be set. Editable only for a
   * resurface status: the PATCH carries `followUpOn` alone, and the server
   * reschedules the follow-up task. Not clearable here — a resurface status
   * needs a date.
   */
  const followUpField = () => {
    const stored = company.followUpOn ?? null;
    const resurfaceMode =
      company.stageStatus?.resurfaceMode ??
      stageStatusRecords.find((status) => status.id === company.stageStatus?.id)?.resurfaceMode ??
      null;
    if (stored === null && resurfaceMode === null) return null;

    const reason = !canUpdate
      ? denialReason(ability, 'update', 'companies')
      : resurfaceMode === null
        ? 'Only a resurfacing stage status has a follow-up date.'
        : null;
    const minDate = nextDayIso(todayUtcIsoDate());

    return (
      <InlineField
        label="Follow-up date"
        type="date"
        value={stored ?? ''}
        display={
          <span className={stored === null ? 'text-fg-subtle' : undefined}>
            Follow up on {stored === null ? '—' : formatDateOnly(stored)}
          </span>
        }
        disabled={!canUpdate || resurfaceMode === null}
        {...(reason === null ? {} : { disabledReason: reason })}
        validate={(next) => {
          if (next === '' || !isRealDate(next)) return 'Enter a follow-up date.';
          if (next < minDate) return 'The follow-up date must be after today.';
          return null;
        }}
        onSave={(next) =>
          saveCompanyField(
            'followUpOn',
            { followUpOn: next },
            {
              sent: next,
              read: (saved) => saved.followUpOn ?? '',
              noun: 'Follow-up date',
            },
            (caught) =>
              isFollowUpDateRefusal(caught)
                ? {
                    status: 'error',
                    message: caught.fieldErrors.followUpOn ?? caught.message,
                  }
                : null,
          )
        }
      />
    );
  };
  // Rendered beside the stage status when that shows, else on its own row, so
  // a stored date is never hidden by the status field's visibility.
  const followUpNode = followUpField();

  /**
   * What kind of organisation this is — `Institution` or `Partner` today.
   *
   * ⚠️ **A FOURTH axis, and not derivable from the three beside it.** Stage is
   * where an account sits commercially, Approval is whether it was signed off,
   * Source is how it reached us, and this is what the counterparty IS. Source
   * and Type are the pair most likely to be read as one control — they are
   * adjacent, both are short master pickers, and both answer a question starting
   * "what kind of" — so the labels stay `Company source` and `Company type` in
   * full rather than being shortened to anything they could share.
   *
   * ⚠️ **`null` is the ORDINARY state, not a gap** — the column is new and
   * nothing was backfilled. So "No type recorded" is a labelled CHOICE in the
   * list and the rendering of an unset value, in those same words: picking the
   * empty row returns the reader to exactly what they started at.
   *
   * ⛔ **Never "None", and never `Institution`.** "None" is an answer somebody
   * gave — an organisation of no kind; `Institution` is the seeded first row and
   * what most accounts are, which is what would make a silent default invisible.
   * The blank says only that nobody has classified this account.
   *
   * ⚠️ **A failed master read DISABLES the control, with its OWN reason** —
   * separate from the permission one, because "you may not change this" and
   * "there is nothing to pick from" want different sentences and a bare
   * `disabled` makes them identical.
   */
  const companyTypeField = () => {
    const stored = company.companyType;

    const reason = !canUpdate
      ? denialReason(ability, 'update', 'companies')
      : companyTypesFailed
        ? 'Type list unavailable — can’t be changed here.'
        : null;

    return (
      <InlineField
        kind="picker"
        label="Company type"
        value={stored ? String(stored.id) : ''}
        display={
          stored ? (
            <span className="min-w-0">{masterLabel(stored.id, stored.name)}</span>
          ) : (
            <span className="text-fg-subtle">No type recorded</span>
          )
        }
        disabled={!canUpdate || companyTypesFailed}
        {...(reason === null ? {} : { disabledReason: reason })}
        options={companyTypeOptions(companyTypeRecords, stored)}
        optionsLoading={companyTypesLoading}
        emptyOptionLabel="No type recorded"
        placeholder={companyTypesLoading ? 'Loading types…' : 'No type recorded'}
        searchPlaceholder="Search types…"
        onSave={(next) =>
          saveCompanyField(
            'companyTypeId',
            /*
             * ONE explicit key, never a spread: `null` clears the column and
             * `''` is a 422 on a reference id, so the empty choice must travel
             * as `null`.
             */
            { companyTypeId: next === '' ? null : Number(next) },
            /*
             * ⚠️ The read-back is not optional here, for `sourceField`'s reason:
             * a `200` is not evidence the change was applied — `PATCH
             * /companies` is not `.strict()`, so a key it does not recognise is
             * STRIPPED and answered cheerfully.
             *
             * ⚠️ **`companyTypeId` out, `companyType` back** — read key ≠ write
             * key, and here they differ by a SUFFIX rather than by a word, so
             * reading the wrong one verifies nothing while looking right.
             */
            {
              sent: next,
              read: (saved) => (saved.companyType ? String(saved.companyType.id) : ''),
              noun: 'type',
            },
          )
        }
      />
    );
  };

  /**
   * Whether partners can find this company in the institution search on the
   * public forms (backend migration 263). Used to keep internal records such as
   * finance or accounts departments out of a booking partner's picker.
   *
   * ⚠️ Two choices and no empty one: the column is NOT NULL, so `null` would be
   * a 422. An older API omits the key, and absent reads as shown.
   */
  const partnerSearchField = () => {
    const hidden = company.hiddenFromPartnerSearch === true;
    const reason = canUpdate ? null : denialReason(ability, 'update', 'companies');

    return (
      <InlineField
        kind="picker"
        label="Partner search"
        value={hidden ? 'hidden' : 'shown'}
        display={
          hidden ? (
            <span className="text-fg">Hidden from partners</span>
          ) : (
            <span className="text-fg">Shown to partners</span>
          )
        }
        disabled={!canUpdate}
        {...(reason === null ? {} : { disabledReason: reason })}
        options={[
          { value: 'shown', label: 'Shown to partners' },
          { value: 'hidden', label: 'Hidden from partners' },
        ]}
        onSave={(next) =>
          saveCompanyField(
            'hiddenFromPartnerSearch',
            { hiddenFromPartnerSearch: next === 'hidden' },
            {
              sent: next,
              read: (saved) => (saved.hiddenFromPartnerSearch === true ? 'hidden' : 'shown'),
              noun: 'partner search setting',
            },
          )
        }
      />
    );
  };

  /**
   * The primary contact: one of THIS account's own contacts, and writable from
   * the company alone — there is no "make me primary" on the contact side.
   *
   * Three things shape it:
   *
   * 1. **`null` is a real, common and often permanent answer**, so "No primary
   *    contact" is a choice in the list rather than only the empty rendering of
   *    one.
   * 2. **Ineligible contacts are MARKED, never hidden** — see
   *    `primaryContactRefusal`. The five 422s are the server's to give and each
   *    names a different next action, so `saveCompanyField` puts the server's
   *    own sentence on this control and adds nothing of ours to it.
   * 3. **A failed lookup DISABLES the control** rather than opening an empty
   *    list, because an empty list can only be used to clear a designation.
   */
  const primaryContactField = () => {
    const stored = company.primaryContact;

    const options: InlineFieldOption[] = (primaryContacts ?? []).map((contact) => {
      const hint = [contact.email, primaryContactRefusal(contact)]
        .filter((part): part is string => part !== null && part.trim() !== '')
        .join(' · ');
      return {
        value: String(contact.id),
        label: contact.name,
        ...(hint === '' ? {} : { hint }),
      };
    });

    /*
     * ⚠️ The stored primary is injected whether or not the fetched list holds
     * them. The server's read joins on the contact id ALONE, so a designation
     * left behind by somebody who moved company still reads back with a real
     * name — and a picker that dropped them would show "No primary contact"
     * over a designation that exists, then clear it on the next save.
     *
     * They are only LABELLED as no longer belonging here when the list is
     * known to be complete: a failed or truncated read is not evidence about
     * who this account's contacts are, and accusing a live contact on the
     * strength of a request that never came back is the worse error. Same rule
     * as `outsideGroupNote` on the two staff pickers above.
     */
    if (stored && !options.some((option) => option.value === String(stored.id))) {
      const departed = primaryContacts !== null && !primaryContactsMore;
      const hint = [stored.email, departed ? 'No longer one of this account’s contacts' : null]
        .filter((part): part is string => part !== null && part.trim() !== '')
        .join(' · ');
      options.unshift({
        value: String(stored.id),
        label: stored.name,
        ...(hint === '' ? {} : { hint }),
      });
    }

    /*
     * Truncation is the only footnote, and a failed lookup deliberately has
     * none: the footnote renders inside the popover, and a failed lookup
     * disables the control, so the editor never opens to show it. The failure
     * is said as the `disabledReason` below instead, which renders under the
     * value where somebody reading the page can actually see it.
     */
    const truncated = primaryContactsMore
      ? `Showing the first ${CONTACTS_PER_PAGE} contacts.`
      : null;

    // A bare `disabled` makes "you may not" and "nothing to pick from" look
    // identical, and they want different sentences.
    const reason = !canUpdate
      ? denialReason(ability, 'update', 'companies')
      : primaryContactsFailed
        ? 'Contact list unavailable — can’t be changed here.'
        : null;

    return (
      <InlineField
        kind="picker"
        label="Primary contact"
        value={stored ? String(stored.id) : ''}
        display={<PrimaryContactValue contact={stored} />}
        disabled={!canUpdate || primaryContactsFailed}
        {...(reason === null ? {} : { disabledReason: reason })}
        options={options}
        optionsLoading={primaryContactsLoading}
        emptyOptionLabel="No primary contact"
        placeholder={primaryContactsLoading ? 'Loading contacts…' : 'No primary contact'}
        searchPlaceholder="Search this account’s contacts…"
        {...(truncated === null ? {} : { footnote: truncated })}
        onSave={(next) =>
          saveCompanyField(
            'primaryContactId',
            // `null` clears the designation; `''` is a 422 on this key, so the
            // empty choice must never travel as the empty string.
            { primaryContactId: next === '' ? null : Number(next) },
            {
              sent: next,
              read: (saved) => (saved.primaryContact ? String(saved.primaryContact.id) : ''),
              noun: 'primary contact',
            },
          )
        }
      />
    );
  };

  /**
   * The account's default currency, pushed to `Contact.DefaultCurrency`.
   *
   * 1. **The picker is Xero's enabled list, not our master** — 25 rows against
   *    164; the other 139 are hard-refused on save. We write `currencyId`,
   *    matched to the enabled code.
   * 2. **The code is the identity**; the name is a hint. See
   *    `currencyPickerOptions`.
   * 3. **`currency.id` can be null while `code` is set** — see `currencyValue`.
   * 4. **Clearing always works and never asks Xero**, so nobody is trapped
   *    with a currency they cannot remove.
   */
  const currencyField = () => {
    const stored = company.currency;
    const { options, codeByValue, unmatched } = currencyPickerOptions(
      xeroCurrencies.currencies,
      currencyMaster,
      stored,
    );
    const listLoading = xeroCurrencies.loading || currencyMasterLoading;

    // ⚠️ A guard on OFFERING, never on saving — same shape as the branding
    // theme and tax rate. Clearing makes no Xero call, so the control is
    // inert only when there is nothing to choose from AND nothing to clear.
    const listUnavailable =
      xeroLoading ||
      !xeroConnection.connected ||
      xeroNotInstalled ||
      xeroCurrencies.failed ||
      currencyMasterFailed;
    const canOnlyClear = listUnavailable && stored !== null;

    // The intersection, not `options.length`, which also counts an injected
    // stored value.
    const offered = xeroCurrencies.currencies.length - unmatched.length;
    const unmatchedNote =
      unmatched.length === 0
        ? ''
        : ` ${joinList(unmatched)} ${
            unmatched.length === 1 ? 'is' : 'are'
          } enabled in Xero but not in SEED EMERGE, so ${
            unmatched.length === 1 ? 'it' : 'they'
          } can’t be chosen.`;
    const footnote = xeroLoading
      ? 'Checking the Xero connection…'
      : !xeroConnection.connected
        ? xeroNotInstalled
          ? `Xero isn’t set up, so its currencies can’t be listed.${
              canOnlyClear ? ' The one already set can still be cleared.' : ''
            }`
          : `Xero isn’t connected, so its currencies can’t be listed.${
              canOnlyClear ? ' The one already set can still be cleared.' : ''
            }`
        : xeroCurrencies.cooledDown
          ? // Named rather than folded into the generic read failure — a
            // retry is the one action that cannot work while the budget is
            // spent.
            `Xero rate limit reached — no currency can be chosen right now.${
              canOnlyClear ? ' The one already set can still be cleared.' : ''
            }`
          : xeroCurrencies.failed
            ? `Xero’s currencies couldn’t be read — no currency can be chosen.${
                canOnlyClear ? ' The one already set can still be cleared.' : ''
              }`
            : currencyMasterFailed
              ? 'Currency list unavailable. Reload to try again.'
              : listLoading
                ? 'Reading Xero’s currencies…'
                : offered === 0
                  ? `No currency Xero enables can be written from SEED EMERGE.${unmatchedNote}`
                  : `Only the ${offered} ${
                      offered === 1 ? 'currency' : 'currencies'
                    } Xero enables can be chosen.${unmatchedNote}`;

    return (
      <InlineField
        kind="picker"
        label="Default currency"
        value={currencyValue(stored)}
        display={
          stored === null ? (
            <span className="text-fg-subtle">Not set</span>
          ) : (
            // The CODE, with the name beneath it — `id` may be null and
            // `name` may be either source's wording, but `code` always works.
            <span className="grid gap-0.5">
              <span className="font-medium text-fg">{stored.code}</span>
              {stored.name.trim() !== '' && stored.name.trim() !== stored.code && (
                <span className="text-xs text-fg-muted">{stored.name}</span>
              )}
            </span>
          )
        }
        disabled={!canUpdate || (listUnavailable && !canOnlyClear)}
        options={options}
        optionsLoading={listLoading}
        emptyOptionLabel="Not set"
        placeholder={listLoading ? 'Reading currencies from Xero…' : 'Not set'}
        searchPlaceholder="Search by code or name…"
        footnote={footnote}
        onSave={(next) => {
          // `next` is a master id, or `''` for "Not set" — never the
          // code-derived value of a stored currency our master cannot name.
          const code = next === '' ? '' : codeByValue.get(next);
          return saveCompanyField(
            'currencyId',
            { currencyId: next === '' ? null : Number(next) },
            // Read back by CODE, not id — `currency.id` may come back null
            // for a code our master doesn't carry.
            code === undefined
              ? undefined
              : {
                  sent: code,
                  read: (saved) => (saved.currency?.code ?? '').toUpperCase(),
                  noun: 'currency',
                },
            describeCurrencyFailure,
          );
        }}
      />
    );
  };

  /** The regions SET. Multi because the data is multi — a company can hold four. */
  const regionsField = () => {
    const retired = retiredRegionNames(regionRecords, company.regions);
    return (
      <InlineRegionsField
        label="Regions of focus"
        values={company.regions.map((region) => String(region.id))}
        options={regionOptions(regionRecords, company.regions)}
        optionsLoading={regionsLoading}
        disabled={!canUpdate}
        emptyLabel="None chosen"
        placeholder={regionsLoading ? 'Loading regions…' : 'None chosen'}
        searchPlaceholder="Search regions…"
        {...(regionsFailed
          ? { footnote: 'Region list unavailable. Reload to try again.' }
          : {})}
        {...(canUpdate && retired.length > 0
          ? {
              note: `${joinList(retired)} ${
                retired.length === 1 ? 'is' : 'are'
              } retired — remove ${retired.length === 1 ? 'it' : 'them'} to save this field.`,
            }
          : {})}
        onSave={saveRegions}
      />
    );
  };

  /**
   * The projects this account is being pursued for. A SET, edited where it is
   * read, through the same control as the regions set above.
   *
   * ⚠️ The READ VIEW is collapsed, which the regions set's is not, and that is
   * what `display` exists for. A company holds two to four regions and all of
   * them are the answer; there are 113 ACTIVE projects (measured 2026-09-12;
   * ⚠️ *SUPERSEDED: "97 live projects"*) and their names are long
   * (`Business School Festival - India - Summer 2026`), so an uncollapsed chip
   * list turns this row into a paragraph. `companyProjectsLabel` words the
   * collapse and `companyProjectsTitle` carries the full list on the `title` —
   * the same split as the city chips on `/projects`, where the name stayed
   * visible and the country moved to the hover.
   *
   * 🔴 **`block truncate` IS LOAD-BEARING SINCE 2026-09-12 AND WAS ADDED WITH
   * THE OWNER'S "SHOW THE VALUES" RULING.** `companyProjectsLabel` used to
   * render a COUNT past one project (`2 projects`), which fitted this row at any
   * width; it now renders the NAMES (`BSF India, Campus Tours +2`). The cap
   * bounds the string to two names, but two real project names are ~60
   * characters, so without this class the row wraps to a paragraph — which is
   * the exact failure the paragraph above says `display` exists to prevent, and
   * it would have arrived silently from a change in another file.
   *
   * ⚠️ The two list cells that render this label already carry `truncate`, so
   * this was the ONE site where the wider string escaped. Do not remove it
   * without re-reading `companyProjectsLabel`.
   *
   * ⚠️ `display` replaces the read view ONLY. The editor is still the whole
   * multi-select and the save is still the whole set, so a collapsed display can
   * never become a collapsed write.
   */
  /*
   * ⚠️ **Failed OUTRANKS truncated, and the order is `CompaniesPage`'s and
   * `CompanyProjectsModal`'s — all three read the same two sentences in the same
   * precedence.** A list that never arrived is not a list that arrived short:
   * reporting truncation over a failed read describes a page that was never
   * fetched, and sends the reader hunting a dozen missing projects instead of
   * reloading.
   *
   * ⚠️ **Unlike the primary-contact footnote above, a failed list here does NOT
   * disable the control**, so the editor still opens and the popover really does
   * render this sentence. That is why this field words the failure as a footnote
   * where that one words it as a `disabledReason`; the two are not inconsistent.
   */
  const projectsFootnote = projectsFailed
    ? 'Project list unavailable. Reload to try again.'
    : moreProjects
      ? 'Not all projects are listed — this picker shows the first page of them.'
      : undefined;

  const projectsOfInterestField = () => (
    <InlineRegionsField
      label={PROJECTS_OF_INTEREST_LABEL}
      values={company.projectsOfInterest.map((project) => String(project.id))}
      options={projectOfInterestOptions(
        projectRecords,
        company.projectsOfInterest,
        projectCycleScopes,
        projectCycleScope,
      )}
      optionsLoading={projectsLoading}
      disabled={!canUpdate}
      emptyLabel="None chosen"
      display={
        company.projectsOfInterest.length === 0 ? (
          <span className="text-fg-subtle">None chosen</span>
        ) : (
          <ValuePills
            items={companyProjectsPills(company.projectsOfInterest)}
            title={companyProjectsTitle(company.projectsOfInterest)}
          />
        )
      }
      editorHeader={
        <SegmentedControl
          /*
           * Names the CHOICE rather than the current answer, per
           * `SegmentedControl`'s contract. It is the group's accessible name
           * and renders nowhere visibly, so it is not helper text — the two
           * pill labels are the whole visible explanation of this control, and
           * nothing may be added beside them.
           */
          label="Project cycle"
          value={projectCycleScope}
          options={PROJECT_CYCLE_SCOPE_OPTIONS}
          onChange={setProjectCycleScope}
        />
      }
      placeholder={projectsLoading ? 'Loading projects…' : 'None chosen'}
      searchPlaceholder="Search projects…"
      {...(projectsFootnote === undefined ? {} : { footnote: projectsFootnote })}
      onSave={saveProjectsOfInterest}
    />
  );

  /**
   * Copy the billing address onto shipping AND save it, in ONE request.
   *
   * ⚠️ **One request, not five.** Every other field here saves on its own, so
   * five single-key PATCHes would be the obvious shape — and it would have four
   * durable half-copied states in between, each of them a readable address that
   * is neither the old one nor the new one, with no way to report which of the
   * five failed. `PATCH /companies/:id` takes all five, so the copy is atomic
   * or it is nothing.
   *
   * ⚠️ **A ONE-TIME copy, never a mirror.** Nothing here records that the two
   * addresses were ever made equal, and a later billing edit must not follow —
   * the owner chose that over a stored "same as billing" flag, which would have
   * meant an edit to one address silently rewriting another.
   */
  const runAddressCopy = async () => {
    // Nothing to send is a 422, and the button is already held down for it —
    // this is the guard behind the optimisation, not a second copy of it.
    if (addressCopyChanges.length === 0) return;

    setAddressCopyConfirmOpen(false);
    setAddressCopy({ kind: 'saving' });

    /*
     * ⚠️ Only the keys that actually DIFFER, and `null` — never `''` — for a
     * billing field that is blank. An empty string is a value on this API, so
     * sending one would leave the shipping column holding `''` against a
     * billing column holding `null`: two addresses that read identically on
     * screen and differ on the wire, which puts the button straight back into a
     * state where it thinks it has work to do.
     */
    const patch: CompanyInput = {};
    for (const { to, billing } of addressCopyChanges) {
      patch[to] = billing === '' ? null : billing;
    }

    const result = await saveCompanyField(
      addressCopyChanges.map(({ to }) => to),
      patch,
    );

    setAddressCopy(
      result.status === 'saved'
        ? { kind: 'saved' }
        : result.status === 'error'
          ? { kind: 'error', message: result.message }
          : // `warning` and `partial` both mean the write is over and something
            // still wants attention. Neither may render as a clean save.
            { kind: 'warning', message: result.message },
    );
  };

  const addressCopyLine =
    addressCopy.kind === 'saving'
      ? 'Saving…'
      : addressCopy.kind === 'saved'
        ? 'Saved.'
        : addressCopy.kind === 'idle'
          ? addressCopyRefusal
          : addressCopy.message;

  /*
   * The control, above the shipping fields it writes — the same wording and the
   * same placement the two Edit dialogs use, so the detail page and the modal
   * do not disagree about what a record can do.
   *
   * ⚠️ Rendered for EVERYBODY, disabled with its reason, rather than hidden
   * from a reader who cannot write. A missing affordance is indistinguishable
   * from a removed feature — the same call the inline fields on this page make.
   */
  const sameAsBillingControl = (
    <div className="mt-2 grid justify-items-start gap-1.5">
      <Button
        variant="secondary"
        size="sm"
        loading={addressCopy.kind === 'saving'}
        disabled={addressCopyRefusal !== null}
        aria-describedby={addressCopyLine ? 'company-address-copy-status' : undefined}
        onClick={() => {
          // A previous outcome describes a press that is over. Clearing it here
          // rather than only on success means a stale failure cannot sit under
          // a button that is now working.
          setAddressCopy({ kind: 'idle' });
          if (addressCopyOverwrites.length > 0) setAddressCopyConfirmOpen(true);
          else void runAddressCopy();
        }}
      >
        {SAME_AS_BILLING_LABEL}
      </Button>
      {addressCopyLine && (
        <p
          id="company-address-copy-status"
          role={
            addressCopy.kind === 'error' || addressCopy.kind === 'warning' ? 'alert' : 'status'
          }
          className={cx(
            'm-0 text-xs [overflow-wrap:anywhere]',
            // Never colour alone: "Saved.", a refusal and a reason for being
            // disabled are three different sentences before they are three
            // different colours.
            addressCopy.kind === 'error'
              ? 'text-danger'
              : addressCopy.kind === 'warning'
                ? 'text-warning'
                : addressCopy.kind === 'saved'
                  ? 'text-success'
                  : 'text-fg-muted',
          )}
        >
          {addressCopyLine}
        </p>
      )}
    </div>
  );

  /**
   * One half of the Addresses group.
   *
   * ⚠️ **The two halves are no longer the same length, and that asymmetry is
   * the feature** (2026-08-31): billing renders FOUR rows and shipping FIVE.
   * A company's country of record is the `countryId` foreign key and has its
   * own picker in Account details, so there is no billing Country line to render
   * — while `shippingCountry` is still free text with no foreign key behind it.
   *
   * Billing is driven off `COMPANY_ADDRESS_PAIRS`, which is exactly what the
   * copy control uses, so the column and the copy can never disagree about
   * which fields a billing address is. Shipping is driven off the full
   * `ADDRESS_PAIRS`, because it kept every one of its columns.
   */
  const addressColumn = (title: string, prefix: 'billing' | 'shipping', action?: ReactNode) => {
    const keys: readonly CompanyAddressKey[] =
      prefix === 'billing'
        ? COMPANY_ADDRESS_PAIRS.map(([billing]) => billing)
        : ADDRESS_PAIRS.map(([, shipping]) => shipping);
    const rows = keys.map((key) => ({ key, label: ADDRESS_ROW_LABELS[key] }));

    return (
      <section className="min-w-0">
        <h4 className="m-0 text-xs font-semibold text-fg-secondary">{title}</h4>
        {action}
        <dl className="m-0 mt-2 grid gap-y-3">
          {rows.map(({ key, label }) => (
            <DetailItem key={key} label={label}>
              {textField(key, `${title} ${label.toLowerCase()}`)}
            </DetailItem>
          ))}
        </dl>
      </section>
    );
  };

  /*
   * The "+" on the Sales & billing history: withheld — not disabled — when the
   * reader may create none of the three, exactly as `/companies` withholds its
   * three mounts. `{id, name}` and nothing else, because the flow fetches the
   * company itself (see `openProjectLines`).
   */
  const createMenu =
    createTargets.length > 0 ? (
      <CreateForCompanyMenu
        prominent
        company={{ id: company.id, name: company.name }}
        targets={createTargets}
        onSelect={openProjectLines({ id: company.id, name: company.name })}
      />
    ) : undefined;

  /*
   * The shell props every section shares, read off the FOLD preference. The
   * hook refuses `toggle` while its read is in flight, so a header pressed
   * before the preference has landed is simply a no-op — the headers render
   * normally throughout.
   *
   * ⛔ No `onMove` since 2026-10-20: the shell's up/down arrows are gone and
   * Edit layout is the ONE way to reorder.
   */
  const sectionShell = (id: CompanyPageSectionId) => ({
    id: `company-section-${id}`,
    collapsed: folds.isCollapsed(id),
    onToggle: () => folds.toggle(id),
  });

  /*
   * The SEVEN sections, built once here and rendered by `LayoutGrid` below.
   * Owner's decisions, 2026-10-11:
   *
   *   Overview · Contacts · Sales & billing history · Purchases & interest ·
   *   Attachments · Marketing emails · Xero & billing setup
   *
   * is the DEFAULT order, the last four fold by default, and both are the
   * reader's own to rearrange (`useCompanyPageLayout`). Every in-place editor,
   * validation and "Saved." notice is the one it was before the re-homing — the
   * groups moved; nothing in them changed.
   *
   * 2026-10-16: an eighth, Tasks, sits after Contacts and is open by default;
   * a ninth, Notes, sits after Tasks and is open by default.
   * 2026-10-23: Emails (logged BCC threads) sits after Attachments, open by default.
   *
   * ⚠️ `marketing` (and `tasks`, `notes`) is `null` for a reader who may not read it,
   * and a `null` section is SKIPPED by the loop — not rendered folded, not
   * rendered as an empty box. Hiding a section is a view decision; the panel's
   * own API refuses on its own terms.
   */
  // `lead-history` is rendered on its own below (`leadHistorySection`).
  const sections: Omit<Record<CompanyPageSectionId, ReactNode>, typeof LEAD_HISTORY_SECTION> = {
    overview: (
      <CollapsibleSection {...sectionShell('overview')} title="Overview">
        {/*
          Identity, then Ownership, then Contact details and the two addresses,
          then the Description — and the "Created … by …" line closing it. The
          Financial details and the Xero configurations that used to sit between
          Identity and Ownership are in "Xero & billing setup"; the purchase
          history and Projects of interest are in "Purchases & interest".
        */}
        <div>
          <DetailGroup title="Identity">
            <DetailItem label="Pipeline stage" labelId="company-stage-label">
              {canUpdate ? (
                <div className="grid gap-1.5">
                  <Select
                    id="company-stage"
                    aria-labelledby="company-stage-label"
                    className="max-w-56"
                    value={stageValue}
                    disabled={stageStatus.kind === 'saving' || stagesFailed}
                    invalid={stageStatus.kind === 'field'}
                    aria-describedby={stageMessage ? 'company-stage-status' : undefined}
                    onChange={(event) => void saveStage(event.currentTarget.value)}
                  >
                    {/* `null` is a real state, so returning to it is a real choice. */}
                    <option value="">{UNCLASSIFIED_LABEL}</option>
                    {stageChoices.map((choice) => (
                      <option key={choice.value} value={choice.value}>
                        {choice.label}
                      </option>
                    ))}
                  </Select>

                  {/* One live region for every outcome, so a save never
                      reports itself twice or in two different voices. */}
                  <p
                    id="company-stage-status"
                    role={
                      stageStatus.kind === 'banner' ||
                      stageStatus.kind === 'field' ||
                      stageStatus.kind === 'stripped'
                        ? 'alert'
                        : 'status'
                    }
                    className={cx(
                      'm-0 text-xs [overflow-wrap:anywhere]',
                      stageStatus.kind === 'banner' || stageStatus.kind === 'field'
                        ? 'text-danger'
                        : stageStatus.kind === 'stripped'
                          ? 'text-warning'
                          : 'text-fg-muted',
                    )}
                  >
                    {stageMessage}
                  </p>

                  {stagesFailed && (
                    <div>
                      <Button size="sm" variant="secondary" onClick={retryStages}>
                        Try again
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <StageBadge stage={company.stage} />
              )}
            </DetailItem>
            {showStageStatus ? (
              <DetailItem label={stageStatusLabel}>
                <div className="grid gap-1.5">
                  {stageStatusField()}
                  {followUpNode}
                </div>
              </DetailItem>
            ) : (
              // A stored follow-up date never hides with the status field.
              followUpNode !== null && (
                <DetailItem label="Follow-up date">{followUpNode}</DetailItem>
              )
            )}
            {/*
              ⚠️ A SEPARATE AXIS FROM THE STAGE ABOVE, and deliberately next to
              it rather than merged with it. Stage is where an account sits
              commercially; this is whether the school was signed off at all. A
              company can be `approved` and still a Lead. Never render one as
              the other, and never derive either from the other.

              Read-only, and the type is the guard: `approvalStatus` is absent
              from `CompanyInput`, so no patch can express it. The only writers
              are the two public approval endpoints, which act on a token the
              approver was emailed — a client that could set this could approve
              its own company.

              The Resend control sits HERE, with the state it acts on, so the
              button and the fact it depends on can never disagree. It renders
              for everyone, disabled with its reason where it does not apply:
              an absent affordance is indistinguishable from a removed feature,
              which this app has already had reported as a bug.
            */}
            <DetailItem label="Approval">
              <div className="grid gap-2">
                <ApprovalStatusBadge status={company.approvalStatus} />
                <ResendApprovalControl company={company} variant="detail" />
              </div>
            </DetailItem>
            {/*
              ⚠️ Beside the pipeline stage, and placed AFTER Approval rather
              than between the two: the comment above records that Approval
              sits next to Stage deliberately, and this group is a
              `sm:grid-cols-2 lg:grid-cols-3` grid — so Stage, Approval and
              Source share one row at `lg` and Source falls directly beneath
              Stage at `sm`. Adjacent either way, with nothing displaced.

              ⚠️ A THIRD AXIS, and not derivable from either of the two beside
              it. Stage is where an account sits commercially, Approval is
              whether the school was signed off, and this is how it reached us
              in the first place. Never render one as another.

              ⚠️ Blank is ordinary and permanent for most accounts — see
              `sourceField`. Nothing here may nag about it, and no empty state
              may imply a value is on its way.
            */}
            <DetailItem label="Company source">{sourceField()}</DetailItem>
            {/*
              ⚠️ Directly after Source, which is the pairing the reader
              expects and the one most at risk of being read as a single
              control: both are short master pickers and both answer a
              question beginning "what kind of". Two full labels — `Company
              source` and `Company type` — are what keep them apart, and
              neither may be shortened to a word the other could share.

              ⚠️ Blank is ordinary and permanent for most accounts — see
              `companyTypeField`. Nothing here may nag about it, and no empty
              state may imply a value is on its way.
            */}
            <DetailItem label="Company type">{companyTypeField()}</DetailItem>
            <DetailItem label="Partner search">{partnerSearchField()}</DetailItem>
            {/* Company name: the display name everywhere in SEED EMERGE. Not
                unique, and editing it never pushes to Xero — the Xero Contact
                name is the Billing name (Xero section). */}
            <DetailItem label={COMPANY_NAME_LABEL}>
              {textField('name', COMPANY_NAME_LABEL, {
                autoComplete: 'organization',
                display: <span className="font-medium text-fg">{company.name}</span>,
              })}
            </DetailItem>
            {/* ⚠️ EMPTY IS THE ORDINARY CASE and stays that way: every
                company's `shortName` is null today, Xero has no equivalent
                field, and nothing will ever bulk-populate it — hand-typed or
                empty, permanently. A short name must never be derived from
                the full name. `companyDocumentName()` in `lib/format.ts` is
                the ONE resolver for "short name when set, name otherwise". */}
            <DetailItem label="Short name">
              {textField('shortName', 'Short name', {
                emptyLabel: 'Not set',
              })}
            </DetailItem>
            <DetailItem label="Faculty">{facultyField()}</DetailItem>
            {/* The company's country OF RECORD — `d_companies.country_id`,
                the foreign key the server derives the billing region from,
                which decides the currency and the Xero tax and branding
                defaults on the approval page and in the approval email.

                ⚠️ EDITABLE IN PLACE since 2026-10-11, on the owner's
                instruction. *SUPERSEDED: "READ-ONLY HERE, DELIBERATELY … kept
                out of the in-place editors because it MOVES MONEY."* The
                money point still stands — a change re-derives the billing
                region on the server — which is why the control writes the
                key alone and changes nothing else on the record.

                ⚠️ **`null` is the ordinary state and must read as an
                absence**, never as a country. "No country recorded" and "Rest
                of the World" are different facts and the second is a claim
                about a currency. */}
            <DetailItem label="Country">{countryField()}</DetailItem>
            {/* READ-ONLY: `xeroContactId` is owned by the Xero link and
                rejected on write — sending it is a 422 naming the field. */}
            <DetailItem label="Xero contact ID">
              <span className="font-mono text-xs">{orDash(company.xeroContactId)}</span>
            </DetailItem>
            <DetailItem label="Website">
              {textField('website', 'Website', {
                inputMode: 'url',
                action: <ValueAction value={company.website} kind="url" />,
              })}
            </DetailItem>
            <DetailItem label="LinkedIn">
              {textField('linkedin', 'LinkedIn', {
                inputMode: 'url',
                action: <ValueAction value={company.linkedin} kind="url" />,
              })}
            </DetailItem>
            {/* A row of its own: a company can hold four regions, and all
                four must be visible at once.

                ⚠️ Still an IDENTITY field (2026-10-11): the owner's list of
                what moves to "Purchases & interest" named Projects of interest
                alone, and a region of focus is where the account IS, not what
                it has bought. */}
            <DetailItem
              label="Regions of focus"
              className="sm:col-span-2 lg:col-span-3"
            >
              {regionsField()}
            </DetailItem>
          </DetailGroup>

          {/* Owner is deliberately not here — the account is read through
              the account lead and client success manager, and a third name
              beside them made this block hard to scan. */}
          <DetailGroup title="Ownership">
            <DetailItem label="Account lead">
              {staffField('accountLeadId', 'Account lead', company.accountLead)}
            </DetailItem>
            <DetailItem label="Client success manager">
              {staffField(
                'clientSuccessManagerId',
                'Client success manager',
                company.clientSuccessManager,
              )}
            </DetailItem>
          </DetailGroup>

          <DetailGroup title="Contact details" columns="sm:grid-cols-2 lg:grid-cols-4">
            {/* A person rather than a channel, so it leads the group and
                takes two cells — the name and the email under it need the
                width, and a 4-column cell at `lg` truncates both. */}
            <DetailItem label="Primary contact" className="sm:col-span-2">
              {primaryContactField()}
            </DetailItem>
            <DetailItem label="Email">
              {textField('email', 'Email', {
                type: 'email',
                autoComplete: 'email',
                action: <ValueAction value={company.email} kind="email" />,
              })}
            </DetailItem>
            <DetailItem label="Secondary email">
              {textField('secondaryEmail', 'Secondary email', {
                type: 'email',
                action: <ValueAction value={company.secondaryEmail} kind="email" />,
              })}
            </DetailItem>
            <DetailItem label="Mobile">
              {textField('mobile', 'Mobile', {
                type: 'tel',
                autoComplete: 'tel',
                action: <ValueAction value={company.mobile} kind="tel" />,
              })}
            </DetailItem>
            <DetailItem label="Alternate mobile">
              {textField('alternateMobile', 'Alternate mobile', {
                type: 'tel',
                action: <ValueAction value={company.alternateMobile} kind="tel" />,
              })}
            </DetailItem>
          </DetailGroup>

          {/* Written out rather than handed to `DetailGroup`: each address
              is five separate columns on the wire, each saving on its own,
              grouped and sub-headed so the five still read as one address. */}
          <div className="border-b border-border px-4 py-4 last:border-b-0">
            <h3 className="m-0 text-sm font-semibold text-fg">Addresses</h3>
            <div className="mt-3 grid gap-x-6 gap-y-5 sm:grid-cols-2">
              {addressColumn('Billing', 'billing')}
              {addressColumn('Shipping', 'shipping', sameAsBillingControl)}
            </div>
          </div>

          <DetailGroup title="Notes" columns="grid-cols-1">
            <DetailItem label="Description">
              {textField('description', 'Description', {
                multiline: true,
                rows: 5,
                display:
                  company.description && company.description.trim() !== '' ? (
                    <span className="block whitespace-pre-wrap">{company.description}</span>
                  ) : undefined,
              })}
            </DetailItem>
          </DetailGroup>
        </div>

        <PanelFooter>
          {/* Created only — "Updated" would move on almost every visit to a
              record edited field by field, reporting the last click rather
              than anything about the account. */}
          <p className="m-0 text-xs text-fg-muted">
            Created {formatDateTime(company.createdAt)}
            {company.createdBy ? ` by ${company.createdBy.name}` : ''}
          </p>
        </PanelFooter>
      </CollapsibleSection>
    ),

    contacts: (
      <CollapsibleSection
        {...sectionShell('contacts')}
        title="Contacts"
        /*
         * A COUNT, as the folded-or-open summary the shell is built for — not
         * the "{n} contacts" line that used to sit under the heading and went
         * under the owner's stricter helper-text ruling (2026-09-05). That was
         * prose under a heading; this is the one line of facts a closed section
         * carries so a reader need not open it to learn it is empty. Same
         * standing as the history's "3 deals · 12 quotes" beside it.
         */
        summary={`${contactCount} ${contactCount === 1 ? 'contact' : 'contacts'}`}
        actions={
          canCreateContact ? (
            <Button size="sm" leadingIcon={<PlusIcon />} onClick={() => setAddContactOpen(true)}>
              Add contact
            </Button>
          ) : undefined
        }
      >
        {/* A 404 here means the account itself is gone, not that it simply
            has no contacts — an error, never the inviting empty state below. */}
        {contactsGone ? (
          <div
            role="alert"
            className="flex flex-col items-start gap-2 px-4 py-4 text-xs text-danger"
          >
            <p className="m-0 font-semibold">Contacts unavailable.</p>
            <p className="m-0 text-fg-secondary">
              This account no longer exists — it was probably deleted while the page was open.
            </p>
            <Button size="sm" onClick={() => navigate('/companies')}>
              Back to companies
            </Button>
          </div>
        ) : (
          <>
            {contactsError && (
              <Notice
                tone="danger"
                role="alert"
                title="Couldn’t load contacts."
                className="m-4"
                action={
                  <Button size="sm" onClick={refreshContacts}>
                    Try again
                  </Button>
                }
              >
                <span className="min-w-0 [overflow-wrap:anywhere]">{contactsError}</span>
              </Notice>
            )}

            <div
              aria-busy={contactsLoading || undefined}
              className={cx(
                'transition-opacity duration-150 ease-out motion-reduce:transition-none',
                contactsLoading && contacts && 'opacity-60',
              )}
            >
              {contactsInitialLoad ? (
                <ContactsSkeleton
                  withActions={canUpdate}
                  withRowActions={canUpdateContacts || canDeleteContacts}
                />
              ) : contactRows.length === 0 ? (
                <EmptyState
                  icon={<UsersIcon />}
                  title={contactsError ? 'Nothing to show' : 'No contacts yet'}
                  description={
                    contactsError
                      ? 'The contact list couldn’t be loaded. Try again.'
                      : canCreateContact
                        ? `Nobody at ${company.name} yet. Add the first one.`
                        : // No Add button for this role, so don't tell them to press one.
                          `Nobody at ${company.name} yet.`
                  }
                  action={
                    contactsError || !canCreateContact ? undefined : (
                      <Button
                        variant="primary"
                        size="sm"
                        leadingIcon={<PlusIcon />}
                        onClick={() => setAddContactOpen(true)}
                      >
                        Add the first contact
                      </Button>
                    )
                  }
                />
              ) : (
                <CompanyContactsList
                  contacts={contactRows}
                  groups={contactGroups}
                  groupsSettled={contactGroupsSettled}
                  primaryContactId={company.primaryContact?.id ?? null}
                  truncated={contactsTruncated}
                  canUpdate={canUpdate}
                  canUpdateContacts={canUpdateContacts}
                  contactsDenial={contactsDenial}
                  action={primaryAction}
                  groupActions={contactGroupActions}
                  onMakePrimary={makePrimary}
                  onChangeGroup={saveContactGroup}
                  canDeleteContacts={canDeleteContacts}
                  onEdit={setEditingContact}
                  onDelete={requestContactDelete}
                />
              )}
            </div>

            {/*
              * ⚠️ **The truncation footer is GONE — owner's stricter
              * helper-text ruling, 2026-09-05.** It read: "Showing the first
              * {n} of {m} contacts, so the counts beside each group are a
              * floor rather than a total. Search all of this account's
              * contacts to see the rest." — with the phrase linking to
              * `/contacts?companyId={id}`.
              *
              * ⚠️ **STATE THE COST rather than letting it look like a
              * tidy-up.** The list is ONE request capped at the endpoint's own
              * ceiling and there is no pager, so past 100 a contact is simply
              * absent — and an absent contact is indistinguishable from one
              * who does not exist. The group counts above remain floors and
              * now say nothing about it. The link was also the only route from
              * here to the full list.
              *
              * `contactsTruncated` is kept and still drives `CompanyContactsList`'s
              * own `truncated` prop; only this footer went.
              */}

            {/*
              * Why there is no "Make primary" on any row, said once. The
              * designation itself is never hidden — the badge renders for
              * every reader — so this explains a missing CONTROL, which is
              * one of the four kinds of sentence the no-helper-text rule
              * keeps.
              */}
            {primaryDenial !== null && contactRows.length > 0 && (
              <PanelFooter>
                <p className="m-0 text-xs text-fg-muted">{primaryDenial}</p>
              </PanelFooter>
            )}
          </>
        )}
      </CollapsibleSection>
    ),

    /* Every task on the account and on its deals, quotes and invoices, in
       Outstanding / Upcoming / Previous tabs (owner, 2026-10-16). SKIPPED, not
       folded, for a reader without `tasks.read` — the marketing rule. */
    tasks: canReadTasks ? (
      <CompanyTasksSection
        {...sectionShell('tasks')}
        companyId={company.id}
        reloadNonce={tasksNonce}
        onChanged={bumpTasks}
      />
    ) : null,

    /* Every note on the account and on its deals, quotes and invoices, with a
       composer that writes onto the company (owner, 2026-10-16). Shares the
       one reload token with the Tasks section and the drawer. SKIPPED, not
       folded, for a reader without `notes.read`. */
    notes: canReadNotes ? (
      <CompanyNotesSection
        key={company.id}
        {...sectionShell('notes')}
        companyId={company.id}
        reloadNonce={tasksNonce}
        onChanged={bumpTasks}
      />
    ) : null,

    history: (
      <DocumentsHistorySection
        {...sectionShell('history')}
        companyId={company.id}
        actions={createMenu}
        reloadNonce={historyNonce}
      />
    ),

    purchases: (
      <CollapsibleSection
        {...sectionShell('purchases')}
        title="Purchases & interest"
        /* Cheap from the record, so the folded section still says it. */
        summary={`${company.projectsOfInterest.length} ${
          company.projectsOfInterest.length === 1 ? 'project' : 'projects'
        } of interest`}
      >
        {/* Read-only: what the account has actually bought and taken part in,
            by cycle. First, because it is the settled half; what the account
            is INTERESTED in follows it. */}
        <CompanyPurchaseHistory company={company} />

        {/* A block of its own, written out rather than handed to
            `DetailGroup`: the editor is a multi-select over long project names
            and needs the full width, and the `<dt>` is its only heading.
            The list creates and closes nothing (owner's decision
            2026-10-12). */}
        <div className="border-b border-border px-4 py-4 last:border-b-0">
          <dl className="m-0">
            <DetailItem label={PROJECTS_OF_INTEREST_LABEL}>{projectsOfInterestField()}</DetailItem>
          </dl>
        </div>

        {/* What this institution asked for on the 2027 interest form. It gates
            itself on the `event-interest` module and renders NOTHING for a
            reader without it, so it is mounted unconditionally — a `{can && …}`
            here would put the decision in two places. The sub-heading is this
            page's, because the embedded panel draws none. */}
        {/* 🔴 `onDeleted` IS `refreshRecord` BECAUSE DELETING A SUBMISSION
            TAKES THIS COMPANY OFF THE PROJECTS OF INTEREST ITS `yes` ANSWERS
            NAMED — and that set is rendered by `projectsOfInterestField`
            directly above, from `company.projectsOfInterest`. The panel
            re-reads its own list; without this the row above it goes on
            stating the old set, on the same screen, with nothing failing.
            ⚠️ `refreshRecord` bumps a nonce and RE-READS. It does not navigate
            and it does not close anything — checked before reusing it, because
            several callbacks named like this in this app mean *close*. */}
        <div className="border-b border-border last:border-b-0">
          <h3 className="m-0 px-4 pt-4 text-sm font-semibold text-fg">Event interest</h3>
          <CompanyEventInterestPanel companyId={company.id} onDeleted={refreshRecord} embedded />
        </div>
      </CollapsibleSection>
    ),

    /* Services (owner 2026-10-18): this company's Client Deliverables rows for
       the current and previous cycle. SKIPPED, not folded, without
       `deliverables` read — and so never fetched. */
    services: canReadDeliverables ? (
      <CompanyServicesSection companyId={company.id} shell={sectionShell('services')} />
    ) : null,

    attachments: (
      <CollapsibleSection {...sectionShell('attachments')} title="Attachments">
        {/* Files and links held against the account.

            ⚠️ MOUNTED UNCONDITIONALLY AND GATING ITS OWN CONTROLS. Reading an
            attachment is open to anyone who can read the company — that is the
            owner ruling, taken over "a new permission granted per set" — so a
            `{canUpdate && …}` here would hide the VALUES from a reader
            entitled to see them. The panel gates attach and edit on
            `companies.update` and delete on the admin tier, internally.

            ⛔ IT TAKES NO `onDeleted`, AND THE CONTRAST WITH THE EVENT-INTEREST
            PANEL IS THE POINT rather than an omission. Deleting an interest
            submission changes the projects-of-interest set rendered on this
            same page, so that panel has to tell the page to re-read. An
            attachment is referenced by nothing else on this screen — no field,
            no badge, no count — so a refresh here would re-fetch the whole
            company to change nothing. ⚠️ If a count of attachments is ever
            added to the header, to this section's summary or to the delete
            dialog, THAT is when this needs the callback, and the absence of
            one becomes a live defect. */}
        <CompanyAttachmentsPanel companyId={company.id} embedded />

        {/* Every one here is a LINK to a file in Drive or SharePoint, filed
            per cycle — nothing is stored here, so no upload control anywhere.
            In Attachments because that is the question it answers ("where are
            this account's files"); written out rather than handed to
            `DetailGroup` because the field carries its own cycle picker and
            block headings, not `<dt>`/`<dd>` pairs. */}
        <div className="border-t border-border px-4 py-4">
          <h3 className="m-0 text-sm font-semibold text-fg">Proposals and reports</h3>
          <div className="mt-3">
            <CompanyCycleLinksField companyId={company.id} canUpdate={canUpdate} />
          </div>
        </div>
      </CollapsibleSection>
    ),

    /* The account's logged (BCC) email threads (2026-10-23). Mounted for
       anyone who can read the company — the same visibility as Attachments,
       where each thread's trail is also filed. */
    emails: (
      <CollapsibleSection {...sectionShell('emails')} title="Emails">
        <CompanyEmailTrails companyId={company.id} companyName={company.name} />
      </CollapsibleSection>
    ),

    /* Marketing emails sent to this account's contacts. SKIPPED, not folded,
       for a reader without `marketing-campaigns.read` — see `canReadMarketing`. */
    marketing: canReadMarketing ? (
      <CollapsibleSection {...sectionShell('marketing')} title="Marketing emails">
        <MarketingHistoryPanel companyId={company.id} embedded />
      </CollapsibleSection>
    ) : null,

    /* "Product Insights" (id stays 'fab': stored per user): the linked SEED FAB
       school(s)' funnel plus every programme with data for them. The component
       renders nothing for a reader without `product-insights` read or a
       company with no linked school, so the section is skipped rather than
       drawn empty. */
    fab: <CompanyProductInsightsSection companyId={company.id} shell={sectionShell('fab')} />,

    /* "Company mapping" (owner 2026-10-25): the SEED platform company records
       linked to this company, with link/unlink (gated inside on
       `product-insights.update`). SKIPPED, not folded, for a reader without
       `product-insights` read — same call as `marketing`. */
    mapping: ability.canRead('product-insights') ? (
      <CompanyMappingSection
        companyId={company.id}
        companyName={company.name}
        onCompanyRenamed={refreshRecord}
        shell={sectionShell('mapping')}
      />
    ) : null,

    xero: (
      <CollapsibleSection
        {...sectionShell('xero')}
        title="Xero & billing setup"
        /* Folded, this line IS the section — a whole-connection failure must
           not wait behind the fold. `undefined` when there is nothing to say. */
        summary={xeroSectionSummary(brandingThemes)}
      >
        {/* The link FIRST: it is the prerequisite fact — what this account IS
            in Xero — and everything below is configured around it. The panel
            carries its own actions and error state, and its own tenant line,
            so nothing here repeats either. */}
        <div className="border-b border-border">
          <XeroPanel
            entity="companies"
            recordId={company.id}
            recordName={company.billingName?.trim() || company.name}
            link={linkOf(company)}
            connection={xeroConnection}
            // 2026-10-02 (owner): Xero push / link / unlink / resolve is `companies.send`.
            canManage={ability.canSend('companies')}
            onChanged={refreshRecord}
            onRequestLink={() => setLinkOpen(true)}
            embedded
          />
        </div>

        {/* TWO fields, deliberately — not a stub. Xero's own Financial
            details screen shows six; four cannot reach Xero from here:
            `BankAccountDetails` is a 403 under our OAuth scopes, and
            `BatchPayments` answers 200 and is silently dropped, absent
            from Xero's own read-back. Four inputs that save, report
            success and write nothing is worse than two that work.
            The "None" tax-scheme dropdown beside VAT on Xero's screen is
            absent too — likely an organisation-level setting, and a
            picker that configures nothing is worse than no picker. */}
        <DetailGroup
          title="Financial details"
          columns="sm:grid-cols-2"
          badge={
            <Badge tone="neutral" variant="outline">
              Sent to Xero
            </Badge>
          }
          note="Pushed on the next Xero sync."
        >
          {/* The Xero Contact name, always (owner 2026-10-25). Required and
              strictly unique; blank is refused before the request (and by the
              server, 422 `BILLING_NAME_REQUIRED`). */}
          <DetailItem label={BILLING_NAME_LABEL}>
            {textField('billingName', BILLING_NAME_LABEL, { autoComplete: 'organization' })}
          </DetailItem>
          <DetailItem label="VAT number">
            {textField('xeroTaxNumber', 'VAT number')}
          </DetailItem>
          <DetailItem label="Default currency">{currencyField()}</DetailItem>
        </DetailGroup>

        {/*
          ⛔ The credit BALANCE is deliberately NOT a group here, one line up
          from the currency it is struck per — and it is no longer a block of
          its own on this page either (2026-10-11). It lives in the Sales &
          billing history's Credit notes tab, beside the notes it is computed
          from. The reason it was never in "Financial details" still stands:
          that group's `Sent to Xero` badge and "Pushed on the next Xero sync."
          are both false of a figure EMERGE derives on every read and pushes
          nowhere.
        */}

        {/* Everything configured around the link, under it. ⚠️ Not all of it
            reaches Xero: the two sales defaults tell Xero what to assume, while
            the branding theme is our own prefill for a new document and is
            never sent to the contact — see `XeroSalesDefaultsFields`. */}
        <DetailGroup title="Xero configurations" columns="sm:grid-cols-2">
          <XeroSalesDefaultsFields
            company={company}
            canUpdate={canUpdate}
            themes={brandingThemes} // the page's one read of the theme list, shared
            onSave={saveCompanyField} // the page's writer, so a save here replaces
            // the same loaded record every other field reads
          />
        </DetailGroup>
      </CollapsibleSection>
    ),
  };

  /*
   * "Account lead history" (owner 2026-10-25; layout id `lead-history`, never
   * rename). Readable by anyone who can read this company; the section gates
   * its own Add / Edit / Delete on `account-lead-history.update`. A history
   * write can move the current lead, so the record is re-read after one.
   */
  const leadHistoryShell = LEAD_HISTORY_IN_FOLD_REGISTRY
    ? {
        id: `company-section-${LEAD_HISTORY_SECTION}`,
        collapsed: folds.isCollapsed(LEAD_HISTORY_FOLD_ID),
        onToggle: () => folds.toggle(LEAD_HISTORY_FOLD_ID),
      }
    : {
        id: `company-section-${LEAD_HISTORY_SECTION}`,
        collapsed: leadHistoryCollapsed,
        onToggle: () => setLeadHistoryCollapsed((collapsed) => !collapsed),
      };
  const leadHistorySection = (
    <CollapsibleSection {...leadHistoryShell} title={LEAD_HISTORY_TITLE}>
      <AccountLeadHistorySection
        companyId={company.id}
        companyName={company.name}
        reloadToken={leadHistoryNonce}
        onChanged={refreshRecord}
      />
    </CollapsibleSection>
  );

  // The grid's render map: one thunk per section; a `null` section is skipped.
  const sectionRenders: Record<string, () => ReactNode> = {
    ...Object.fromEntries(
      (Object.keys(sections) as (keyof typeof sections)[]).map((id) => [id, () => sections[id]]),
    ),
    [LEAD_HISTORY_SECTION]: () => leadHistorySection,
  };

  return (
    <>
      <PageHeader
        eyebrow={<BackToCompanies />}
        title={company.name}
        description={
          /*
           * ONE read-only facts line (owner's decision, 2026-10-11): plain text,
           * middle-dot separated, no tiles and no boxes beyond the stage badge
           * the owner named. The separators are `aria-hidden` — a screen reader
           * meets five facts, not five facts and four dots.
           */
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-fg-secondary">
            {headerFacts.map((fact, index) => (
              <Fragment key={index}>
                {index > 0 && <span aria-hidden="true">·</span>}
                {fact}
              </Fragment>
            ))}
          </span>
        }
        actions={
          <>
            {/*
              Edit layout (2026-10-20) replaces the old "Reset layout" button
              and the shells' up/down arrows. Its Reset restores the built-in
              layout, folds included (`layoutControl`).
            */}
            <LayoutToolbar layout={layoutControl} />
            <ActivityButton
              key={`activity-${tasksNonce}`}
              target={activityTarget}
              onOpen={() => setActivityOpen(true)}
            />
            {/* The same "+" as the Sales & Billing section (owner, 2026-10-16). */}
            {createMenu}
            {canUpdate && (
              <Button
                variant="primary"
                leadingIcon={<PencilIcon />}
                onClick={() => setEditOpen(true)}
              >
                Edit
              </Button>
            )}
            {canDelete && (
              <Button
                variant="danger"
                leadingIcon={<TrashIcon />}
                onClick={() => {
                  setDeleteError(null);
                  setConfirmOpen(true);
                  openDeleteCounts();
                }}
              >
                Delete
              </Button>
            )}
          </>
        }
      />

      {/*
        The last layout WRITE failing is a fact the reader cannot otherwise see
        — the screen already shows the arrangement they asked for, so without
        this line a fold that quietly did not persist reads as one that did.
        Muted and one line: the layout is a convenience, not the record.
        (FOLD saves only; the manager's own save failure shows in its toolbar.)
      */}
      {folds.saveError !== null && (
        <p role="status" className="m-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
          {folds.saveError}
        </p>
      )}

      {/*
        Keyed on the record, so opening a different account cannot carry a
        half-typed draft or a "Saved" line across to a company it was never
        about. A refresh of the SAME record (a Xero action, a retry) keeps the
        key and therefore keeps whatever the user is in the middle of.

        ⚠️ The key is on the whole stack of sections now that the in-place
        editors live in more than one of them (Overview and Xero & billing
        setup). It was on the body of the single "Account details" panel.

        Rendered by the page layout manager in the reader's own order, width
        and visibility (2026-10-20; was a `space-y-4` stack — the grid's
        default gap is the same 16 px). A `null` section (marketing, for a
        reader without the grant) is skipped.
      */}
      <LayoutGrid
        key={company.id}
        layout={layoutControl}
        render={sectionRenders}
      />

      <CompanyFormModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        company={company}
        onSaved={(saved) => {
          setRecordResult({ key: recordKey, value: { status: 'ready', company: saved } });
          // The form carries the stage too, so a modal save can contradict
          // whatever the in-place control was last saying — any pending draft
          // or older message is stale from here.
          stageSeq.current += 1;
          setStageDraft(null);
          setStageStatus({ kind: 'idle' });
          // The form can now ADD contacts (2026-10-15), so re-read the list.
          refreshContacts();
          setEditOpen(false);
        }}
      />

      {/* Only mounted while open, so its state is seeded from props each time
          rather than reset by an effect. */}
      <XeroLinkModal
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        entity="companies"
        recordId={company.id}
        // The name Xero knows the company by: the Billing name (Company name only
        // as a defensive fallback for an older API that omits it).
        recordName={company.billingName?.trim() || company.name}
        onLinked={() => {
          setLinkOpen(false);
          refreshRecord();
        }}
      />

      <ContactFormModal
        open={addContactOpen}
        onClose={() => setAddContactOpen(false)}
        lockedCompany={{ id: company.id, name: company.name }}
        onSaved={() => {
          setAddContactOpen(false);
          /*
           * One refresh is now enough. It used to also reset the pager, because
           * a new contact sorts by name and could land on any page — there is
           * no pager any more, so the whole list comes back and the new person
           * appears under their group wherever they sort. The SAME refetch also
           * refills the primary-contact picker: one fetch, one nonce.
           */
          refreshContacts();
        }}
      />

      {/* The row's Edit. Keyed by the contact so each open seeds its baseline
          from the record pressed, not from whoever was edited last. A save can
          change the name the primary badge and the Account-details picker show,
          so the record is re-read when this contact is the primary. */}
      {editingContact && (
        <ContactFormModal
          key={editingContact.id}
          open
          onClose={() => setEditingContact(null)}
          contact={editingContact}
          lockedCompany={{ id: company.id, name: company.name }}
          onSaved={(saved) => {
            setEditingContact(null);
            refreshContacts();
            if (company.primaryContact?.id === saved.id) refreshRecord();
          }}
        />
      )}

      <ConfirmDialog
        open={pendingContactDelete !== null}
        onClose={closeContactDelete}
        onConfirm={() => void confirmContactDelete()}
        title={pendingContactDelete ? `Delete ${pendingContactDelete.name}?` : 'Delete contact?'}
        description={
          pendingContactDelete && (
            <>
              This removes {pendingContactDelete.name} from {company.name}.
              {company.primaryContact?.id === pendingContactDelete.id &&
                ` They are the primary contact, so ${company.name} will have none until you choose another.`}{' '}
              This can’t be undone.
            </>
          )
        }
        confirmLabel="Delete contact"
        loading={deletingContact}
        {...(contactDeleteError ? { error: contactDeleteError } : {})}
      />

      {/* ONE flow for the page, mounted once — the history section's "+" sets
          the request. `null` is closed, so there is no second `open` flag to
          disagree with it. On a save the HISTORY re-reads (it is the list the
          new document lands in) and so does the RECORD — a raised document can
          move the account's stage and its recent activity, and this page holds
          no copy of either to patch. */}
      <CompanyProjectLinesFlow
        request={projectLinesRequest}
        onClose={() => setProjectLinesRequest(null)}
        onSaved={() => {
          setProjectLinesRequest(null);
          setHistoryNonce((n) => n + 1);
          refreshRecord();
        }}
      />

      {/* The stage-status follow-up dialog — once per page. */}
      {stageStatusChange.dialog}

      {/* The account lead's "Effective from" question — once per page. */}
      <AccountLeadChangeDialog
        open={pendingLeadChange !== null && pendingLeadChange.companyId === company.id}
        companyName={company.name}
        fromLead={pendingLeadChange?.fromLead ?? null}
        toLead={pendingLeadChange?.toLead ?? null}
        onConfirm={(dateIso) => void confirmLeadChange(dateIso)}
        onClose={closeLeadChange}
        busy={leadChangeBusy}
        error={leadChangeError}
      />

      {/* A sheet OVER the account, not a panel below it: history is read
          alongside the record, not after scrolling past it. */}
      <ActivityDrawer
        open={activityOpen}
        onClose={() => setActivityOpen(false)}
        target={activityTarget}
        recordLabel={activityLabel}
        viewerId={user?.id ?? null}
        viewerTimeZone={viewerZone(user?.timezone)}
        onActivityChanged={bumpTasks}
      />

      {/* ⚠️ THIS COMMENT USED TO SAY THE OPPOSITE, AND THE CHANGE THAT FALSIFIED
          IT WAS IN THE OTHER REPO. It read: "Deliberately NOT the
          deal/quote/invoice/deliverable pattern — those pre-fetch task/note
          counts because their delete SUCCEEDS and destroys that history. A
          company delete is REFUSED (409) while anything hangs off it, so
          nothing is lost by not warning first." Every word was true until
          backend migration 111 (2026-08-30, owner's instruction) moved
          `fk_notes_company` to ON DELETE CASCADE.

          It is now HALF true, which is why it is replaced rather than deleted:
          TASKS still refuse the delete, so nothing warns about them and the
          server's 409 names them at the moment of the attempt. NOTES are
          destroyed, so this dialog is the only warning anybody gets.

          The contacts sentence is a third thing again — contacts SURVIVE, and
          are orphaned. Three references, three different fates; do not collapse
          them into one sentence. `companyNotesConsequence` is the single copy of
          the notes wording, shared with the row action on /companies. */}
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => void handleDelete()}
        title={`Delete ${company.name}?`}
        description={[
          contactCount > 0
            ? `Its ${contactCount} ${contactCount === 1 ? 'contact' : 'contacts'} will be kept but unassigned.`
            : null,
          companyNotesConsequence(noteTotal, systemNoteTotal),
          'If it has a Xero contact, that stays in Xero.',
          'This can’t be undone.',
        ]
          .filter(Boolean)
          .join(' ')}
        confirmLabel="Delete company"
        loading={deleting}
        {...(deleteError ? { error: deleteError } : {})}
      />

      {/* Only reached when shipping holds values that would be DESTROYED —
          `runAddressCopy` is called straight out when every field the copy
          touches is blank, because a confirmation over nothing is how people
          learn to click through the one that matters.

          The sentence names the fields by the label they carry in the column
          above, so the reader can see what is about to change without reading
          the dialog against the page. It says "billing address", not "billing",
          because what replaces them is the whole address including its blanks —
          a billing field nobody filled in clears its shipping counterpart.

          ⚠️ Deliberately no `loading`: confirming CLOSES this dialog and the
          write reports at the button, beside the five values it changed and in
          the same place every other save on this page reports. A spinner here
          would be unreachable — both state writes batch into one render — and
          an unreachable branch reads as a working one. */}
      <ConfirmDialog
        open={addressCopyConfirmOpen}
        onClose={() => setAddressCopyConfirmOpen(false)}
        onConfirm={() => void runAddressCopy()}
        title="Replace the shipping address?"
        description={`The shipping ${joinList(
          addressCopyOverwrites.map(({ to }) => ADDRESS_ROW_LABELS[to].toLowerCase()),
        )} will be overwritten with the billing address. This can’t be undone.`}
        confirmLabel="Replace shipping"
      />
    </>
  );
}
